import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { 
  User, Calendar, FileText, Pill, Eye, Clock, Printer, 
  ChevronLeft, Plus, X, Search, Info, AlertTriangle,
  Upload, Camera, Timer, Check, Volume2, ArrowLeft,
  Sparkles, Brain, Loader2, Mail, Phone, Tag, Send, CheckCircle, Shield, Play, Download, Save,
  Mic, Square, BookOpen, Copy, Percent
} from 'lucide-react';
import VisitaPDFViewer from '../components/VisitaPDFViewer';
import { VoiceRecorder, VisitRecorder, DropsTimer, DrugSelector, DrugInfoModal } from '../components/cartella-clinica';
import ImportTemplateModal from '../components/ImportTemplateModal';
import { PaymentDiscountSelector, PaymentStatusBadge } from '../components/PaymentDiscountSelector';

const API = process.env.REACT_APP_BACKEND_URL;

const CartellaClinicaNew = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [patient, setPatient] = useState(null);
  const [patientRecord, setPatientRecord] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [activeTab, setActiveTab] = useState('esame');
  
  // Storico visite
  const [storicoVisite, setStoricoVisite] = useState([]);
  const [selectedVisita, setSelectedVisita] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  const [compareVisita, setCompareVisita] = useState(null);
  
  // Examination state
  const [esame, setEsame] = useState({
    patologie_generali: '',
    farmaci_sistemici: [],
    patologia_oftalmica: '',
    terapia_colliri: [],
    // Interventi chirurgici oftalmici con dettagli
    interventi_oftalmici: [], // Array di { tipo, lateralita, anno }
    // Storico bendaggio (anamnesi)
    storia_bendaggio: {
      eseguito: false,
      occhio: '', // OD, OS, entrambi
      eta_inizio: ''
    },
    motivo_visita: '',
    motivo_visita_dettaglio: '', // Dettagli aggiuntivi sul motivo
    sintomi_riferiti: '',  // Sintomi riferiti dal paziente
    
    // Stato visita: draft, saved, signed
    stato_visita: 'draft',
    firma_digitale: null,
    
    // Immagine Gaze9 per motilità oculare
    gaze9_image_url: '',
    
    // Motilità Oculare
    motilita_oculare: {
      allineamento: 'ortotropico', // ortotropico, ET, XT, ipertropia, ipotropia
      // Angolo di deviazione per posizione
      angolo_lontano: '', // PL - per lontano
      angolo_vicino: '', // PV - per vicino
      angolo_sguardo_alto: '',
      angolo_sguardo_basso: '',
      angolo_sguardo_destra: '',
      angolo_sguardo_sinistra: '',
      posizioni_sguardo: {
        adduzione_od: 'normale',
        abduzione_od: 'normale',
        elevazione_od: 'normale',
        depressione_od: 'normale',
        adduzione_os: 'normale',
        abduzione_os: 'normale',
        elevazione_os: 'normale',
        depressione_os: 'normale'
      },
      // Tabella 3x3 strabismo - 9 posizioni di sguardo
      strabismo_9_posizioni: {
        alto_sx: '',      // Sguardo alto-sinistra
        alto_centro: '',  // Sguardo alto-centro
        alto_dx: '',      // Sguardo alto-destra
        centro_sx: '',    // Sguardo centro-sinistra
        centro: '',       // Posizione primaria
        centro_dx: '',    // Sguardo centro-destra
        basso_sx: '',     // Sguardo basso-sinistra
        basso_centro: '', // Sguardo basso-centro
        basso_dx: ''      // Sguardo basso-destra
      },
      asimmetria_facciale: '',
      inclinazione_capo: '',
      krimsky: '',
      stereo_lang_ii: '', // positivo, negativo, non eseguibile
      stereopsi_angolo_corretto: '', // presente, assente
      luci_worth: '', // fusione, diplopia, soppressione OD, soppressione OS
      head_tilt_test: '',
      test_bambola: '',
      note_motilita: ''
    },
    
    // Ecografia Oculare
    ecografia: {
      eseguita: false,
      od_image_url: '',
      od_referto: '',
      os_image_url: '',
      os_referto: '',
      note: ''
    },
    
    // Bendaggio
    bendaggio: {
      prescritto: false,
      occhio: '', // OD, OS
      ore_giorno: '',
      durata_settimane: '',
      note: ''
    },
    
    occhio_destro: {
      visus: '',
      visus_con_correzione: '',
      // Autoref senza cicloplegia
      autoref_sfera: '',
      autoref_cilindro: '',
      autoref_asse: '',
      // Autoref CON cicloplegia
      autoref_ciclo_sfera: '',
      autoref_ciclo_cilindro: '',
      autoref_ciclo_asse: '',
      prescrizione_sfera: '',
      prescrizione_cilindro: '',
      prescrizione_asse: '',
      tonometria: '', // un solo valore
      annessi: 'Sani',
      segmento_anteriore: '',
      mezzi_diottrici: 'trasparenti',
      cornea_fluo: '', // fluo_positivo, fluo_negativo, o vuoto
      opacita_secondaria: '',
      esiti: '',
      retinografia_url: '',
      retinografia_referto: '',
      // Nuovi esami diagnostici
      topografia_url: '',
      topografia_referto: '',
      topografia_cheratocono: 'negativo', // negativo, sospetto, positivo
      topografia_k1_k2: '', // K1 e K2 valori
      oct_url: '', // Legacy - manteniamo per retrocompatibilità
      oct_macula_url: '',
      oct_macula_referto: '',
      oct_nervo_url: '',
      oct_nervo_referto: '',
      oct_referto: '',
      // Conta endoteliale CV, CD, CT
      conta_cv: '',
      conta_cd: '',
      conta_ct: '',
      // Campo Visivo Computerizzato
      campo_visivo_url: '',
      campo_visivo_referto: ''
    },
    occhio_sinistro: {
      visus: '',
      visus_con_correzione: '',
      // Autoref senza cicloplegia
      autoref_sfera: '',
      autoref_cilindro: '',
      autoref_asse: '',
      // Autoref CON cicloplegia
      autoref_ciclo_sfera: '',
      autoref_ciclo_cilindro: '',
      autoref_ciclo_asse: '',
      prescrizione_sfera: '',
      prescrizione_cilindro: '',
      prescrizione_asse: '',
      tonometria: '', // un solo valore
      annessi: 'Sani',
      segmento_anteriore: '',
      mezzi_diottrici: 'trasparenti',
      cornea_fluo: '', // fluo_positivo, fluo_negativo, o vuoto
      opacita_secondaria: '',
      esiti: '',
      retinografia_url: '',
      retinografia_referto: '',
      // Nuovi esami diagnostici
      topografia_url: '',
      topografia_referto: '',
      topografia_cheratocono: 'negativo',
      topografia_k1_k2: '', // K1 e K2 valori
      oct_url: '', // Legacy
      oct_macula_url: '',
      oct_macula_referto: '',
      oct_nervo_url: '',
      oct_nervo_referto: '',
      oct_referto: '',
      // Conta endoteliale CV, CD, CT
      conta_cv: '',
      conta_cd: '',
      conta_ct: '',
      // Campo Visivo Computerizzato
      campo_visivo_url: '',
      campo_visivo_referto: ''
    },
    cicloplegia_eseguita: false,
    // Cicloplegia x2 con timer separati per OD e OS
    cicloplegia_x2_od: {
      attiva: false,
      orario_somministrazione: '',
      timer_scaduto: false
    },
    cicloplegia_x2_os: {
      attiva: false,
      orario_somministrazione: '',
      timer_scaduto: false
    },
    consigli: '',
    prossima_visita_mesi: null,
    prescrizioni_colliri: []
  });

  // Timer Cicloplegia state (separati per OD e OS)
  const [cicloplegiaTimerOD, setCicloplegiaTimerOD] = useState(null);
  const [cicloplegiaTimeRemainingOD, setCicloplegiaTimeRemainingOD] = useState(0);
  const cicloplegiaIntervalRefOD = useRef(null);
  
  const [cicloplegiaTimerOS, setCicloplegiaTimerOS] = useState(null);
  const [cicloplegiaTimeRemainingOS, setCicloplegiaTimeRemainingOS] = useState(0);
  const cicloplegiaIntervalRefOS = useRef(null);

  const [selectedDrugInfo, setSelectedDrugInfo] = useState(null);
  const [showDrugSelector, setShowDrugSelector] = useState(false);

  // Retinography refs
  const retinografiaODRef = useRef(null);
  const retinografiaOSRef = useRef(null);
  // Topografia refs
  const topografiaODRef = useRef(null);
  const topografiaOSRef = useRef(null);
  // OCT refs
  const octODRef = useRef(null);
  const octOSRef = useRef(null);
  const octMaculaODRef = useRef(null);
  const octMaculaOSRef = useRef(null);
  const octNervoODRef = useRef(null);
  const octNervoOSRef = useRef(null);
  // Campo Visivo refs
  const campoVisivoODRef = useRef(null);
  const campoVisivoOSRef = useRef(null);
  
  // Gaze9 ref per motilità oculare
  const gaze9Ref = useRef(null);

  // AI Diagnosis state
  const [aiDiagnosisLoading, setAiDiagnosisLoading] = useState(false);
  const [aiDiagnosis, setAiDiagnosis] = useState(null);

  // AI Terapia state
  const [aiTerapiaLoading, setAiTerapiaLoading] = useState(false);
  const [aiTerapia, setAiTerapia] = useState(null);
  const [showAiTerapiaModal, setShowAiTerapiaModal] = useState(false);

  // Consensi Informati state
  const [consensi, setConsensi] = useState([]);
  const [showUploadConsenso, setShowUploadConsenso] = useState(false);
  const [uploadingConsenso, setUploadingConsenso] = useState(false);

  // Voice Transcription state
  const [isRecording, setIsRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Concludi Visita state
  const [showConcludiModal, setShowConcludiModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [concludiLoading, setConcludiLoading] = useState(false);
  const [concludiData, setConcludiData] = useState({
    diagnosi: '',
    terapia: '',
    consigli: '',
    prossimo_controllo: '',
    send_email: true,
    send_sms: true,
    signature_otp: ''
  });
  
  // Firma digitale OTP state
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpStep, setOtpStep] = useState('request'); // 'request', 'verify', 'done'
  const [otpLoading, setOtpLoading] = useState(false);
  const [signatureToken, setSignatureToken] = useState(null);

  // Import Template Modal state
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importTargetField, setImportTargetField] = useState('diagnosi');
  const [importInitialSearch, setImportInitialSearch] = useState('');

  const openImportModal = (field, search = '') => {
    setImportTargetField(field);
    setImportInitialSearch(search);
    setImportModalOpen(true);
  };

  const handleTemplateImport = (content) => {
    setConcludiData(prev => ({
      ...prev,
      [importTargetField]: prev[importTargetField] ? `${prev[importTargetField]}\n\n${content}` : content
    }));
  };
  
  const [patientTags, setPatientTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [tagsToAdd, setTagsToAdd] = useState([]);
  const [tagsToRemove, setTagsToRemove] = useState([]);
  const [lastSavedEsameId, setLastSavedEsameId] = useState(null);
  
  // PDF Viewer state
  const [showPDFViewer, setShowPDFViewer] = useState(false);
  const [selectedVisitForPDF, setSelectedVisitForPDF] = useState(null);
  
  // Chatbot Anamnesis Reports
  const [chatbotAnamnesisReports, setChatbotAnamnesisReports] = useState([]);
  const [showAnamnesisReports, setShowAnamnesisReports] = useState(false);

  // AI Image Analysis state
  const [aiImageAnalysisLoading, setAiImageAnalysisLoading] = useState({});
  const [aiImageAnalysisResults, setAiImageAnalysisResults] = useState({});

  // Componente per mostrare i 2 pareri AI
  const AIResultsDisplay = ({ resultKey, showMLForTypes = ['oct_macula', 'oct_nervo', 'retinografia'] }) => {
    const result = aiImageAnalysisResults[resultKey];
    if (!result) return null;
    
    const examType = resultKey.split('_').slice(0, -1).join('_'); // es: retinografia_OD -> retinografia
    const showML = showMLForTypes.includes(examType);
    const hasMLParere = result.parere_ml?.success;
    
    return (
      <div className="mb-2 space-y-2">
        {/* PARERE 1 - AI Descrittiva */}
        <div className="p-2 bg-purple-50 rounded-lg border border-purple-200">
          <p className="font-medium text-purple-800 text-xs flex items-center gap-1">
            <span className="bg-purple-200 px-1.5 py-0.5 rounded text-[10px]">PARERE 1</span>
            🧠 AI Descrittiva (GPT-4o)
          </p>
          <p className="text-purple-700 text-xs mt-1">{result.referto}</p>
          {result.evoluzione && (
            <p className="mt-1 text-xs font-medium" style={{ color: result.evoluzione === 'MIGLIORAMENTO' ? 'green' : result.evoluzione === 'PEGGIORAMENTO' ? 'red' : 'gray' }}>
              📊 Evoluzione: {result.evoluzione}
            </p>
          )}
        </div>
        
        {/* PARERE 2 - ML Diagnostico (solo per tipi supportati) */}
        {showML && hasMLParere && (
          <div className="p-2 bg-blue-50 rounded-lg border border-blue-200">
            <p className="font-medium text-blue-800 text-xs flex items-center gap-1">
              <span className="bg-blue-200 px-1.5 py-0.5 rounded text-[10px]">PARERE 2</span>
              🔬 ML Diagnostico ({result.parere_ml.model?.split(' ')[0] || 'HuggingFace'})
            </p>
            <div className="mt-1 space-y-1">
              {result.parere_ml.predictions?.slice(0, 4).map((pred, idx) => (
                <div key={idx} className="flex items-center justify-between text-xs">
                  <span className="text-blue-700">{pred.patologia}</span>
                  <span className={`font-bold px-1.5 py-0.5 rounded ${
                    pred.probabilita > 80 ? 'bg-red-100 text-red-700' :
                    pred.probabilita > 50 ? 'bg-yellow-100 text-yellow-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    {pred.probabilita}%
                  </span>
                </div>
              ))}
            </div>
            {result.parere_ml.diagnosi_principale && (
              <p className="mt-2 text-xs font-medium text-blue-800 border-t border-blue-200 pt-1">
                ➡️ Diagnosi ML: {result.parere_ml.diagnosi_principale.patologia} ({result.parere_ml.diagnosi_principale.confidence})
              </p>
            )}
          </div>
        )}
        
        {/* Messaggio se ML non supportato */}
        {showML && !hasMLParere && result.has_dual_opinion === false && (
          <p className="text-xs text-gray-400 italic">
            ℹ️ Parere ML non disponibile per questo tipo di esame
          </p>
        )}
      </div>
    );
  };

  // Function to analyze medical images with AI
  const analyzeImageWithAI = async (imageUrl, examType, eye) => {
    const key = `${examType}_${eye}`;
    setAiImageAnalysisLoading(prev => ({ ...prev, [key]: true }));
    
    try {
      const token = localStorage.getItem('token');
      
      // Extract base64 from data URL
      const base64Match = imageUrl.match(/^data:image\/[^;]+;base64,(.+)$/);
      if (!base64Match) {
        throw new Error('Immagine non valida');
      }
      const imageBase64 = base64Match[1];
      
      // Usa l'endpoint dual per ottenere 2 pareri
      const response = await axios.post(
        `${API}/api/medical-imaging/analyze-dual`,
        {
          image_base64: imageBase64,
          exam_type: examType,
          eye: eye,
          patient_id: patient?.id
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const dualResult = response.data.data;
        
        // Estrai i due pareri
        const parere1 = dualResult.parere_1_descrittivo?.data || {};
        const parere2 = dualResult.parere_2_diagnostico?.data || null;
        
        // Combina i risultati
        const combinedResult = {
          ...parere1,
          parere_ml: parere2, // Aggiunge il parere ML
          has_dual_opinion: parere2?.success || false
        };
        
        setAiImageAnalysisResults(prev => ({ ...prev, [key]: combinedResult }));
        
        // Auto-fill fields based on exam type (usa parere 1 - descrittivo)
        // Anti-overwrite: se il medico ha gia' compilato manualmente, chiedi conferma
        const eyeKey = eye === 'OD' ? 'occhio_destro' : 'occhio_sinistro';
        const aiSafeUpdateEye = (field, newValue) => {
          if (!newValue) return;
          const currentValue = (esame[eyeKey] || {})[field];
          if (currentValue && currentValue.trim && currentValue.trim() !== '' && currentValue !== newValue) {
            // Campo gia' compilato dal medico: chiedi conferma prima di sovrascrivere
            const ok = window.confirm(
              `Il campo "${field}" (${eye}) è già compilato:\n\n"${currentValue.substring(0,150)}"\n\nVuoi SOVRASCRIVERE con il nuovo testo AI?\n\nOK = sovrascrivi\nAnnulla = mantieni testo attuale`
            );
            if (!ok) return;
          }
          updateEye(eyeKey, field, newValue);
        };
        
        if (examType === 'topografia') {
          const cheratocono = parere1.screening_cheratocono?.toLowerCase() || 'negativo';
          aiSafeUpdateEye('topografia_cheratocono', cheratocono);
          aiSafeUpdateEye('topografia_referto', parere1.referto || '');
        } else if (examType === 'retinografia') {
          aiSafeUpdateEye('retinografia_referto', parere1.referto || '');
        } else if (examType === 'oct_macula') {
          aiSafeUpdateEye('oct_macula_referto', parere1.referto || '');
        } else if (examType === 'oct_nervo') {
          aiSafeUpdateEye('oct_nervo_referto', parere1.referto || '');
        } else if (examType === 'cvc' || examType === 'campo_visivo') {
          aiSafeUpdateEye('campo_visivo_referto', parere1.referto || '');
        } else if (examType === 'gaze9') {
          if (parere1.allineamento_primario) {
            const currentAlign = esame.motilita_allineamento;
            const currentNote = esame.motilita_note;
            const willOverwrite = (currentAlign && currentAlign !== parere1.allineamento_primario)
              || (currentNote && currentNote.trim && currentNote.trim() !== '');
            if (willOverwrite && !window.confirm('I campi Motilita risultano gia compilati. Sovrascrivere con il referto AI?\n\nOK = sovrascrivi\nAnnulla = mantieni')) {
              // Non sovrascrive
            } else {
              setEsame(p => ({
                ...p,
                motilita_allineamento: parere1.allineamento_primario,
                motilita_note: parere1.referto || ''
              }));
            }
          }
        }
        
        // Messaggio con info sui pareri
        let successMsg = `Analisi ${examType.toUpperCase()} ${eye} completata!`;
        if (parere2?.success) {
          successMsg += ` (2 pareri disponibili)`;
        }
        toast.success(successMsg);
      } else {
        throw new Error(response.data.error || 'Errore analisi');
      }
    } catch (error) {
      console.error('AI Image Analysis Error:', error);
      toast.error(`Errore analisi AI: ${error.response?.data?.error || error.message}`);
    } finally {
      setAiImageAnalysisLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  // Common diagnosis tags
  const COMMON_TAGS = [
    '#miopia', '#ipermetropia', '#presbiopia', '#astigmatismo',
    '#glaucoma', '#cataratta', '#maculopatia', '#retinopatia',
    '#occhiosecco', '#cheratocono', '#strabismo', '#ambliopia',
    '#congiuntivite', '#blefarite', '#uveite'
  ];

  // Cicloplegia Timer functions - OD
  const startCicloplegiaTimerOD = () => {
    const now = new Date();
    const orario = now.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
    
    setEsame(prev => ({
      ...prev,
      cicloplegia_x2_od: {
        attiva: true,
        orario_somministrazione: orario,
        timer_scaduto: false
      }
    }));
    
    // Timer di 20 minuti (1200 secondi)
    const CICLOPLEGIA_DURATION = 20 * 60;
    setCicloplegiaTimeRemainingOD(CICLOPLEGIA_DURATION);
    
    if (cicloplegiaIntervalRefOD.current) {
      clearInterval(cicloplegiaIntervalRefOD.current);
    }
    
    cicloplegiaIntervalRefOD.current = setInterval(() => {
      setCicloplegiaTimeRemainingOD(prev => {
        if (prev <= 1) {
          clearInterval(cicloplegiaIntervalRefOD.current);
          notifyCicloplegiaScaduta('OD');
          setEsame(p => ({
            ...p,
            cicloplegia_x2_od: { ...p.cicloplegia_x2_od, timer_scaduto: true }
          }));
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };
  
  const stopCicloplegiaTimerOD = () => {
    if (cicloplegiaIntervalRefOD.current) {
      clearInterval(cicloplegiaIntervalRefOD.current);
    }
    setCicloplegiaTimeRemainingOD(0);
    setEsame(prev => ({
      ...prev,
      cicloplegia_x2_od: {
        attiva: false,
        orario_somministrazione: '',
        timer_scaduto: false
      }
    }));
  };

  // Cicloplegia Timer functions - OS
  const startCicloplegiaTimerOS = () => {
    const now = new Date();
    const orario = now.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
    
    setEsame(prev => ({
      ...prev,
      cicloplegia_x2_os: {
        attiva: true,
        orario_somministrazione: orario,
        timer_scaduto: false
      }
    }));
    
    const CICLOPLEGIA_DURATION = 20 * 60;
    setCicloplegiaTimeRemainingOS(CICLOPLEGIA_DURATION);
    
    if (cicloplegiaIntervalRefOS.current) {
      clearInterval(cicloplegiaIntervalRefOS.current);
    }
    
    cicloplegiaIntervalRefOS.current = setInterval(() => {
      setCicloplegiaTimeRemainingOS(prev => {
        if (prev <= 1) {
          clearInterval(cicloplegiaIntervalRefOS.current);
          notifyCicloplegiaScaduta('OS');
          setEsame(p => ({
            ...p,
            cicloplegia_x2_os: { ...p.cicloplegia_x2_os, timer_scaduto: true }
          }));
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };
  
  const stopCicloplegiaTimerOS = () => {
    if (cicloplegiaIntervalRefOS.current) {
      clearInterval(cicloplegiaIntervalRefOS.current);
    }
    setCicloplegiaTimeRemainingOS(0);
    setEsame(prev => ({
      ...prev,
      cicloplegia_x2_os: {
        attiva: false,
        orario_somministrazione: '',
        timer_scaduto: false
      }
    }));
  };
  
  const notifyCicloplegiaScaduta = (eye) => {
    // Browser notification
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(`⏰ Cicloplegia ${eye} Completata`, {
        body: `Il tempo di attesa per la cicloplegia ${eye} è terminato. Può procedere con l'esame.`,
        icon: '/favicon.ico'
      });
    }
    // Audio notification
    const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleC4tU3m919uwVCIpTHW/1MjHZTw1V4C0vcLGZy8sW4GqrKy0bScqX3yUnZOPawQAB2p1ZFZPe6OilIlrJCJUc5m0rpZ6VA8TW4CgtK2Tc0cUH1mBl6CkkoFWFxpcf5KejoZpIhpch5makIdsJhxhh5KTi35oJiBhho+OiH1pJyJlh4yLhXxpKCRohIiJgnxqKSVohISGgXxrKiZpg4KDgHxsKydqgoGBgHxtKyhqgYCAf3xuLClrgICAf3xvLSpsgH9/f3xvLitsgH9/f3xwLyxsgH9+fn1wMC1tgH9+fn1xMS5tgH5+fn1xMi9ugH5+fn1yMzBvgH5+fn1zNDFwgH5+fn10NTJxgH5+fn11NjNygX5+fn12Nz');
    audio.play().catch(() => {}); // Ignore errors
    
    // Toast notification
    toast.success(`⏰ Cicloplegia ${eye} completata! Può procedere con l'esame.`);
  };
  
  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    // Cleanup timers on unmount
    return () => {
      if (cicloplegiaIntervalRefOD.current) {
        clearInterval(cicloplegiaIntervalRefOD.current);
      }
      if (cicloplegiaIntervalRefOS.current) {
        clearInterval(cicloplegiaIntervalRefOS.current);
      }
    };
  }, []);
  
  const formatTimerDisplay = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Load data
  useEffect(() => {
    loadData();
  }, [patientId]);

  const loadData = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/auth');
      return;
    }

    try {
      // Get current user
      const userRes = await axios.get(`${API}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentUser(userRes.data);

      // Determine target patient
      const targetId = patientId || userRes.data.id;

      // Load patient data and anamnesis reports
      const [patientRes, recordRes, anamnesisReportsRes] = await Promise.all([
        axios.get(`${API}/api/admin/patients/${targetId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null })),
        axios.get(`${API}/api/patient-records/${targetId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: {} })),
        axios.get(`${API}/api/chat/anamnesis-reports?patient_id=${targetId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] }))
      ]);

      if (patientRes.data) {
        // L'API restituisce { user: {...}, patient_record: {...}, ... }
        const patientData = patientRes.data.user || patientRes.data;
        setPatient(patientData);
      }
      setPatientRecord(recordRes.data || patientRes.data?.patient_record || {});
      
      // Set chatbot anamnesis reports
      setChatbotAnamnesisReports(anamnesisReportsRes.data || []);

      // Pre-fill anamnesis from patient record
      if (recordRes.data) {
        setEsame(prev => ({
          ...prev,
          patologie_generali: recordRes.data.pathological_anamnesis || '',
          farmaci_sistemici: recordRes.data.current_medications || [],
          patologia_oftalmica: recordRes.data.ophthalmic_pathology || '',
          terapia_colliri: recordRes.data.eye_drops_therapy || [],
          interventi_oftalmici: recordRes.data.ophthalmic_surgeries || []
        }));
      }

      // Load storico visite
      try {
        const storicoRes = await axios.get(`${API}/api/esami-clinici?patient_id=${targetId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStoricoVisite(storicoRes.data || []);
      } catch (err) {
        console.log('No visit history yet');
      }

      // Load patient tags
      if (recordRes.data?.diagnosis_tags) {
        setPatientTags(recordRes.data.diagnosis_tags);
      }

    } catch (e) {
      console.error('Error loading data:', e);
      toast.error('Errore caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  // ========== AUTOSAVE BOZZA - VERSIONE ROBUSTA ==========
  const [searchParams] = useSearchParams();
  const appointmentId = searchParams.get('appointment') || searchParams.get('appointment_id') || null;
  // Doctor: prendi dall'utente corrente quando è medico/ortottista; admin/altri usano 'any'
  const draftDoctorId = (currentUser?.role === 'doctor' || currentUser?.role === 'ortottista')
    ? (currentUser?.id || 'any')
    : 'any';
  
  // Chiave univoca per (patient, appointment, doctor): bozze diverse non si sovrascrivono
  const draftKey = `cartella_bozza_${patientId || 'nuovo'}_${appointmentId || 'libero'}_${draftDoctorId}`;
  
  // Stato indicator UI (tipo Google Docs)
  const [draftStatus, setDraftStatus] = useState({
    state: 'idle', // idle | saving | saved | error
    lastSavedAt: null, // Date
    serverSync: false, // true se ultimo save propagato al backend
  });
  
  const draftSaveTimerRef = useRef(null);
  const draftRestoredRef = useRef(false);
  
  // ----- Helper: salva bozza locale + server (idempotente) -----
  const persistDraft = useCallback(async (esameSnapshot, opts = {}) => {
    const isSilent = opts.silent === true;
    if (!patientId) return;
    // Skip se snapshot vuoto/inutile
    const hasContent = !!(
      esameSnapshot.motivo_visita ||
      esameSnapshot.diagnosi ||
      esameSnapshot.occhio_destro?.visus ||
      esameSnapshot.occhio_sinistro?.visus ||
      esameSnapshot.occhio_destro?.segmento_anteriore ||
      esameSnapshot.occhio_sinistro?.segmento_anteriore ||
      esameSnapshot.terapia ||
      esameSnapshot.patologie_generali ||
      esameSnapshot.farmaci_sistemici ||
      esameSnapshot.patologia_oftalmica_nota ||
      esameSnapshot.terapia_con_colliri ||
      esameSnapshot.allergie ||
      esameSnapshot.consigli ||
      (esameSnapshot.prescrizioni_colliri || []).length > 0
    );
    if (!hasContent) return;
    
    const now = Date.now();
    const draftToSave = { ...esameSnapshot, _savedAt: now };
    
    // 1. localStorage SEMPRE (offline-safe)
    try {
      localStorage.setItem(draftKey, JSON.stringify(draftToSave));
    } catch (e) {
      // localStorage full: prosegui con server-side
    }
    
    if (!isSilent) setDraftStatus(s => ({ ...s, state: 'saving' }));
    
    // 2. Server-side (cloud backup, idempotente)
    let serverOk = false;
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await axios.put(`${API}/api/cartella-drafts/${encodeURIComponent(draftKey)}`, {
          draft_key: draftKey,
          patient_id: patientId,
          appointment_id: appointmentId,
          data: esameSnapshot,
          client_saved_at: new Date(now).toISOString(),
        }, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 8000,
        });
        serverOk = true;
      }
    } catch (e) {
      // Server fail = ok comunque grazie a localStorage
      serverOk = false;
    }
    
    setDraftStatus({
      state: 'saved',
      lastSavedAt: new Date(now),
      serverSync: serverOk,
    });
  }, [patientId, appointmentId, draftKey]);
  
  // ----- Carica bozza al mount: priorità server > localStorage -----
  useEffect(() => {
    if (draftRestoredRef.current) return; // run only once
    if (!patientId) return;
    
    let cancelled = false;
    (async () => {
      let restored = false;
      // 1. Prova prima il server (autorevole, sopravvive a cambio device)
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const res = await axios.get(`${API}/api/cartella-drafts/${encodeURIComponent(draftKey)}`, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 5000,
          });
          if (cancelled) return;
          const serverDraft = res.data?.data;
          const savedAt = res.data?.saved_at;
          if (serverDraft && Object.keys(serverDraft).length > 0) {
            // Filtra metadati prima di setEsame
            const { _savedAt, ...draftData } = serverDraft;
            setEsame(prev => ({ ...prev, ...draftData }));
            setDraftStatus({
              state: 'saved',
              lastSavedAt: savedAt ? new Date(savedAt) : new Date(),
              serverSync: true,
            });
            toast.info('Bozza ripristinata (cloud) ☁️', { duration: 5000 });
            restored = true;
          }
        }
      } catch (e) {
        // 404 atteso quando non esiste; altri errori → fallback localStorage
      }
      
      // 2. Fallback localStorage
      if (!restored) {
        const savedDraft = localStorage.getItem(draftKey);
        if (savedDraft) {
          try {
            const draft = JSON.parse(savedDraft);
            const savedTime = draft._savedAt || 0;
            const hoursSinceSave = (Date.now() - savedTime) / (1000 * 60 * 60);
            if (hoursSinceSave < 72) {
              const { _savedAt, ...draftData } = draft;
              if (!cancelled) {
                setEsame(prev => ({ ...prev, ...draftData }));
                setDraftStatus({
                  state: 'saved',
                  lastSavedAt: savedTime ? new Date(savedTime) : null,
                  serverSync: false,
                });
                toast.info('Bozza ripristinata (locale) 💾', { duration: 5000 });
              }
            } else {
              localStorage.removeItem(draftKey);
            }
          } catch (e) {
            localStorage.removeItem(draftKey);
          }
        }
      }
      draftRestoredRef.current = true;
    })();
    
    return () => { cancelled = true; };
  }, [patientId, draftKey]);
  
  // ----- Debounce 1.5s su modifica esame -----
  useEffect(() => {
    if (!draftRestoredRef.current) return; // non salvare durante mount
    if (draftSaveTimerRef.current) clearTimeout(draftSaveTimerRef.current);
    draftSaveTimerRef.current = setTimeout(() => {
      persistDraft(esame, { silent: false });
    }, 1500);
    return () => {
      if (draftSaveTimerRef.current) clearTimeout(draftSaveTimerRef.current);
    };
  }, [esame, persistDraft]);
  
  // ----- Beforeunload: flush sincrono prima di chiudere/refresh -----
  useEffect(() => {
    const handler = () => {
      // Flush localStorage sincrono (sempre rapido)
      try {
        localStorage.setItem(draftKey, JSON.stringify({ ...esame, _savedAt: Date.now() }));
      } catch (e) {
        // ignore
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [esame, draftKey]);
  
  // ----- API pubbliche legacy (saveDraftNow / clearDraft) preservate per chiamate esistenti -----
  const saveDraftNow = useCallback(() => {
    persistDraft(esame, { silent: false });
  }, [esame, persistDraft]);
  
  const clearDraft = useCallback(async () => {
    try {
      localStorage.removeItem(draftKey);
    } catch (e) {/* ignore */}
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await axios.delete(`${API}/api/cartella-drafts/${encodeURIComponent(draftKey)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch (e) {/* ignore */}
    setDraftStatus({ state: 'idle', lastSavedAt: null, serverSync: false });
  }, [draftKey]);

  // Update eye field
  const updateEye = (eye, field, value) => {
    setEsame(prev => ({
      ...prev,
      [eye]: {
        ...prev[eye],
        [field]: value
      }
    }));
  };

  // Handle image upload (file input or drag & drop)
  const handleImageUpload = (e, eye, field) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Per favore seleziona un\'immagine');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      updateEye(eye, field, event.target.result);
    };
    reader.readAsDataURL(file);
  };

  // Carica riepilogo pagamento quando si apre il modale Sconto/Pro bono
  useEffect(() => {
    if (!showPaymentModal || !appointmentId) return;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/api/appointments/${appointmentId}/payment-summary`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setPaymentSummary(res.data);
      } catch (e) {
        if (!cancelled) {
          setPaymentSummary({
            appointment_id: appointmentId,
            base_amount: 0,
            manual_discount_percent: 0,
            pro_bono: false,
            payment_timing: 'end',
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [showPaymentModal, appointmentId]);

  // Carica riepilogo pagamento al mount (per il badge in header)
  useEffect(() => {
    if (!appointmentId) return;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/api/appointments/${appointmentId}/payment-summary`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setPaymentSummary(res.data);
      } catch (e) {
        // Silently ignore - appuntamento potrebbe non esistere
      }
    })();
    return () => { cancelled = true; };
  }, [appointmentId]);

  // Handle drag & drop
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e, eye, field) => {
    e.preventDefault();
    e.stopPropagation();
    
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Per favore trascina un\'immagine');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      updateEye(eye, field, event.target.result);
      toast.success('Immagine caricata!');
    };
    reader.readAsDataURL(file);
  };

  // Add prescribed drug (from drug catalog)
  const addPrescription = (drug) => {
    setEsame(prev => ({
      ...prev,
      prescrizioni_colliri: [
        ...prev.prescrizioni_colliri,
        {
          farmaco_id: drug.id,
          nome_commerciale: drug.nome_commerciale || '',
          principio_attivo: drug.principio_attivo || '',
          concentrazione: drug.concentrazione || '',
          forma_farmaceutica: drug.forma_farmaceutica || '',
          posologia: drug.posologia_standard || drug.posologia || '',
          durata: drug.durata_consigliata || drug.durata || '',
          occhio: 'OU',
          istruzioni: '',
          controindicazioni: drug.controindicazioni || '',
          note: ''
        }
      ]
    }));
    setShowDrugSelector(false);
  };

  // Add manual prescription (campi vuoti da compilare a mano)
  const addManualPrescription = () => {
    setEsame(prev => ({
      ...prev,
      prescrizioni_colliri: [
        ...prev.prescrizioni_colliri,
        {
          farmaco_id: 'manual_' + Date.now(),
          nome_commerciale: '',
          principio_attivo: '',
          concentrazione: '',
          forma_farmaceutica: 'Collirio',
          posologia: '',
          durata: '',
          occhio: 'OU',
          istruzioni: '',
          controindicazioni: '',
          note: '',
          _manual: true
        }
      ]
    }));
    setShowDrugSelector(false);
  };

  // Update prescription
  const updatePrescription = (index, field, value) => {
    setEsame(prev => ({
      ...prev,
      prescrizioni_colliri: prev.prescrizioni_colliri.map((p, i) => 
        i === index ? { ...p, [field]: value } : p
      )
    }));
  };

  // Remove prescription
  const removePrescription = (index) => {
    setEsame(prev => ({
      ...prev,
      prescrizioni_colliri: prev.prescrizioni_colliri.filter((_, i) => i !== index)
    }));
  };

  // Handle retinography upload
  const handleRetinographyUpload = async (eye, file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      updateEye(eye, 'retinografia_url', e.target.result);
    };
    reader.readAsDataURL(file);
  };

  // Save examination
  const saveEsame = async () => {
    const token = localStorage.getItem('token');
    const targetId = patientId || currentUser?.id;

    try {
      const response = await axios.post(`${API}/api/esame-clinico`, {
        patient_id: targetId,
        doctor_id: currentUser?.id,
        ...esame
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Visita salvata nello storico!');
      
      // Pulisci la bozza dopo il salvataggio
      clearDraft();
      
      // Save esame ID for concludi visita
      setLastSavedEsameId(response.data.id);
      
      // Reload storico
      const storicoRes = await axios.get(`${API}/api/esami-clinici?patient_id=${targetId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStoricoVisite(storicoRes.data || []);
      
      // Reset form for new visit
      resetEsameForm();
      
    } catch (e) {
      console.error('Error saving:', e);
      toast.error('Errore nel salvataggio');
    }
  };

  // Richiedi OTP per firma digitale
  const requestSignatureOTP = async () => {
    setOtpLoading(true);
    const token = localStorage.getItem('token');
    const esameId = lastSavedEsameId || storicoVisite[0]?.id;
    
    try {
      await axios.post(`${API}/api/signature/request-otp`, {
        esame_id: esameId,
        patient_id: patientId || currentUser?.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('📱 OTP inviato via SMS! Controlla il telefono.');
      setOtpStep('verify');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore invio OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  // Verifica OTP e ottieni token di firma
  const verifySignatureOTP = async () => {
    if (!concludiData.signature_otp || concludiData.signature_otp.length !== 6) {
      toast.error('Inserisci il codice OTP a 6 cifre');
      return;
    }
    
    setOtpLoading(true);
    const token = localStorage.getItem('token');
    const esameId = lastSavedEsameId || storicoVisite[0]?.id;
    
    try {
      const response = await axios.post(`${API}/api/signature/verify-otp`, {
        otp: concludiData.signature_otp,
        esame_id: esameId,
        patient_id: patientId || currentUser?.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSignatureToken(response.data.signature_token);
      setOtpStep('done');
      toast.success('✅ Firma digitale verificata!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Codice OTP non valido');
    } finally {
      setOtpLoading(false);
    }
  };

  // Concludi Visita - send email/SMS and update tags
  const concludiVisita = async () => {
    if (!lastSavedEsameId && storicoVisite.length === 0) {
      toast.error('Devi prima salvare la visita');
      return;
    }
    
    const esameId = lastSavedEsameId || storicoVisite[0]?.id;
    if (!esameId) {
      toast.error('Nessuna visita da concludere');
      return;
    }

    setConcludiLoading(true);
    const token = localStorage.getItem('token');
    const targetId = patientId || currentUser?.id;

    try {
      const response = await axios.post(`${API}/api/concludi-visita`, {
        esame_id: esameId,
        patient_id: targetId,
        diagnosi: concludiData.diagnosi,
        terapia: concludiData.terapia,
        consigli: concludiData.consigli,
        prossimo_controllo: concludiData.prossimo_controllo,
        tags_to_add: tagsToAdd,
        tags_to_remove: tagsToRemove,
        send_email: concludiData.send_email,
        send_sms: concludiData.send_sms,
        signature_otp: signatureToken || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Visita conclusa con successo!');
      
      if (response.data.email_sent) {
        toast.success('Email con referto inviata al paziente');
      }
      if (response.data.sms_sent) {
        toast.success('SMS inviato al paziente');
      }
      if (response.data.signature_verified) {
        toast.success('Firma digitale verificata');
      }

      // Update local tags
      const newTags = [...patientTags.filter(t => !tagsToRemove.includes(t)), ...tagsToAdd];
      setPatientTags([...new Set(newTags)]);

      // Reset state
      setShowConcludiModal(false);
      setShowOtpModal(false);
      setSignatureToken(null);
      setOtpStep('request');
      setConcludiData({
        diagnosi: '',
        terapia: '',
        consigli: '',
        prossimo_controllo: '',
        send_email: true,
        send_sms: true,
        signature_otp: ''
      });
      setTagsToAdd([]);
      setTagsToRemove([]);
      // Pulisci la bozza autosalvata (server + localStorage) dopo conclusione definitiva
      try { await clearDraft(); } catch (e) { /* ignore */ }
      
      setLastSavedEsameId(null);

    } catch (e) {
      console.error('Error concluding visit:', e);
      toast.error(e.response?.data?.detail || 'Errore nella conclusione della visita');
    } finally {
      setConcludiLoading(false);
    }
  };

  // Add tag to be added list
  const addTagToList = (tag) => {
    const normalizedTag = tag.startsWith('#') ? tag.toLowerCase() : `#${tag.toLowerCase()}`;
    if (!tagsToAdd.includes(normalizedTag) && !patientTags.includes(normalizedTag)) {
      setTagsToAdd([...tagsToAdd, normalizedTag]);
    }
    setNewTag('');
  };

  // Remove tag from add list or mark existing for removal
  const removeTagFromList = (tag, isExisting) => {
    if (isExisting) {
      if (!tagsToRemove.includes(tag)) {
        setTagsToRemove([...tagsToRemove, tag]);
      }
    } else {
      setTagsToAdd(tagsToAdd.filter(t => t !== tag));
    }
  };

  // Undo tag removal
  const undoTagRemoval = (tag) => {
    setTagsToRemove(tagsToRemove.filter(t => t !== tag));
  };

  // Get AI Diagnosis Suggestion
  const getAiDiagnosis = async () => {
    const token = localStorage.getItem('token');
    setAiDiagnosisLoading(true);
    setAiDiagnosis(null);
    
    try {
      // Extract base64 from data URLs if present
      const extractBase64 = (dataUrl) => {
        if (!dataUrl || !dataUrl.startsWith('data:')) return null;
        return dataUrl.split(',')[1];
      };
      
      const requestData = {
        patient_id: patientId || currentUser?.id,
        eta: patient?.age || 50,
        sesso: patient?.gender || 'N/D',
        patologie_generali: esame.patologie_generali,
        farmaci_sistemici: esame.farmaci_sistemici,
        patologia_oftalmica: esame.patologia_oftalmica,
        motivo_visita: esame.motivo_visita,
        // OD
        visus_od: esame.occhio_destro.visus,
        tonometria_od: esame.occhio_destro.tonometria,
        segmento_anteriore_od: esame.occhio_destro.segmento_anteriore,
        mezzi_diottrici_od: esame.occhio_destro.mezzi_diottrici,
        conta_endoteliale_od: `CV: ${esame.occhio_destro.conta_cv || 'N/A'}, CD: ${esame.occhio_destro.conta_cd || 'N/A'}, CT: ${esame.occhio_destro.conta_ct || 'N/A'}`,
        // OS
        visus_os: esame.occhio_sinistro.visus,
        tonometria_os: esame.occhio_sinistro.tonometria,
        segmento_anteriore_os: esame.occhio_sinistro.segmento_anteriore,
        mezzi_diottrici_os: esame.occhio_sinistro.mezzi_diottrici,
        conta_endoteliale_os: `CV: ${esame.occhio_sinistro.conta_cv || 'N/A'}, CD: ${esame.occhio_sinistro.conta_cd || 'N/A'}, CT: ${esame.occhio_sinistro.conta_ct || 'N/A'}`,
        // Referti strumentali già analizzati
        referto_oct_od: esame.occhio_destro.oct_referto || '',
        referto_oct_os: esame.occhio_sinistro.oct_referto || '',
        referto_topografia_od: esame.occhio_destro.topografia_referto || '',
        referto_topografia_os: esame.occhio_sinistro.topografia_referto || '',
        referto_campo_visivo_od: esame.occhio_destro.campo_visivo_referto || '',
        referto_campo_visivo_os: esame.occhio_sinistro.campo_visivo_referto || '',
        referto_retinografia_od: esame.occhio_destro.retinografia_referto || '',
        referto_retinografia_os: esame.occhio_sinistro.retinografia_referto || '',
        // ========== MOTILITÀ OCULARE COMPLETA ==========
        motilita_oculare: {
          allineamento: esame.motilita_oculare.allineamento,
          angolo_lontano: esame.motilita_oculare.angolo_lontano,
          angolo_vicino: esame.motilita_oculare.angolo_vicino,
          angolo_sguardo_alto: esame.motilita_oculare.angolo_sguardo_alto,
          angolo_sguardo_basso: esame.motilita_oculare.angolo_sguardo_basso,
          angolo_sguardo_destra: esame.motilita_oculare.angolo_sguardo_destra,
          angolo_sguardo_sinistra: esame.motilita_oculare.angolo_sguardo_sinistra,
          posizioni_sguardo: esame.motilita_oculare.posizioni_sguardo,
          strabismo_9_posizioni: esame.motilita_oculare.strabismo_9_posizioni,
          krimsky: esame.motilita_oculare.krimsky,
          stereo_lang_ii: esame.motilita_oculare.stereo_lang_ii,
          stereopsi_angolo_corretto: esame.motilita_oculare.stereopsi_angolo_corretto,
          luci_worth: esame.motilita_oculare.luci_worth,
          head_tilt_test: esame.motilita_oculare.head_tilt_test,
          note_motilita: esame.motilita_oculare.note_motilita
        },
        // Referto Gaze9 se presente
        referto_gaze9: esame.gaze9_referto || '',
        // Images (base64)
        oct_od_base64: extractBase64(esame.occhio_destro.oct_url),
        oct_os_base64: extractBase64(esame.occhio_sinistro.oct_url),
        topografia_od_base64: extractBase64(esame.occhio_destro.topografia_url),
        topografia_os_base64: extractBase64(esame.occhio_sinistro.topografia_url),
        campo_visivo_od_base64: extractBase64(esame.occhio_destro.campo_visivo_url),
        campo_visivo_os_base64: extractBase64(esame.occhio_sinistro.campo_visivo_url),
        // Immagine Gaze9 se presente
        gaze9_base64: extractBase64(esame.gaze9_image_url)
      };
      
      const response = await axios.post(`${API}/api/ai/diagnosi-suggerita`, requestData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAiDiagnosis(response.data);
      toast.success('Suggerimento AI generato!');
      
    } catch (e) {
      console.error('AI Diagnosis error:', e);
      toast.error('Errore nel servizio AI: ' + (e.response?.data?.detail || e.message));
    } finally {
      setAiDiagnosisLoading(false);
    }
  };

  // Confirm and send diagnosis to patient
  const confirmAndSendDiagnosis = async (sendEmail, sendSms) => {
    if (!aiDiagnosis?.diagnosis_id) {
      toast.error('Nessuna diagnosi da confermare');
      return;
    }
    
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.post(`${API}/api/ai/conferma-diagnosi`, {
        diagnosis_id: aiDiagnosis.diagnosis_id,
        send_email: sendEmail,
        send_sms: sendSms
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      if (response.data.note) {
        toast.info(response.data.note, { duration: 5000 });
      }
    } catch (e) {
      console.error('Confirm diagnosis error:', e);
      toast.error('Errore: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Get AI Therapy suggestion
  const getAiTerapia = async () => {
    const token = localStorage.getItem('token');
    setAiTerapiaLoading(true);
    setAiTerapia(null);
    
    try {
      // Build diagnosis from AI or manual
      const diagnosi = aiDiagnosis?.diagnosis?.diagnosi_principale || concludiData.diagnosi || esame.patologia_oftalmica || 'Non specificata';
      
      // Calculate age
      let eta = null;
      if (patient?.date_of_birth || patient?.birth_date) {
        const birthDate = new Date(patient.date_of_birth || patient.birth_date);
        eta = Math.floor((new Date() - birthDate) / (365.25 * 24 * 60 * 60 * 1000));
      }
      
      const requestData = {
        diagnosi: diagnosi,
        eta_paziente: eta,
        sesso: patient?.gender || 'N/D',
        occhio: 'OU', // Can be refined based on diagnosis
        esame_clinico: {
          visus_od: esame.occhio_destro.visus,
          visus_os: esame.occhio_sinistro.visus,
          tono_od: esame.occhio_destro.tonometria,
          tono_os: esame.occhio_sinistro.tonometria,
          seg_ant_od: esame.occhio_destro.segmento_anteriore,
          seg_ant_os: esame.occhio_sinistro.segmento_anteriore
        },
        patologie_concomitanti: esame.patologie_generali ? [esame.patologie_generali] : [],
        farmaci_in_uso: esame.farmaci_sistemici || [],
        allergie: [] // Could be added to patient profile
      };
      
      const response = await axios.post(`${API}/api/ai-terapia-suggerita`, requestData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAiTerapia(response.data);
      setShowAiTerapiaModal(true);
      toast.success('Suggerimento terapia generato!');
      
    } catch (e) {
      console.error('AI Terapia error:', e);
      toast.error('Errore AI Terapia: ' + (e.response?.data?.detail || e.message));
    } finally {
      setAiTerapiaLoading(false);
    }
  };

  // Apply AI suggested therapy to prescription
  const applyAiTerapia = () => {
    if (!aiTerapia?.farmaci_consigliati) return;
    
    // Add each suggested drug to prescriptions
    aiTerapia.farmaci_consigliati.forEach(farmaco => {
      addPrescription({
        nome_commerciale: farmaco.nome,
        principio_attivo: farmaco.concentrazione || '',
        posologia: farmaco.posologia,
        durata: farmaco.durata,
        occhio: farmaco.occhio || 'OU',
        note: 'Suggerito da AI'
      });
    });
    
    setShowAiTerapiaModal(false);
    toast.success('Terapia applicata alla prescrizione!');
  };

  // Voice Recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        audioChunksRef.current.push(e.data);
      };
      
      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await transcribeAudio(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      toast.success('Registrazione avviata... Parla ora!');
    } catch (e) {
      console.error('Recording error:', e);
      toast.error('Errore accesso microfono. Verifica i permessi.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (audioBlob) => {
    const token = localStorage.getItem('token');
    setTranscribing(true);
    
    try {
      // Convert blob to base64
      const reader = new FileReader();
      const base64Promise = new Promise((resolve) => {
        reader.onloadend = () => {
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
      });
      reader.readAsDataURL(audioBlob);
      const audioBase64 = await base64Promise;
      
      const response = await axios.post(`${API}/api/voice-transcription`, {
        audio_base64: audioBase64,
        audio_format: 'webm',
        context: 'cartella_clinica'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        // Append transcription to consigli or notes
        setEsame(prev => ({
          ...prev,
          consigli: (prev.consigli || '') + '\n' + response.data.transcription
        }));
        toast.success('Trascrizione completata!');
      }
    } catch (e) {
      console.error('Transcription error:', e);
      toast.error('Errore trascrizione: ' + (e.response?.data?.detail || e.message));
    } finally {
      setTranscribing(false);
    }
  };

  // Load consensi for patient
  const loadConsensi = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API}/api/consensi-informati/patient/${patientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConsensi(res.data);
    } catch (e) {
      console.error('Error loading consensi:', e);
    }
  };

  // Upload consenso informato
  const uploadConsenso = async (file, tipoConsenso) => {
    const token = localStorage.getItem('token');
    setUploadingConsenso(true);
    
    try {
      // Convert file to base64
      const reader = new FileReader();
      const base64Promise = new Promise((resolve) => {
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
      });
      reader.readAsDataURL(file);
      const fileBase64 = await base64Promise;
      
      await axios.post(`${API}/api/consensi-informati/upload`, {
        patient_id: patientId,
        tipo_consenso: tipoConsenso,
        file_base64: fileBase64,
        file_name: file.name
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Consenso caricato con successo!');
      loadConsensi();
      setShowUploadConsenso(false);
    } catch (e) {
      console.error('Upload consenso error:', e);
      toast.error('Errore upload: ' + (e.response?.data?.detail || e.message));
    } finally {
      setUploadingConsenso(false);
    }
  };

  // Reset form for new visit
  const resetEsameForm = () => {
    setEsame(prev => ({
      ...prev,
      motivo_visita: '',
      occhio_destro: {
        ...prev.occhio_destro,
        visus: '',
        visus_con_correzione: '',
        autoref_sfera: '',
        autoref_cilindro: '',
        autoref_asse: '',
        prescrizione_sfera: '',
        prescrizione_cilindro: '',
        prescrizione_asse: '',
        tonometria_senza_gocce: '',
        tonometria_con_gocce: '',
        annessi: 'Sani',
        segmento_anteriore: '',
        mezzi_diottrici: 'trasparenti',
        cornea_fluo: '',
        retinografia_url: '',
        retinografia_referto: '',
        topografia_url: '',
        topografia_referto: '',
        topografia_cheratocono: 'negativo',
        oct_url: '',
        oct_referto: '',
        conta_endoteliale: '',
        conta_endoteliale_note: ''
      },
      occhio_sinistro: {
        ...prev.occhio_sinistro,
        visus: '',
        visus_con_correzione: '',
        autoref_sfera: '',
        autoref_cilindro: '',
        autoref_asse: '',
        prescrizione_sfera: '',
        prescrizione_cilindro: '',
        prescrizione_asse: '',
        tonometria_senza_gocce: '',
        tonometria_con_gocce: '',
        annessi: 'Sani',
        segmento_anteriore: '',
        mezzi_diottrici: 'trasparenti',
        cornea_fluo: '',
        retinografia_url: '',
        retinografia_referto: '',
        topografia_url: '',
        topografia_referto: '',
        topografia_cheratocono: 'negativo',
        oct_url: '',
        oct_referto: '',
        conta_endoteliale: '',
        conta_endoteliale_note: ''
      },
      consigli: '',
      prossima_visita_mesi: null,
      prescrizioni_colliri: []
    }));
  };

  // Load a previous visit into view
  const loadVisita = (visita) => {
    setSelectedVisita(visita);
  };

  // Import all data from a previous visit into the current draft
  const importFromPreviousVisit = (visita) => {
    if (!visita) return;
    
    const newEsame = { ...esame };
    
    // Import anamnesi fields
    if (visita.patologie_generali) newEsame.patologie_generali = visita.patologie_generali;
    if (visita.farmaci_sistemici?.length) newEsame.farmaci_sistemici = [...visita.farmaci_sistemici];
    if (visita.patologia_oftalmica) newEsame.patologia_oftalmica = visita.patologia_oftalmica;
    if (visita.terapia_colliri?.length) newEsame.terapia_colliri = [...visita.terapia_colliri];
    if (visita.allergie_farmaci) newEsame.allergie_farmaci = visita.allergie_farmaci;
    if (visita.allergie_colliri) newEsame.allergie_colliri = visita.allergie_colliri;
    if (visita.interventi_oftalmici?.length) newEsame.interventi_oftalmici = visita.interventi_oftalmici.map(i => ({...i}));
    if (visita.storia_bendaggio) newEsame.storia_bendaggio = { ...visita.storia_bendaggio };
    if (visita.motivo_visita) newEsame.motivo_visita = visita.motivo_visita;
    if (visita.motivo_visita_dettaglio) newEsame.motivo_visita_dettaglio = visita.motivo_visita_dettaglio;
    if (visita.sintomi_riferiti) newEsame.sintomi_riferiti = visita.sintomi_riferiti;
    
    // Import occhio destro
    if (visita.occhio_destro) {
      newEsame.occhio_destro = { ...newEsame.occhio_destro };
      const od = visita.occhio_destro;
      Object.keys(od).forEach(key => {
        if (od[key] !== '' && od[key] !== null && od[key] !== undefined) {
          newEsame.occhio_destro[key] = od[key];
        }
      });
    }
    
    // Import occhio sinistro
    if (visita.occhio_sinistro) {
      newEsame.occhio_sinistro = { ...newEsame.occhio_sinistro };
      const os = visita.occhio_sinistro;
      Object.keys(os).forEach(key => {
        if (os[key] !== '' && os[key] !== null && os[key] !== undefined) {
          newEsame.occhio_sinistro[key] = os[key];
        }
      });
    }
    
    // Import motilita oculare
    if (visita.motilita_oculare) {
      newEsame.motilita_oculare = { ...newEsame.motilita_oculare };
      const mo = visita.motilita_oculare;
      Object.keys(mo).forEach(key => {
        if (typeof mo[key] === 'object' && mo[key] !== null) {
          newEsame.motilita_oculare[key] = { ...mo[key] };
        } else if (mo[key] !== '' && mo[key] !== null && mo[key] !== undefined) {
          newEsame.motilita_oculare[key] = mo[key];
        }
      });
    }
    
    // Import ecografia
    if (visita.ecografia) newEsame.ecografia = { ...visita.ecografia };
    
    // Import bendaggio
    if (visita.bendaggio) newEsame.bendaggio = { ...visita.bendaggio };
    
    // Import prescrizioni colliri
    if (visita.prescrizioni_colliri?.length) {
      newEsame.prescrizioni_colliri = visita.prescrizioni_colliri.map(p => ({...p}));
    }
    
    // Import consigli
    if (visita.consigli) newEsame.consigli = visita.consigli;
    
    // Reset stato to draft
    newEsame.stato_visita = 'draft';
    newEsame.firma_digitale = null;
    
    setEsame(newEsame);
    setActiveTab('esame');
    toast.success('Visita precedente importata nella bozza. Modifica i campi necessari e salva.');
  };

  // Print clinical record - generates a formatted print view
  const printPrescription = () => {
    // Create a new window with print-friendly content
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Popup bloccato. Abilita i popup per stampare.');
      return;
    }

    const formatCorneaFluo = (value) => {
      if (value === 'fluo_positivo') return 'Fluo +';
      if (value === 'fluo_negativo') return 'Fluo -';
      return 'N/D';
    };

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Cartella Clinica - ${patient?.first_name || ''} ${patient?.last_name || ''}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; font-size: 11px; padding: 15px; line-height: 1.4; }
          .header { display: flex; justify-content: space-between; border-bottom: 2px solid #2A9FCB; padding-bottom: 10px; margin-bottom: 15px; }
          .logo { font-size: 18px; font-weight: bold; color: #2A9FCB; }
          .subtitle { font-size: 10px; color: #666; }
          .patient-info { background: #f5f5f5; padding: 10px; border-radius: 5px; margin-bottom: 15px; }
          .patient-info h2 { font-size: 14px; margin-bottom: 8px; color: #2A9FCB; }
          .patient-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
          .patient-grid div { font-size: 10px; }
          .patient-grid label { font-weight: bold; display: block; color: #666; }
          .section { margin-bottom: 15px; page-break-inside: avoid; }
          .section h3 { font-size: 12px; color: #2A9FCB; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-bottom: 10px; }
          .eyes-container { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; }
          .eye-box { border: 1px solid #ddd; border-radius: 5px; padding: 10px; }
          .eye-box.od { border-top: 3px solid #2A9FCB; }
          .eye-box.os { border-top: 3px solid #F59E0B; }
          .eye-title { font-weight: bold; font-size: 12px; text-align: center; margin-bottom: 10px; color: #2A9FCB; }
          .field-row { display: grid; grid-template-columns: 1fr 1fr; gap: 5px; margin-bottom: 5px; }
          .field { font-size: 10px; }
          .field label { font-weight: bold; color: #2A9FCB; display: inline-block; min-width: 80px; }
          .field span { display: inline; color: #1F2937; font-weight: 500; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th, td { border: 1px solid #ddd; padding: 5px; text-align: left; font-size: 10px; }
          th { background: #e8f4fd; color: #2A9FCB; font-weight: bold; }
          .footer { margin-top: 20px; text-align: center; font-size: 9px; color: #666; border-top: 1px solid #ddd; padding-top: 10px; }
          .anamnesi-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
          .anamnesi-item { background: #e8f4fd; padding: 8px; border-radius: 4px; font-size: 10px; }
          .anamnesi-item label { font-weight: bold; color: #2A9FCB; display: block; margin-bottom: 3px; }
          h3 { color: #2A9FCB; border-bottom: 2px solid #2A9FCB; padding-bottom: 5px; }
          @media print {
            body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
            .eye-box.od { border-top-color: #2A9FCB !important; }
            .eye-box.os { border-top-color: #F59E0B !important; }
            .field label { color: #2A9FCB !important; }
            .field span { color: #1F2937 !important; }
            h3 { color: #2A9FCB !important; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <div class="logo">PRIME VISION</div>
            <div class="subtitle">Centro di Eccellenza Oftalmologica</div>
            <div class="subtitle">Via Santa Sofia 10, Roma</div>
          </div>
          <div style="text-align: right;">
            <div>Data: ${new Date().toLocaleDateString('it-IT')}</div>
            <div>Dr. ${currentUser?.first_name || ''} ${currentUser?.last_name || ''}</div>
          </div>
        </div>

        <div class="patient-info">
          <h2>Dati Paziente</h2>
          <div class="patient-grid">
            <div><label>Nome</label>${patient?.first_name || 'N/D'} ${patient?.last_name || ''}</div>
            <div><label>Data Nascita</label>${patient?.birth_date || 'N/D'}</div>
            <div><label>Telefono</label>${patient?.phone || 'N/D'}</div>
            <div><label>Email</label>${patient?.email || 'N/D'}</div>
          </div>
        </div>

        <div class="section">
          <h3>Anamnesi</h3>
          <div class="anamnesi-grid">
            <div class="anamnesi-item">
              <label>Patologie Generali</label>
              ${esame.patologie_generali || 'Nessuna'}
            </div>
            <div class="anamnesi-item">
              <label>Farmaci Sistemici</label>
              ${Array.isArray(esame.farmaci_sistemici) ? esame.farmaci_sistemici.join(', ') || 'Nessuno' : esame.farmaci_sistemici || 'Nessuno'}
            </div>
            <div class="anamnesi-item">
              <label>Patologia Oftalmica Nota</label>
              ${esame.patologia_oftalmica || 'Nessuna'}
            </div>
            <div class="anamnesi-item">
              <label>Motivo Visita</label>
              ${esame.motivo_visita || 'N/D'}
            </div>
          </div>
        </div>

        <div class="section">
          <h3>Esame Obiettivo</h3>
          <div class="eyes-container">
            <div class="eye-box od">
              <div class="eye-title">👁️ OCCHIO DESTRO (OD)</div>
              <div class="field-row">
                <div class="field"><label>Visus Naturale</label><span>${esame.occhio_destro.visus || 'N/D'}</span></div>
                <div class="field"><label>Visus Corretto</label><span>${esame.occhio_destro.visus_con_correzione || 'N/D'}</span></div>
              </div>
              <div class="field-row">
                <div class="field"><label>Autoref</label><span>${esame.occhio_destro.autoref_sfera || ''} ${esame.occhio_destro.autoref_cilindro || ''} ${esame.occhio_destro.autoref_asse || ''}</span></div>
                <div class="field"><label>Cicloplegia</label><span>${esame.occhio_destro.autoref_ciclo_sfera || ''} ${esame.occhio_destro.autoref_ciclo_cilindro || ''} ${esame.occhio_destro.autoref_ciclo_asse || ''}</span></div>
              </div>
              <div class="field-row">
                <div class="field"><label>Prescrizione</label><span>${esame.occhio_destro.prescrizione_sfera || ''} ${esame.occhio_destro.prescrizione_cilindro || ''} ${esame.occhio_destro.prescrizione_asse || ''}</span></div>
                <div class="field"><label>Tonometria</label><span>${esame.occhio_destro.tonometria || 'N/D'} mmHg</span></div>
              </div>
              <div class="field"><label>Annessi</label><span>${esame.occhio_destro.annessi || 'Sani'}</span></div>
              <div class="field"><label>Mezzi Diottrici</label><span>${esame.occhio_destro.mezzi_diottrici || 'trasparenti'}</span></div>
              <div class="field"><label>Cornea Fluo</label><span>${formatCorneaFluo(esame.occhio_destro.cornea_fluo)}</span></div>
              <div class="field"><label>Segmento Anteriore</label><span>${esame.occhio_destro.segmento_anteriore || 'Nella norma'}</span></div>
            </div>
            <div class="eye-box os">
              <div class="eye-title">👁️ OCCHIO SINISTRO (OS)</div>
              <div class="field-row">
                <div class="field"><label>Visus Naturale</label><span>${esame.occhio_sinistro.visus || 'N/D'}</span></div>
                <div class="field"><label>Visus Corretto</label><span>${esame.occhio_sinistro.visus_con_correzione || 'N/D'}</span></div>
              </div>
              <div class="field-row">
                <div class="field"><label>Autoref</label><span>${esame.occhio_sinistro.autoref_sfera || ''} ${esame.occhio_sinistro.autoref_cilindro || ''} ${esame.occhio_sinistro.autoref_asse || ''}</span></div>
                <div class="field"><label>Cicloplegia</label><span>${esame.occhio_sinistro.autoref_ciclo_sfera || ''} ${esame.occhio_sinistro.autoref_ciclo_cilindro || ''} ${esame.occhio_sinistro.autoref_ciclo_asse || ''}</span></div>
              </div>
              <div class="field-row">
                <div class="field"><label>Prescrizione</label><span>${esame.occhio_sinistro.prescrizione_sfera || ''} ${esame.occhio_sinistro.prescrizione_cilindro || ''} ${esame.occhio_sinistro.prescrizione_asse || ''}</span></div>
                <div class="field"><label>Tonometria</label><span>${esame.occhio_sinistro.tonometria || 'N/D'} mmHg</span></div>
              </div>
              <div class="field"><label>Annessi</label><span>${esame.occhio_sinistro.annessi || 'Sani'}</span></div>
              <div class="field"><label>Mezzi Diottrici</label><span>${esame.occhio_sinistro.mezzi_diottrici || 'trasparenti'}</span></div>
              <div class="field"><label>Cornea Fluo</label><span>${formatCorneaFluo(esame.occhio_sinistro.cornea_fluo)}</span></div>
              <div class="field"><label>Segmento Anteriore</label><span>${esame.occhio_sinistro.segmento_anteriore || 'Nella norma'}</span></div>
            </div>
          </div>
        </div>

        ${esame.prescrizioni_colliri.length > 0 ? `
        <div class="section">
          <h3>Prescrizioni Farmaci / Terapia</h3>
          ${esame.allergie ? `<p style="background:#FFFBEB;border:1px solid #F59E0B;padding:8px;border-radius:6px;font-size:11px;"><strong>⚠️ Allergie segnalate:</strong> ${esame.allergie}</p>` : ''}
          <table>
            <thead>
              <tr>
                <th>Farmaco</th>
                <th>Principio Attivo</th>
                <th>Conc./%</th>
                <th>Forma</th>
                <th>Posologia</th>
                <th>Durata</th>
                <th>Occhio</th>
                <th>Istruzioni</th>
              </tr>
            </thead>
            <tbody>
              ${esame.prescrizioni_colliri.map(p => `
                <tr>
                  <td><strong>${p.nome_commerciale || ''}</strong></td>
                  <td>${p.principio_attivo || ''}</td>
                  <td>${p.concentrazione || ''}</td>
                  <td>${p.forma_farmaceutica || 'Collirio'}</td>
                  <td>${p.posologia || ''}</td>
                  <td>${p.durata || ''}</td>
                  <td>${p.occhio || 'OU'}</td>
                  <td>${p.istruzioni || ''}</td>
                </tr>
                ${p.controindicazioni ? `<tr><td colspan="8" style="font-size:10px;color:#92400E;background:#FFFBEB;padding:4px 8px;"><strong>Controindicazioni:</strong> ${p.controindicazioni}</td></tr>` : ''}
              `).join('')}
            </tbody>
          </table>
        </div>
        ` : ''}

        ${esame.bendaggio?.prescritto ? `
        <div class="section">
          <h3>Terapia Occlusiva (Bendaggio)</h3>
          <div class="anamnesi-item">
            <p><strong>Occhio:</strong> ${esame.bendaggio.occhio === 'OD' ? 'Occhio Destro (OD)' : esame.bendaggio.occhio === 'OS' ? 'Occhio Sinistro (OS)' : 'Alternato'}</p>
            <p><strong>Durata giornaliera:</strong> ${esame.bendaggio.ore_giorno === 'tutto_giorno' ? 'Tutto il giorno' : (esame.bendaggio.ore_giorno + ' ore al giorno')}</p>
            <p><strong>Durata terapia:</strong> ${esame.bendaggio.durata_settimane === 'continuo' ? 'Continuo fino a controllo' : (esame.bendaggio.durata_settimane + ' settimane')}</p>
            ${esame.bendaggio.note ? `<p><strong>Note:</strong> ${esame.bendaggio.note}</p>` : ''}
          </div>
        </div>
        ` : ''}

        ${esame.consigli ? `
        <div class="section">
          <h3>Consigli</h3>
          <p>${esame.consigli}</p>
        </div>
        ` : ''}

        <div class="footer">
          <p>PRIME VISION - Centro di Eccellenza Oftalmologica</p>
          <p>Documento generato il ${new Date().toLocaleString('it-IT')}</p>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // Wait for content to load then print
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
    };
  };

  // Retinography default text
  const retinografiaDefaultText = "Disco ottico roseo e a margini netti, macula e media periferia nella norma, vasi regolari per calibro e decorso, retina ovunque sul piano.";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F8F7F4' }}>
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const isDoctor = currentUser?.role === 'doctor' || currentUser?.role === 'admin';
  
  // Computed esameId for VisitRecorder component
  const esameId = lastSavedEsameId || storicoVisite[0]?.id || null;

  return (
    <div className="min-h-screen" style={{ background: '#F8F7F4' }}>
      <Toaster position="top-right" />
      
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold" style={{ color: '#2A9FCB' }}>Cartella Clinica</h1>
              {patient && (
                <p className="text-sm text-[#2A9FCB]/80">
                  {patient.first_name} {patient.last_name} • 
                  <span className="ml-1">Gruppo: {patientRecord?.blood_type || 'N/D'}</span>
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Indicator status bozza autosalvata */}
            {isDoctor && (
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full text-xs" 
                style={{
                  background: draftStatus.state === 'saving' ? '#FEF3C7' : draftStatus.state === 'saved' ? '#D1FAE5' : '#F3F4F6',
                  color: draftStatus.state === 'saving' ? '#92400E' : draftStatus.state === 'saved' ? '#065F46' : '#6B7280',
                }}
                data-testid="draft-status-indicator"
                title={draftStatus.serverSync ? 'Sincronizzato con cloud' : 'Salvato solo localmente'}
              >
                {draftStatus.state === 'saving' && (<><Loader2 className="w-3 h-3 animate-spin" /> Salvataggio…</>)}
                {draftStatus.state === 'saved' && draftStatus.lastSavedAt && (
                  <>
                    <CheckCircle className="w-3 h-3" />
                    Bozza salvata {draftStatus.lastSavedAt.toLocaleTimeString('it-IT', {hour:'2-digit', minute:'2-digit'})}
                    {draftStatus.serverSync ? ' ☁️' : ''}
                  </>
                )}
                {draftStatus.state === 'idle' && (<>Pronto</>)}
              </div>
            )}
            {/* Payment Status Badge - mostrato se appointmentId presente e abbiamo dati */}
            {appointmentId && paymentSummary && (
              <PaymentStatusBadge
                proBono={paymentSummary.pro_bono}
                paymentStatus={paymentSummary.payment_status}
                amountToPay={paymentSummary.amount_to_pay}
                discountPercent={paymentSummary.manual_discount_percent}
              />
            )}
            <button
              onClick={printPrescription}
              className="px-4 py-2 rounded-lg border hover:bg-gray-50 flex items-center gap-2"
            >
              <Printer className="w-4 h-4" /> Stampa Cartella
            </button>
            {isDoctor && (
              <>
                <button
                  onClick={saveDraftNow}
                  className="px-4 py-2 rounded-lg border border-amber-500 text-amber-600 hover:bg-amber-50 flex items-center gap-2"
                  data-testid="save-draft-btn"
                >
                  <Save className="w-4 h-4" /> Salva Bozza
                </button>
                <button
                  onClick={() => setShowPaymentModal(true)}
                  className="px-4 py-2 rounded-lg border border-rose-300 text-rose-600 hover:bg-rose-50 flex items-center gap-2"
                  data-testid="open-payment-discount-btn"
                  title="Imposta sconto o Pro bono per la visita"
                >
                  <Percent className="w-4 h-4" /> Sconto / Pro bono
                </button>
                <button
                  onClick={saveEsame}
                  className="px-4 py-2 rounded-lg text-white flex items-center gap-2"
                  style={{ background: '#2A9FCB' }}
                >
                  <Check className="w-4 h-4" /> Salva Visita
                </button>
                <button
                  onClick={() => setShowConcludiModal(true)}
                  className="px-4 py-2 rounded-lg text-white flex items-center gap-2"
                  style={{ background: '#2A9FCB' }}
                >
                  <Send className="w-4 h-4" /> Concludi Visita
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Sconto / Pro bono Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="payment-discount-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 relative">
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Percent className="w-5 h-5 text-[#2A9FCB]" /> Sconto / Pro bono
            </h3>
            {!appointmentId ? (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                Per applicare uno sconto serve un appuntamento attivo (link cartella con ?appointment=...). Apri la cartella dall&apos;agenda o dal pannello sala d&apos;attesa.
                <div className="mt-3 text-right">
                  <button onClick={() => setShowPaymentModal(false)} className="px-3 py-1 bg-gray-200 rounded text-sm">Chiudi</button>
                </div>
              </div>
            ) : (
              <PaymentDiscountSelector
                target="appointment"
                targetId={appointmentId}
                baseAmount={paymentSummary?.base_amount ?? 0}
                initialDiscount={paymentSummary?.manual_discount_percent ?? 0}
                initialProBono={paymentSummary?.pro_bono ?? false}
                initialTiming={paymentSummary?.payment_timing ?? 'end'}
                onApplied={(data) => {
                  setPaymentSummary(prev => ({ ...(prev || {}), ...data }));
                  setShowPaymentModal(false);
                }}
                onClose={() => setShowPaymentModal(false)}
              />
            )}
          </div>
        </div>
      )}

      {/* Concludi Visita Modal */}
      {showConcludiModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-xl font-bold" style={{ color: '#2A9FCB' }}>Concludi Visita</h2>
                  <p className="text-sm text-gray-500">Compila il resoconto e invia al paziente</p>
                </div>
                <button onClick={() => setShowConcludiModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Diagnosi */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium">Diagnosi *</label>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => openImportModal('diagnosi', esame.patologia_oftalmica || '')}
                        className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-[#2A9FCB]/10 text-[#2A9FCB] hover:bg-[#2A9FCB]/20 transition-all"
                        data-testid="import-diagnosi-btn"
                      >
                        <BookOpen className="w-3.5 h-3.5" /> Importa Template
                      </button>
                      <VoiceRecorder 
                        fieldName="Diagnosi"
                        onTranscription={(text) => setConcludiData(prev => ({ 
                          ...prev, 
                          diagnosi: prev.diagnosi ? `${prev.diagnosi} ${text}` : text 
                        }))}
                      />
                    </div>
                  </div>
                  <textarea
                    value={concludiData.diagnosi}
                    onChange={(e) => setConcludiData(prev => ({ ...prev, diagnosi: e.target.value }))}
                    className="w-full p-3 border rounded-lg h-24"
                    placeholder="Inserisci la diagnosi o usa il microfono per dettare..."
                  />
                </div>

                {/* Terapia */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium">Terapia Prescritta *</label>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => openImportModal('terapia', concludiData.diagnosi || esame.patologia_oftalmica || '')}
                        className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-green-100 text-green-700 hover:bg-green-200 transition-all"
                        data-testid="import-terapia-btn"
                      >
                        <BookOpen className="w-3.5 h-3.5" /> Importa Template
                      </button>
                      <VoiceRecorder 
                        fieldName="Terapia"
                        onTranscription={(text) => setConcludiData(prev => ({ 
                          ...prev, 
                          terapia: prev.terapia ? `${prev.terapia} ${text}` : text 
                        }))}
                      />
                    </div>
                  </div>
                  <textarea
                    value={concludiData.terapia}
                    onChange={(e) => setConcludiData(prev => ({ ...prev, terapia: e.target.value }))}
                    className="w-full p-3 border rounded-lg h-24"
                    placeholder="Es: Tobradex collirio 1 goccia x 3 volte al giorno per 7 giorni..."
                  />
                </div>

                {/* Consigli */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium">Consigli</label>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => openImportModal('consigli', concludiData.diagnosi || '')}
                        className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-amber-100 text-amber-700 hover:bg-amber-200 transition-all"
                        data-testid="import-consigli-btn"
                      >
                        <BookOpen className="w-3.5 h-3.5" /> Importa Template
                      </button>
                      <VoiceRecorder 
                        fieldName="Consigli"
                        onTranscription={(text) => setConcludiData(prev => ({ 
                          ...prev, 
                          consigli: prev.consigli ? `${prev.consigli} ${text}` : text 
                        }))}
                      />
                    </div>
                  </div>
                  <textarea
                    value={concludiData.consigli}
                    onChange={(e) => setConcludiData(prev => ({ ...prev, consigli: e.target.value }))}
                    className="w-full p-3 border rounded-lg h-20"
                    placeholder="Eventuali consigli per il paziente..."
                  />
                </div>

                {/* Prossimo Controllo */}
                <div>
                  <label className="block text-sm font-medium mb-1">Prossimo Controllo</label>
                  <input
                    type="text"
                    value={concludiData.prossimo_controllo}
                    onChange={(e) => setConcludiData(prev => ({ ...prev, prossimo_controllo: e.target.value }))}
                    className="w-full p-3 border rounded-lg"
                    placeholder="Es: Tra 6 mesi, Tra 1 anno..."
                  />
                </div>

                {/* Piano di Cura AI */}
                {concludiData.piano_di_cura && (
                  <div className="border-2 border-sky-200 rounded-xl p-4 bg-gradient-to-br from-sky-50 to-blue-50" data-testid="piano-di-cura-section">
                    <h4 className="text-base font-semibold mb-3 flex items-center gap-2 text-sky-800">
                      <FileText className="w-5 h-5" /> Piano di Cura Personalizzato
                      <span className="ml-2 px-2 py-0.5 bg-sky-100 text-sky-600 text-xs rounded-full font-medium">Generato da IA</span>
                    </h4>
                    
                    {concludiData.piano_di_cura.obiettivi_terapeutici && (
                      <div className="mb-3">
                        <p className="text-xs font-semibold text-sky-700 uppercase tracking-wide mb-1">Obiettivi Terapeutici</p>
                        <p className="text-sm text-gray-700 bg-white p-2 rounded-lg">{concludiData.piano_di_cura.obiettivi_terapeutici}</p>
                      </div>
                    )}
                    
                    {concludiData.piano_di_cura.terapia_farmacologica?.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs font-semibold text-sky-700 uppercase tracking-wide mb-1">Terapia Farmacologica</p>
                        <div className="space-y-1">
                          {concludiData.piano_di_cura.terapia_farmacologica.map((f, i) => (
                            <div key={i} className="text-sm bg-white p-2 rounded-lg flex flex-wrap gap-2">
                              <span className="font-medium text-gray-800">{f.farmaco}</span>
                              {f.posologia && <span className="text-sky-600">| {f.posologia}</span>}
                              {f.durata && <span className="text-gray-500">| Durata: {f.durata}</span>}
                              {f.obiettivo && <span className="text-emerald-600 text-xs italic">({f.obiettivo})</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {concludiData.piano_di_cura.controlli_programmati?.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs font-semibold text-sky-700 uppercase tracking-wide mb-1">Controlli Programmati</p>
                        <div className="space-y-1">
                          {concludiData.piano_di_cura.controlli_programmati.map((c, i) => (
                            <div key={i} className="text-sm bg-white p-2 rounded-lg">
                              <span className="font-medium">{c.quando}</span>
                              {c.motivo && <span className="text-gray-600"> - {c.motivo}</span>}
                              {c.esami?.length > 0 && <span className="text-sky-600 text-xs ml-1">(Esami: {c.esami.join(', ')})</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {concludiData.piano_di_cura.modifiche_stile_vita?.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs font-semibold text-sky-700 uppercase tracking-wide mb-1">Stile di Vita</p>
                        <ul className="text-sm text-gray-700 bg-white p-2 rounded-lg space-y-0.5">
                          {concludiData.piano_di_cura.modifiche_stile_vita.map((m, i) => (
                            <li key={i} className="flex items-start gap-1"><span className="text-sky-500 mt-0.5">-</span> {m}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {concludiData.piano_di_cura.segnali_allarme?.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs font-semibold text-red-600 uppercase tracking-wide mb-1">Segnali di Allarme</p>
                        <div className="bg-red-50 border border-red-200 p-2 rounded-lg space-y-0.5">
                          {concludiData.piano_di_cura.segnali_allarme.map((s, i) => (
                            <p key={i} className="text-sm text-red-700 flex items-start gap-1"><span className="font-bold">!</span> {s}</p>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {concludiData.piano_di_cura.prevenzione?.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-1">Prevenzione</p>
                        <ul className="text-sm text-gray-700 bg-white p-2 rounded-lg space-y-0.5">
                          {concludiData.piano_di_cura.prevenzione.map((p, i) => (
                            <li key={i} className="flex items-start gap-1"><span className="text-emerald-500 mt-0.5">-</span> {p}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {concludiData.piano_di_cura.note_piano && (
                      <div>
                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Note Specifiche</p>
                        <p className="text-sm text-gray-700 bg-white p-2 rounded-lg italic">{concludiData.piano_di_cura.note_piano}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Tags del Paziente */}
                <div className="p-4 rounded-lg" style={{ background: '#F0FAF5' }}>
                  <label className="block text-sm font-medium mb-3 flex items-center gap-2">
                    <Tag className="w-4 h-4" /> Tags Diagnosi Paziente
                  </label>
                  
                  {/* Current Tags */}
                  <div className="mb-3">
                    <p className="text-xs text-gray-500 mb-2">Tags attuali:</p>
                    <div className="flex flex-wrap gap-2">
                      {patientTags.length === 0 && tagsToAdd.length === 0 ? (
                        <span className="text-sm text-gray-400">Nessun tag</span>
                      ) : (
                        <>
                          {patientTags.map((tag, i) => (
                            <span 
                              key={`existing-${i}`} 
                              className={`px-3 py-1 rounded-full text-sm flex items-center gap-1 ${
                                tagsToRemove.includes(tag) 
                                  ? 'bg-red-100 text-red-700 line-through' 
                                  : 'bg-emerald-100 text-emerald-700'
                              }`}
                            >
                              {tag}
                              {tagsToRemove.includes(tag) ? (
                                <button onClick={() => undoTagRemoval(tag)} className="ml-1 hover:text-emerald-900">
                                  ↩️
                                </button>
                              ) : (
                                <button onClick={() => removeTagFromList(tag, true)} className="ml-1 hover:text-red-500">
                                  <X className="w-3 h-3" />
                                </button>
                              )}
                            </span>
                          ))}
                          {tagsToAdd.map((tag, i) => (
                            <span key={`new-${i}`} className="px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-700 flex items-center gap-1">
                              {tag} <span className="text-xs">(nuovo)</span>
                              <button onClick={() => removeTagFromList(tag, false)} className="ml-1 hover:text-red-500">
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Add New Tag */}
                  <div className="flex gap-2 mb-3">
                    <input
                      type="text"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && newTag && addTagToList(newTag)}
                      className="flex-1 p-2 border rounded-lg text-sm"
                      placeholder="Aggiungi nuovo tag..."
                    />
                    <button
                      onClick={() => newTag && addTagToList(newTag)}
                      className="px-3 py-2 rounded-lg text-white text-sm"
                      style={{ background: '#2A9FCB' }}
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Common Tags */}
                  <div>
                    <p className="text-xs text-gray-500 mb-2">Tags comuni:</p>
                    <div className="flex flex-wrap gap-1">
                      {COMMON_TAGS.filter(t => !patientTags.includes(t) && !tagsToAdd.includes(t)).map((tag, i) => (
                        <button
                          key={i}
                          onClick={() => addTagToList(tag)}
                          className="px-2 py-1 rounded text-xs bg-gray-100 hover:bg-gray-200 transition-colors"
                        >
                          {tag}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Notification Options */}
                <div className="p-4 rounded-lg bg-gray-50">
                  <label className="block text-sm font-medium mb-3">Notifiche al Paziente</label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={concludiData.send_email}
                        onChange={(e) => setConcludiData(prev => ({ ...prev, send_email: e.target.checked }))}
                        className="w-4 h-4 rounded"
                      />
                      <Mail className="w-4 h-4 text-gray-500" />
                      <span className="text-sm">Invia Email</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={concludiData.send_sms}
                        onChange={(e) => setConcludiData(prev => ({ ...prev, send_sms: e.target.checked }))}
                        className="w-4 h-4 rounded"
                      />
                      <Phone className="w-4 h-4 text-gray-500" />
                      <span className="text-sm">Invia SMS</span>
                    </label>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">
                    Il paziente riceverà il resoconto con diagnosi e terapia
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 mt-6 pt-4 border-t">
                <button
                  onClick={() => setShowConcludiModal(false)}
                  className="flex-1 px-4 py-3 rounded-lg border hover:bg-gray-50"
                >
                  Annulla
                </button>
                <button
                  onClick={concludiVisita}
                  disabled={concludiLoading || !concludiData.diagnosi || !concludiData.terapia}
                  className="flex-1 px-4 py-3 rounded-lg text-white flex items-center justify-center gap-2 disabled:opacity-50"
                  style={{ background: '#D97706' }}
                >
                  {concludiLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      Concludi e Invia
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Firma Digitale OTP */}
      {showOtpModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold flex items-center gap-2" style={{ color: '#2A9FCB' }}>
                  <Shield className="w-6 h-6" /> Firma Digitale
                </h2>
                <button 
                  onClick={() => { setShowOtpModal(false); setOtpStep('request'); }}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Info Medico */}
              <div className="p-4 rounded-lg bg-gray-50 mb-4">
                <p className="text-sm text-gray-600">
                  <strong>Dr. {currentUser?.first_name} {currentUser?.last_name}</strong>
                </p>
                <p className="text-xs text-gray-500">
                  Ordine dei Medici: {currentUser?.medical_license || 'Non specificato'}
                </p>
                <p className="text-xs text-gray-500">
                  Tel: {currentUser?.phone?.substring(0, 4)}****
                </p>
              </div>

              {otpStep === 'request' && (
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-4">
                    Per concludere e firmare digitalmente la visita, è necessario verificare la tua identità con un codice OTP inviato al tuo telefono.
                  </p>
                  <button
                    onClick={requestSignatureOTP}
                    disabled={otpLoading}
                    className="w-full px-4 py-3 rounded-lg text-white flex items-center justify-center gap-2"
                    style={{ background: '#2A9FCB' }}
                    data-testid="request-otp-btn"
                  >
                    {otpLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Shield className="w-5 h-5" />
                        Invia OTP (SMS + Email)
                      </>
                    )}
                  </button>
                </div>
              )}

              {otpStep === 'verify' && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600 text-center">
                    Inserisci il codice a 6 cifre ricevuto via SMS e Email
                  </p>
                  <div className="flex justify-center">
                    <input
                      type="text"
                      maxLength={6}
                      value={concludiData.signature_otp}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setConcludiData(prev => ({ ...prev, signature_otp: value }));
                      }}
                      className="w-48 p-3 border-2 rounded-lg text-center text-2xl tracking-widest font-mono"
                      style={{ borderColor: '#2A9FCB' }}
                      placeholder="000000"
                      data-testid="otp-input"
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setOtpStep('request')}
                      className="flex-1 px-4 py-3 rounded-lg border hover:bg-gray-50"
                    >
                      Indietro
                    </button>
                    <button
                      onClick={verifySignatureOTP}
                      disabled={otpLoading || concludiData.signature_otp.length !== 6}
                      className="flex-1 px-4 py-3 rounded-lg text-white flex items-center justify-center gap-2 disabled:opacity-50"
                      style={{ background: '#2A9FCB' }}
                      data-testid="verify-otp-btn"
                    >
                      {otpLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <CheckCircle className="w-5 h-5" />
                          Verifica
                        </>
                      )}
                    </button>
                  </div>
                  <button
                    onClick={requestSignatureOTP}
                    disabled={otpLoading}
                    className="w-full text-sm text-gray-500 hover:text-gray-700"
                  >
                    Non hai ricevuto il codice? Invia di nuovo
                  </button>
                </div>
              )}

              {otpStep === 'done' && (
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <p className="text-lg font-semibold text-green-600">Firma Verificata!</p>
                  <p className="text-sm text-gray-600">
                    Puoi ora concludere la visita. Il referto sarà firmato digitalmente con i tuoi dati.
                  </p>
                  <button
                    onClick={() => {
                      setShowOtpModal(false);
                      concludiVisita();
                    }}
                    className="w-full px-4 py-3 rounded-lg text-white flex items-center justify-center gap-2"
                    style={{ background: '#D97706' }}
                    data-testid="complete-with-signature-btn"
                  >
                    <Shield className="w-5 h-5" />
                    Concludi con Firma Digitale
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Patient Info Card - Expanded */}
        <div className="bg-white rounded-xl p-4 mb-6 shadow-sm">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <div>
              <label className="text-xs text-[#2A9FCB]/70">Nome</label>
              <p className="font-medium">{patient?.first_name} {patient?.last_name}</p>
            </div>
            <div>
              <label className="text-xs text-[#2A9FCB]/70">Data Nascita</label>
              <p className="font-medium">{patient?.date_of_birth || patient?.birth_date || 'N/D'}</p>
            </div>
            <div>
              <label className="text-xs text-[#2A9FCB]/70">Codice Fiscale</label>
              <p className="font-medium text-sm">{patient?.codice_fiscale || 'N/D'}</p>
            </div>
            <div>
              <label className="text-xs text-[#2A9FCB]/70">Telefono</label>
              <p className="font-medium">{patient?.phone || 'N/D'}</p>
            </div>
            <div>
              <label className="text-xs text-[#2A9FCB]/70">Email</label>
              <p className="font-medium text-sm truncate">{patient?.email || 'N/D'}</p>
            </div>
            <div>
              <label className="text-xs text-[#2A9FCB]/70">Gruppo Sanguigno</label>
              <p className="font-medium">{patient?.blood_type || 'N/D'}</p>
            </div>
          </div>
          {/* Seconda riga - Indirizzo */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3 pt-3 border-t border-gray-100">
            <div>
              <label className="text-xs text-[#2A9FCB]/70">Indirizzo</label>
              <p className="font-medium text-sm">
                {patient?.address_street ? `${patient?.address_street} ${patient?.address_number || ''}` : 'N/D'}
              </p>
            </div>
            <div>
              <label className="text-xs text-[#2A9FCB]/70">Città</label>
              <p className="font-medium">{patient?.address_city || 'N/D'}</p>
            </div>
            <div>
              <label className="text-xs text-[#2A9FCB]/70">CAP</label>
              <p className="font-medium">{patient?.address_zip || 'N/D'}</p>
            </div>
            <div>
              <label className="text-xs text-[#2A9FCB]/70">Sesso</label>
              <p className="font-medium">{patient?.gender === 'M' ? 'Maschio' : patient?.gender === 'F' ? 'Femmina' : 'N/D'}</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'esame', label: 'Esame Clinico', icon: Eye },
            { id: 'prescrizioni', label: 'Prescrizioni', icon: Pill },
            { id: 'storico', label: 'Storico', icon: FileText }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'text-white'
                  : 'bg-white hover:bg-gray-50'
              }`}
              style={activeTab === tab.id ? { background: '#2A9FCB' } : {}}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Esame Clinico Tab */}
        {activeTab === 'esame' && (
          <div className="space-y-6">
            
            {/* REGISTRAZIONE VISITA - Nuovo componente */}
            {isDoctor && (
              <VisitRecorder
                patientId={patientId}
                visitId={esameId}
                currentEsame={esame}
                onTranscriptionComplete={(transcription, summary, diagnosiSuggerite) => {
                  toast.success('Trascrizione completata');
                }}
                onAutoFill={(autoFillData, diagnosiSuggerite) => {
                  // Auto-compila TUTTI i campi con i dati estratti dall'IA
                  setEsame(prev => {
                    const updates = {};
                    
                    // Campi base
                    if (autoFillData.motivo_visita) {
                      updates.motivo_visita = autoFillData.motivo_visita;
                      updates.motivo_visita_source = 'ia';
                    }
                    if (autoFillData.diagnosi) {
                      updates.diagnosi = autoFillData.diagnosi;
                      updates.diagnosi_source = 'ia';
                    }
                    if (autoFillData.diagnosi_secondarie) {
                      updates.diagnosi_secondarie = autoFillData.diagnosi_secondarie;
                    }
                    if (autoFillData.terapia) {
                      updates.terapia = autoFillData.terapia;
                      updates.terapia_source = 'ia';
                    }
                    if (autoFillData.terapia_colliri) {
                      updates.terapia_colliri = autoFillData.terapia_colliri;
                    }
                    // Farmaci prescritti come colliri/prescrizioni
                    if (autoFillData.prescrizioni_colliri && autoFillData.prescrizioni_colliri.length > 0) {
                      updates.prescrizioni_colliri = [
                        ...(prev.prescrizioni_colliri || []),
                        ...autoFillData.prescrizioni_colliri.map(f => ({
                          farmaco_id: f.nome_commerciale?.toLowerCase().replace(/\s/g, '_') || 'ia_' + Date.now(),
                          nome_commerciale: f.nome_commerciale || '',
                          principio_attivo: f.principio_attivo || '',
                          concentrazione: f.concentrazione || '',
                          forma_farmaceutica: f.forma_farmaceutica || 'Collirio',
                          posologia: f.posologia || '',
                          durata: f.durata || '',
                          occhio: f.occhio || 'OU',
                          istruzioni: f.istruzioni || '',
                          controindicazioni: f.controindicazioni || '',
                          categoria: f.categoria || 'altro',
                          source: 'ia'
                        }))
                      ];
                    }
                    // Patologie e allergie
                    if (autoFillData.patologie_generali) {
                      updates.patologie_generali = autoFillData.patologie_generali;
                      updates.patologie_generali_source = 'ia';
                    }
                    if (autoFillData.allergie) {
                      updates.allergie = autoFillData.allergie;
                      updates.allergie_source = 'ia';
                    }
                    // Farmaci sistemici in uso
                    if (autoFillData.farmaci_sistemici) {
                      updates.farmaci_sistemici = autoFillData.farmaci_sistemici;
                    }
                    if (autoFillData.consigli) {
                      updates.consigli = autoFillData.consigli;
                      updates.consigli_source = 'ia';
                    }
                    if (autoFillData.sintomi_riferiti) {
                      updates.sintomi_riferiti = autoFillData.sintomi_riferiti;
                      updates.sintomi_riferiti_source = 'ia';
                    }
                    if (autoFillData.prossimo_controllo) {
                      updates.prossimo_controllo = autoFillData.prossimo_controllo;
                    }
                    if (autoFillData.note_aggiuntive) {
                      updates.note_aggiuntive = autoFillData.note_aggiuntive;
                    }
                    if (autoFillData.campo_visivo) {
                      updates.campo_visivo = autoFillData.campo_visivo;
                    }
                    
                    // Occhio destro - TUTTI i campi
                    if (autoFillData.occhio_destro) {
                      const od = autoFillData.occhio_destro;
                      updates.occhio_destro = {
                        ...prev.occhio_destro,
                        ...(od.visus_naturale && { visus_naturale: od.visus_naturale, visus_naturale_source: 'ia' }),
                        ...(od.visus_corretto && { visus_corretto: od.visus_corretto, visus_corretto_source: 'ia' }),
                        ...(od.refrazione_sfera && { sfera: od.refrazione_sfera, sfera_source: 'ia' }),
                        ...(od.refrazione_cilindro && { cilindro: od.refrazione_cilindro, cilindro_source: 'ia' }),
                        ...(od.refrazione_asse && { asse: od.refrazione_asse, asse_source: 'ia' }),
                        ...(od.tonometria && { tonometria: od.tonometria, tonometria_source: 'ia' }),
                        ...(od.biomicroscopia && { biomicroscopia: od.biomicroscopia, biomicroscopia_source: 'ia' }),
                        ...(od.fundus && { fundus: od.fundus, fundus_source: 'ia' }),
                        ...(od.oct && { oct: od.oct, oct_source: 'ia' })
                      };
                    }
                    
                    // Occhio sinistro - TUTTI i campi
                    if (autoFillData.occhio_sinistro) {
                      const os = autoFillData.occhio_sinistro;
                      updates.occhio_sinistro = {
                        ...prev.occhio_sinistro,
                        ...(os.visus_naturale && { visus_naturale: os.visus_naturale, visus_naturale_source: 'ia' }),
                        ...(os.visus_corretto && { visus_corretto: os.visus_corretto, visus_corretto_source: 'ia' }),
                        ...(os.refrazione_sfera && { sfera: os.refrazione_sfera, sfera_source: 'ia' }),
                        ...(os.refrazione_cilindro && { cilindro: os.refrazione_cilindro, cilindro_source: 'ia' }),
                        ...(os.refrazione_asse && { asse: os.refrazione_asse, asse_source: 'ia' }),
                        ...(os.tonometria && { tonometria: os.tonometria, tonometria_source: 'ia' }),
                        ...(os.biomicroscopia && { biomicroscopia: os.biomicroscopia, biomicroscopia_source: 'ia' }),
                        ...(os.fundus && { fundus: os.fundus, fundus_source: 'ia' }),
                        ...(os.oct && { oct: os.oct, oct_source: 'ia' })
                      };
                    }
                    
                    // Pachimetria
                    if (autoFillData.pachimetria_od) {
                      updates.pachimetria_od = autoFillData.pachimetria_od;
                    }
                    if (autoFillData.pachimetria_os) {
                      updates.pachimetria_os = autoFillData.pachimetria_os;
                    }
                    
                    // Strabismo
                    if (autoFillData.strabismo) {
                      const strab = autoFillData.strabismo;
                      updates.strabismo = {
                        ...(prev.strabismo || {}),
                        presente: strab.presente,
                        tipo: strab.tipo || '',
                        angolo_deviazione: strab.angolo_deviazione || '',
                        cover_test_lontano: strab.cover_test_lontano || '',
                        cover_test_vicino: strab.cover_test_vicino || '',
                        test_worth: strab.test_worth || '',
                        stereopsi: strab.stereopsi || '',
                        source: 'ia'
                      };
                    }
                    
                    // Motilità oculare
                    if (autoFillData.motilita_oculare) {
                      const mot = autoFillData.motilita_oculare;
                      updates.motilita_oculare = {
                        ...(prev.motilita_oculare || {}),
                        versioni: mot.versioni || '',
                        duzioni: mot.duzioni || '',
                        nistagmo: mot.nistagmo || '',
                        saccadi: mot.saccadi || '',
                        pursuit: mot.pursuit || '',
                        source: 'ia'
                      };
                    }
                    
                    return { ...prev, ...updates };
                  });
                  
                  // Auto-fill also concludi data with piano di cura
                  if (autoFillData.consigli || autoFillData.prossimo_controllo || autoFillData.piano_di_cura) {
                    setConcludiData(prev => ({
                      ...prev,
                      ...(autoFillData.diagnosi && { diagnosi: autoFillData.diagnosi }),
                      ...(autoFillData.terapia && { terapia: autoFillData.terapia }),
                      ...(autoFillData.consigli && { consigli: autoFillData.consigli }),
                      ...(autoFillData.prossimo_controllo && { prossimo_controllo: autoFillData.prossimo_controllo }),
                      ...(autoFillData.piano_di_cura && { piano_di_cura: autoFillData.piano_di_cura })
                    }));
                  }
                  
                  // Se ci sono diagnosi suggerite, mostrale come notifica
                  if (diagnosiSuggerite && diagnosiSuggerite.length > 0) {
                    const topDiag = diagnosiSuggerite[0];
                    toast.info(`Diagnosi suggerita: ${topDiag.diagnosi} (${topDiag.probabilita}%)`);
                  }
                }}
              />
            )}

            {/* Anamnesi Pre-Compilata dal Chatbot */}
            {chatbotAnamnesisReports.length > 0 && (
              <div className="bg-gradient-to-r from-green-50 to-sky-50 rounded-xl p-6 shadow-sm border border-green-200">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold flex items-center gap-2" style={{ color: '#2A9FCB' }}>
                    <Brain className="w-5 h-5" /> Anamnesi Pre-Compilata dal Paziente
                    <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                      {chatbotAnamnesisReports.length} report
                    </span>
                  </h2>
                  <button
                    onClick={() => setShowAnamnesisReports(!showAnamnesisReports)}
                    className="text-sm px-3 py-1 rounded-lg"
                    style={{ background: '#2A9FCB', color: 'white' }}
                  >
                    {showAnamnesisReports ? 'Nascondi' : 'Visualizza Report'}
                  </button>
                </div>
                
                {showAnamnesisReports && (
                  <div className="space-y-4">
                    {chatbotAnamnesisReports.map((report, idx) => (
                      <div key={report.id || idx} className="bg-white rounded-lg p-4 border border-green-100">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="text-xs text-gray-500">
                              Compilato il {new Date(report.created_at).toLocaleDateString('it-IT', { 
                                day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                              })}
                              {report.collected_via === 'guided_form' && (
                                <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                                  Form Guidato
                                </span>
                              )}
                            </p>
                            {report.critical_tags && report.critical_tags.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {report.critical_tags.map((tag, i) => (
                                  <span key={i} className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full font-medium flex items-center gap-1">
                                    <AlertTriangle className="w-3 h-3" />
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Visualizzazione strutturata se ha structured_data */}
                        {report.structured_data ? (
                          <div className="space-y-3">
                            {/* Patologie Sistemiche Personali */}
                            {report.structured_data.general_anamnesis?.systemic_conditions?.length > 0 && (
                              <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                                <div className="flex items-center justify-between mb-2">
                                  <h4 className="text-sm font-semibold text-red-800 flex items-center gap-1">
                                    🏥 Patologie Sistemiche
                                  </h4>
                                  <button
                                    onClick={() => {
                                      const conditions = report.structured_data.general_anamnesis.systemic_conditions
                                        .map(id => {
                                          const labels = {
                                            'diabete_tipo1': 'Diabete Tipo 1', 'diabete_tipo2': 'Diabete Tipo 2',
                                            'ipertensione': 'Ipertensione', 'cardiopatia': 'Cardiopatia',
                                            'ictus': 'Ictus/TIA', 'artrite_reumatoide': 'Artrite reumatoide',
                                            'lupus': 'Lupus', 'sjogren': 'Sjögren', 'sclerosi_multipla': 'Sclerosi multipla',
                                            'malattie_tiroidee': 'Malattie tiroidee', 'hiv': 'HIV/AIDS',
                                            'epatite': 'Epatite B/C', 'neoplasie': 'Neoplasie'
                                          };
                                          return labels[id] || id;
                                        }).join(', ');
                                      setEsame(prev => ({
                                        ...prev,
                                        patologie_generali: prev.patologie_generali 
                                          ? prev.patologie_generali + ', ' + conditions
                                          : conditions
                                      }));
                                      toast.success('Patologie importate');
                                    }}
                                    className="text-xs px-2 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200"
                                  >
                                    + Importa
                                  </button>
                                </div>
                                <p className="text-sm text-red-700">
                                  {report.structured_data.general_anamnesis.systemic_conditions.map(id => {
                                    const labels = {
                                      'diabete_tipo1': 'Diabete Tipo 1', 'diabete_tipo2': 'Diabete Tipo 2',
                                      'ipertensione': 'Ipertensione', 'cardiopatia': 'Cardiopatia',
                                      'ictus': 'Ictus/TIA', 'artrite_reumatoide': 'Artrite reumatoide',
                                      'lupus': 'Lupus', 'sjogren': 'Sjögren', 'sclerosi_multipla': 'Sclerosi multipla',
                                      'malattie_tiroidee': 'Malattie tiroidee', 'hiv': 'HIV/AIDS',
                                      'epatite': 'Epatite B/C', 'neoplasie': 'Neoplasie'
                                    };
                                    return labels[id] || id;
                                  }).join(', ')}
                                </p>
                              </div>
                            )}
                            
                            {/* Farmaci Sistemici */}
                            {report.structured_data.general_anamnesis?.farmaci_sistemici && (
                              <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                                <div className="flex items-center justify-between mb-2">
                                  <h4 className="text-sm font-semibold text-blue-800 flex items-center gap-1">
                                    💊 Farmaci Sistemici
                                  </h4>
                                  <button
                                    onClick={() => {
                                      const farmaci = report.structured_data.general_anamnesis.farmaci_sistemici;
                                      setEsame(prev => ({
                                        ...prev,
                                        farmaci_sistemici: Array.isArray(prev.farmaci_sistemici)
                                          ? [...prev.farmaci_sistemici, farmaci]
                                          : prev.farmaci_sistemici ? prev.farmaci_sistemici + ', ' + farmaci : farmaci
                                      }));
                                      toast.success('Farmaci importati');
                                    }}
                                    className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200"
                                  >
                                    + Importa
                                  </button>
                                </div>
                                <p className="text-sm text-blue-700">{report.structured_data.general_anamnesis.farmaci_sistemici}</p>
                              </div>
                            )}
                            
                            {/* Terapia Oculare / Colliri */}
                            {report.structured_data.general_anamnesis?.farmaci_oculari && (
                              <div className="p-3 rounded-lg bg-sky-50 border border-sky-200">
                                <div className="flex items-center justify-between mb-2">
                                  <h4 className="text-sm font-semibold text-sky-800 flex items-center gap-1">
                                    👁️ Terapia Oculare
                                  </h4>
                                  <button
                                    onClick={() => {
                                      const colliri = report.structured_data.general_anamnesis.farmaci_oculari;
                                      setEsame(prev => ({
                                        ...prev,
                                        terapia_colliri: Array.isArray(prev.terapia_colliri)
                                          ? [...prev.terapia_colliri, colliri]
                                          : prev.terapia_colliri ? prev.terapia_colliri + ', ' + colliri : colliri
                                      }));
                                      toast.success('Terapia oculare importata');
                                    }}
                                    className="text-xs px-2 py-1 rounded bg-sky-100 text-sky-700 hover:bg-sky-200"
                                  >
                                    + Importa
                                  </button>
                                </div>
                                <p className="text-sm text-sky-700">{report.structured_data.general_anamnesis.farmaci_oculari}</p>
                              </div>
                            )}
                            
                            {/* Patologie Oculari */}
                            {report.structured_data.ocular_pathologies && Object.keys(report.structured_data.ocular_pathologies).some(k => 
                              report.structured_data.ocular_pathologies[k]?.od || report.structured_data.ocular_pathologies[k]?.os
                            ) && (
                              <div className="p-3 rounded-lg bg-purple-50 border border-purple-200">
                                <div className="flex items-center justify-between mb-2">
                                  <h4 className="text-sm font-semibold text-purple-800 flex items-center gap-1">
                                    👓 Patologie Oculari
                                  </h4>
                                  <button
                                    onClick={() => {
                                      const pathologies = Object.entries(report.structured_data.ocular_pathologies)
                                        .filter(([_, v]) => v?.od || v?.os)
                                        .map(([k, v]) => {
                                          const eyes = [];
                                          if (v.od) eyes.push('OD');
                                          if (v.os) eyes.push('OS');
                                          return `${k.replace(/_/g, ' ')} (${eyes.join('+')})`;
                                        }).join(', ');
                                      setEsame(prev => ({
                                        ...prev,
                                        patologia_oftalmica: prev.patologia_oftalmica 
                                          ? prev.patologia_oftalmica + ', ' + pathologies
                                          : pathologies
                                      }));
                                      toast.success('Patologie oculari importate');
                                    }}
                                    className="text-xs px-2 py-1 rounded bg-purple-100 text-purple-700 hover:bg-purple-200"
                                  >
                                    + Importa
                                  </button>
                                </div>
                                <p className="text-sm text-purple-700">
                                  {Object.entries(report.structured_data.ocular_pathologies)
                                    .filter(([_, v]) => v?.od || v?.os)
                                    .map(([k, v]) => {
                                      const eyes = [];
                                      if (v.od) eyes.push('OD');
                                      if (v.os) eyes.push('OS');
                                      return `${k.replace(/_/g, ' ')} (${eyes.join('+')})`;
                                    }).join(', ')}
                                </p>
                              </div>
                            )}
                            
                            {/* Allergie */}
                            {(report.structured_data.general_anamnesis?.allergie_farmaci || report.structured_data.general_anamnesis?.allergie_colliri) && (
                              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                                <div className="flex items-center justify-between mb-2">
                                  <h4 className="text-sm font-semibold text-amber-800 flex items-center gap-1">
                                    ⚠️ Allergie
                                  </h4>
                                  <button
                                    onClick={() => {
                                      const allergieFarmaci = report.structured_data.general_anamnesis.allergie_farmaci || '';
                                      const allergieColliri = report.structured_data.general_anamnesis.allergie_colliri || '';
                                      const allergieText = [
                                        allergieFarmaci ? `Farmaci: ${allergieFarmaci}` : '',
                                        allergieColliri ? `Colliri: ${allergieColliri}` : ''
                                      ].filter(Boolean).join('. ');
                                      setEsame(p => ({ ...p, allergie: allergieText }));
                                      toast.success('Allergie importate');
                                    }}
                                    className="text-xs px-2 py-1 rounded bg-amber-600 text-white hover:bg-amber-700 flex items-center gap-1"
                                    data-testid="import-allergie-btn"
                                  >
                                    <Plus className="w-3 h-3" /> Importa
                                  </button>
                                </div>
                                {report.structured_data.general_anamnesis.allergie_farmaci && (
                                  <p className="text-sm text-amber-700">Farmaci: {report.structured_data.general_anamnesis.allergie_farmaci}</p>
                                )}
                                {report.structured_data.general_anamnesis.allergie_colliri && (
                                  <p className="text-sm text-amber-700">Colliri: {report.structured_data.general_anamnesis.allergie_colliri}</p>
                                )}
                              </div>
                            )}
                            
                            {/* Sintomi Riferiti dal Paziente */}
                            {report.structured_data.symptoms && report.structured_data.symptoms.length > 0 && (
                              <div className="p-3 rounded-lg bg-orange-50 border-2 border-orange-400">
                                <div className="flex items-center justify-between mb-2">
                                  <h4 className="text-sm font-semibold text-orange-800 flex items-center gap-1">
                                    🩺 Sintomi Riferiti dal Paziente
                                  </h4>
                                  <button
                                    onClick={() => {
                                      const symptomLabels = {
                                        'visione_offuscata': 'Visione offuscata',
                                        'visione_doppia': 'Diplopia',
                                        'emeralopia': 'Emeralopia',
                                        'nictalopia': 'Nictalopia',
                                        'fotofobia': 'Fotofobia',
                                        'abbagliamento': 'Abbagliamento',
                                        'aloni': 'Aloni intorno alle luci',
                                        'mosche_volanti': 'Miodesopsie',
                                        'lampi_luce': 'Fotopsie',
                                        'macchia_centrale': 'Scotoma',
                                        'visione_tunnel': 'Visione a tunnel',
                                        'distorsione_immagini': 'Metamorfopsie',
                                        'difficolta_profondita': 'Difficoltà percezione profondità',
                                        'difficolta_gradini': 'Difficoltà scale/gradini',
                                        'difficolta_guida_notturna': 'Difficoltà guida notturna',
                                        'lacrimazione': 'Lacrimazione eccessiva',
                                        'secchezza': 'Secchezza oculare',
                                        'corpo_estraneo': 'Sensazione corpo estraneo',
                                        'bruciore': 'Bruciore',
                                        'prurito': 'Prurito',
                                        'dolore_oculare': 'Dolore oculare',
                                        'pesantezza_palpebre': 'Pesantezza palpebre',
                                        'arrossamento': 'Arrossamento',
                                        'gonfiore_palpebre': 'Gonfiore palpebre',
                                        'secrezioni': 'Secrezioni/croste',
                                        'affaticamento_visivo': 'Affaticamento visivo',
                                        'cefalea_visiva': 'Cefalea da sforzo visivo',
                                        'difficolta_lettura': 'Difficoltà lettura',
                                        'difficolta_computer': 'Difficoltà computer/smartphone'
                                      };
                                      const sintomiText = report.structured_data.symptoms
                                        .map(s => symptomLabels[s] || s)
                                        .join(', ');
                                      setEsame(prev => ({
                                        ...prev,
                                        sintomi_riferiti: prev.sintomi_riferiti 
                                          ? prev.sintomi_riferiti + ', ' + sintomiText
                                          : sintomiText
                                      }));
                                      toast.success('Sintomi importati');
                                    }}
                                    className="text-xs px-2 py-1 rounded bg-orange-100 text-orange-700 hover:bg-orange-200"
                                  >
                                    + Importa
                                  </button>
                                </div>
                                <div className="flex flex-wrap gap-1.5 mb-2">
                                  {report.structured_data.symptoms.map((symptomId, i) => {
                                    const symptomLabels = {
                                      'visione_offuscata': 'Visione offuscata',
                                      'visione_doppia': 'Diplopia',
                                      'emeralopia': 'Emeralopia',
                                      'nictalopia': 'Nictalopia',
                                      'fotofobia': 'Fotofobia',
                                      'abbagliamento': 'Abbagliamento',
                                      'aloni': 'Aloni',
                                      'mosche_volanti': 'Miodesopsie',
                                      'lampi_luce': 'Fotopsie',
                                      'macchia_centrale': 'Scotoma',
                                      'visione_tunnel': 'Visione a tunnel',
                                      'distorsione_immagini': 'Metamorfopsie',
                                      'difficolta_profondita': 'Difficoltà 3D',
                                      'difficolta_gradini': 'Difficoltà scale',
                                      'difficolta_guida_notturna': 'Guida notturna',
                                      'lacrimazione': 'Lacrimazione',
                                      'secchezza': 'Secchezza',
                                      'corpo_estraneo': 'Corpo estraneo',
                                      'bruciore': 'Bruciore',
                                      'prurito': 'Prurito',
                                      'dolore_oculare': 'Dolore',
                                      'pesantezza_palpebre': 'Palpebre pesanti',
                                      'arrossamento': 'Arrossamento',
                                      'gonfiore_palpebre': 'Gonfiore',
                                      'secrezioni': 'Secrezioni',
                                      'affaticamento_visivo': 'Affaticamento',
                                      'cefalea_visiva': 'Cefalea',
                                      'difficolta_lettura': 'Difficoltà lettura',
                                      'difficolta_computer': 'Difficoltà schermi'
                                    };
                                    return (
                                      <span key={i} className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">
                                        {symptomLabels[symptomId] || symptomId}
                                      </span>
                                    );
                                  })}
                                </div>
                                {report.structured_data.symptom_notes && (
                                  <p className="text-sm text-orange-700 italic border-t border-orange-200 pt-2 mt-2">
                                    &quot;{report.structured_data.symptom_notes}&quot;
                                  </p>
                                )}
                              </div>
                            )}
                            
                            {/* Il Paziente Aggiunge */}
                            {report.structured_data.general_anamnesis?.note_paziente && (
                              <div className="p-3 rounded-lg bg-yellow-50 border-2 border-yellow-400">
                                <h4 className="text-sm font-semibold text-yellow-800 flex items-center gap-1 mb-2">
                                  📝 Il Paziente Aggiunge
                                </h4>
                                <p className="text-sm text-yellow-800 whitespace-pre-wrap">
                                  {report.structured_data.general_anamnesis.note_paziente}
                                </p>
                              </div>
                            )}
                            
                            {/* Motivo Visita */}
                            {report.structured_data.general_anamnesis?.motivo_visita && (
                              <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                                <div className="flex items-center justify-between mb-1">
                                  <h4 className="text-sm font-semibold text-gray-700">Motivo della Visita</h4>
                                  <button
                                    onClick={() => {
                                      const motivoValue = report.structured_data.general_anamnesis.motivo_visita;
                                      console.log('Importing motivo_visita:', motivoValue);
                                      setEsame(prev => ({
                                        ...prev,
                                        motivo_visita: motivoValue,
                                        motivo_visita_source: 'patient',
                                        motivo_visita_dettaglio: ''
                                      }));
                                      toast.success(`Motivo della visita importato: "${motivoValue}"`);
                                    }}
                                    className="text-xs px-2 py-1 rounded bg-sky-100 text-sky-700 hover:bg-sky-200 font-medium"
                                    data-testid="import-motivo-visita-btn"
                                  >
                                    + Importa
                                  </button>
                                </div>
                                <p className="text-sm text-gray-600">{report.structured_data.general_anamnesis.motivo_visita}</p>
                              </div>
                            )}
                          </div>
                        ) : (
                          /* Fallback: mostra report testuale se non ha structured_data */
                          <div>
                            <div className="flex justify-end mb-2">
                              <button
                                onClick={() => {
                                  setEsame(prev => ({
                                    ...prev,
                                    patologie_generali: prev.patologie_generali 
                                      ? prev.patologie_generali + '\n\n--- Anamnesi ---\n' + report.report_text
                                      : report.report_text
                                  }));
                                  toast.success('Anamnesi importata nel campo "Patologie Generali"');
                                }}
                                className="text-xs px-2 py-1 rounded border flex items-center gap-1"
                                style={{ borderColor: '#2A9FCB', color: '#2A9FCB' }}
                              >
                                <Plus className="w-3 h-3" />
                                Importa tutto
                              </button>
                            </div>
                            <div className="text-sm text-gray-700 whitespace-pre-wrap max-h-60 overflow-y-auto bg-gray-50 p-3 rounded-lg">
                              {report.report_text}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {!showAnamnesisReports && chatbotAnamnesisReports.length > 0 && (
                  <p className="text-sm text-gray-600">
                    Il paziente ha compilato {chatbotAnamnesisReports.length} report di anamnesi. 
                    Clicca &quot;Visualizza Report&quot; per vedere i dettagli e importarli nella cartella.
                  </p>
                )}
              </div>
            )}

            {/* Anamnesi Riassuntiva */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: '#2A9FCB' }}>
                <FileText className="w-5 h-5" /> Anamnesi Riassuntiva
              </h2>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Patologie Generali</label>
                  <textarea
                    value={esame.patologie_generali}
                    onChange={(e) => setEsame(p => ({ ...p, patologie_generali: e.target.value }))}
                    className="w-full p-2 border rounded-lg h-20"
                    placeholder="Diabete, ipertensione..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Farmaci Sistemici</label>
                  <textarea
                    value={Array.isArray(esame.farmaci_sistemici) ? esame.farmaci_sistemici.join(', ') : esame.farmaci_sistemici}
                    onChange={(e) => setEsame(p => ({ ...p, farmaci_sistemici: e.target.value ? e.target.value.split(/,\s*/).filter(s => s.length > 0) : [] }))}
                    className="w-full p-2 border rounded-lg h-20"
                    placeholder="Metformina, Ramipril..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Patologia Oftalmica Nota</label>
                  <textarea
                    value={esame.patologia_oftalmica}
                    onChange={(e) => setEsame(p => ({ ...p, patologia_oftalmica: e.target.value }))}
                    className="w-full p-2 border rounded-lg h-20"
                    placeholder="Glaucoma, cataratta..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Terapia con Colliri</label>
                  <textarea
                    value={Array.isArray(esame.terapia_colliri) ? esame.terapia_colliri.join(', ') : esame.terapia_colliri}
                    onChange={(e) => setEsame(p => ({ ...p, terapia_colliri: e.target.value ? e.target.value.split(/,\s*/).filter(s => s.length > 0) : [] }))}
                    className="w-full p-2 border rounded-lg h-20"
                    placeholder="Timololo 0.5%..."
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1 text-amber-700">⚠️ Allergie (farmaci/colliri)</label>
                  <textarea
                    value={esame.allergie || ''}
                    onChange={(e) => setEsame(p => ({ ...p, allergie: e.target.value }))}
                    className="w-full p-2 border border-amber-300 rounded-lg h-16 bg-amber-50"
                    placeholder="Nessuna allergia nota / Allergia a penicillina, fluorochinoloni..."
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1">Interventi Chirurgici Oftalmici</label>
                  <div className="border rounded-lg p-3 space-y-2">
                    {/* Lista interventi esistenti */}
                    {(esame.interventi_oftalmici || []).map((intervento, idx) => (
                      <div key={idx} className="flex items-center gap-2 bg-gray-50 p-2 rounded">
                        <span className="flex-1 text-sm">
                          {typeof intervento === 'object' 
                            ? `${intervento.tipo} - ${intervento.lateralita} (${intervento.anno})`
                            : intervento}
                        </span>
                        <button
                          onClick={() => setEsame(p => ({
                            ...p,
                            interventi_oftalmici: p.interventi_oftalmici.filter((_, i) => i !== idx)
                          }))}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    {/* Form per aggiungere nuovo intervento */}
                    <div className="flex gap-2 items-end pt-2 border-t">
                      <div className="flex-1">
                        <label className="text-xs text-gray-500">Tipo intervento</label>
                        <input
                          id="nuovo-intervento-tipo"
                          type="text"
                          className="w-full p-1.5 border rounded text-sm"
                          placeholder="Cataratta, Cross-linking, LASIK..."
                        />
                      </div>
                      <div className="w-24">
                        <label className="text-xs text-gray-500">Lateralità</label>
                        <select id="nuovo-intervento-lat" className="w-full p-1.5 border rounded text-sm">
                          <option value="OD">OD</option>
                          <option value="OS">OS</option>
                          <option value="OU">OU</option>
                        </select>
                      </div>
                      <div className="w-20">
                        <label className="text-xs text-gray-500">Anno</label>
                        <input
                          id="nuovo-intervento-anno"
                          type="number"
                          min="1950"
                          max={new Date().getFullYear()}
                          className="w-full p-1.5 border rounded text-sm"
                          placeholder="2024"
                        />
                      </div>
                      <button
                        onClick={() => {
                          const tipo = document.getElementById('nuovo-intervento-tipo').value;
                          const lat = document.getElementById('nuovo-intervento-lat').value;
                          const anno = document.getElementById('nuovo-intervento-anno').value;
                          if (tipo && anno) {
                            setEsame(p => ({
                              ...p,
                              interventi_oftalmici: [...(p.interventi_oftalmici || []), { tipo, lateralita: lat, anno }]
                            }));
                            document.getElementById('nuovo-intervento-tipo').value = '';
                            document.getElementById('nuovo-intervento-anno').value = '';
                          }
                        }}
                        className="px-3 py-1.5 rounded text-sm text-white"
                        style={{ backgroundColor: '#2A9FCB' }}
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Storia Bendaggio (Anamnesi) */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1">Storia Bendaggio (Occlusione)</label>
                  <div className="flex items-center gap-4 p-3 border rounded-lg bg-gray-50">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={esame.storia_bendaggio?.eseguito || false}
                        onChange={(e) => setEsame(p => ({
                          ...p,
                          storia_bendaggio: { ...p.storia_bendaggio, eseguito: e.target.checked }
                        }))}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Ha bendato un occhio in passato?</span>
                    </label>
                    {esame.storia_bendaggio?.eseguito && (
                      <>
                        <select
                          value={esame.storia_bendaggio?.occhio || ''}
                          onChange={(e) => setEsame(p => ({
                            ...p,
                            storia_bendaggio: { ...p.storia_bendaggio, occhio: e.target.value }
                          }))}
                          className="p-1.5 border rounded text-sm"
                        >
                          <option value="">Quale occhio?</option>
                          <option value="OD">Occhio Destro (OD)</option>
                          <option value="OS">Occhio Sinistro (OS)</option>
                          <option value="alternato">Alternato</option>
                        </select>
                        <div className="flex items-center gap-1">
                          <span className="text-sm text-gray-600">A che età?</span>
                          <input
                            type="text"
                            value={esame.storia_bendaggio?.eta_inizio || ''}
                            onChange={(e) => setEsame(p => ({
                              ...p,
                              storia_bendaggio: { ...p.storia_bendaggio, eta_inizio: e.target.value }
                            }))}
                            className="w-24 p-1.5 border rounded text-sm"
                            placeholder="es. 3-5 anni"
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: '#EA580C' }}>🩺 Sintomi Riferiti dal Paziente</label>
                  <textarea
                    value={esame.sintomi_riferiti}
                    onChange={(e) => setEsame(p => ({ ...p, sintomi_riferiti: e.target.value, sintomi_riferiti_source: 'manual' }))}
                    className="w-full p-2 border rounded-lg h-20"
                    style={{ 
                      borderColor: esame.sintomi_riferiti ? '#EA580C' : undefined,
                      color: esame.sintomi_riferiti_source === 'ia' ? '#DC2626' : 
                             esame.sintomi_riferiti_source === 'patient' ? '#059669' : 'inherit'
                    }}
                    placeholder="Fotofobia, miodesopsie, secchezza..."
                  />
                  {esame.sintomi_riferiti_source && (
                    <p className="text-xs mt-1" style={{ 
                      color: esame.sintomi_riferiti_source === 'ia' ? '#DC2626' : 
                             esame.sintomi_riferiti_source === 'patient' ? '#059669' : '#6B7280' 
                    }}>
                      {esame.sintomi_riferiti_source === 'ia' ? '🔴 Trascritto da IA' : 
                       esame.sintomi_riferiti_source === 'patient' ? '🟢 Inserito dal paziente' : '⚫ Manuale'}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Motivo della Visita</label>
                  <select
                    key={`motivo-select-${esame.motivo_visita}`}
                    value={esame.motivo_visita}
                    onChange={(e) => setEsame(p => ({ ...p, motivo_visita: e.target.value, motivo_visita_source: 'manual' }))}
                    className="w-full p-2 border rounded-lg mb-2"
                    data-testid="motivo-visita-select"
                    style={{ 
                      color: esame.motivo_visita_source === 'ia' ? '#DC2626' : 
                             esame.motivo_visita_source === 'patient' ? '#059669' : 'inherit'
                    }}
                  >
                    <option value="">Seleziona motivo...</option>
                    {/* Opzione custom per valore importato - prima delle opzioni standard */}
                    {esame.motivo_visita && ![
                      '', 'Controllo periodico', 'Calo del visus da lontano', 'Calo del visus da vicino',
                      'Calo del visus rapidamente progressivo', 'Strabismo', 'Valutazione chirurgica per blefaroplastica',
                      'Arrossamento oculare', 'Dolore oculare', 'Secchezza oculare', 'Controllo post-operatorio',
                      'Prescrizione lenti', 'Screening glaucoma', 'Altro'
                    ].includes(esame.motivo_visita) && (
                      <option value={esame.motivo_visita} style={{ color: esame.motivo_visita_source === 'patient' ? '#059669' : '#DC2626', fontWeight: 'bold' }}>
                        ✓ {esame.motivo_visita} (importato)
                      </option>
                    )}
                    <option value="Controllo periodico">Controllo periodico</option>
                    <option value="Calo del visus da lontano">Calo del visus da lontano</option>
                    <option value="Calo del visus da vicino">Calo del visus da vicino</option>
                    <option value="Calo del visus rapidamente progressivo">Calo del visus rapidamente progressivo</option>
                    <option value="Strabismo">Strabismo</option>
                    <option value="Valutazione chirurgica per blefaroplastica">Valutazione chirurgica per blefaroplastica</option>
                    <option value="Arrossamento oculare">Arrossamento oculare</option>
                    <option value="Dolore oculare">Dolore oculare</option>
                    <option value="Secchezza oculare">Secchezza oculare</option>
                    <option value="Controllo post-operatorio">Controllo post-operatorio</option>
                    <option value="Prescrizione lenti">Prescrizione lenti</option>
                    <option value="Screening glaucoma">Screening glaucoma</option>
                    <option value="Altro">Altro (specificare sotto)</option>
                  </select>
                  {/* Campo testo per motivo personalizzato */}
                  <input
                    type="text"
                    value={esame.motivo_visita_dettaglio || ''}
                    onChange={(e) => setEsame(p => ({ ...p, motivo_visita_dettaglio: e.target.value }))}
                    className="w-full p-2 border rounded-lg"
                    placeholder="Dettagli aggiuntivi o motivo specifico..."
                  />
                </div>
              </div>
            </div>

            {/* Esame Occhi - Two Columns Side by Side */}
            <div className="grid md:grid-cols-2 gap-6 items-start" id="printable-eyes-section">
              {/* Occhio Destro */}
              <div className="bg-white rounded-xl p-6 shadow-sm h-fit">
                <h2 className="text-lg font-semibold mb-4 text-center" style={{ background: '#E8F5E9', padding: '8px', borderRadius: '8px' }}>
                  👁️ OCCHIO DESTRO (OD)
                </h2>

                {/* Visus */}
                <div className="mb-4">
                  <h3 className="font-medium mb-2">Visus</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70">Naturale</label>
                      <select
                        value={esame.occhio_destro.visus}
                        onChange={(e) => updateEye('occhio_destro', 'visus', e.target.value)}
                        className="w-full p-2 border rounded-lg"
                      >
                        <option value="">Seleziona</option>
                        {['10/10', '9/10', '8/10', '7/10', '6/10', '5/10', '4/10', '3/10', '2/10', '1/10', '1/20', '1/30', '1/60', '1/120', 'Moto Mano', 'Percezione Luce', 'Visus Spento'].map(v => (
                          <option key={v} value={v}>{v}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70">Con Correzione</label>
                      <select
                        value={esame.occhio_destro.visus_con_correzione}
                        onChange={(e) => updateEye('occhio_destro', 'visus_con_correzione', e.target.value)}
                        className="w-full p-2 border rounded-lg"
                      >
                        <option value="">Seleziona</option>
                        {['10/10', '9/10', '8/10', '7/10', '6/10', '5/10', '4/10', '3/10', '2/10', '1/10', '1/20', '1/30', '1/60', '1/120', 'Moto Mano', 'Percezione Luce', 'Visus Spento'].map(v => (
                          <option key={v} value={v}>{v}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Autoref SENZA cicloplegia */}
                <div className="mb-4">
                  <h3 className="font-medium mb-2">Autoref (senza cicloplegia)</h3>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70">Sfera</label>
                      <input
                        type="text"
                        value={esame.occhio_destro.autoref_sfera}
                        onChange={(e) => updateEye('occhio_destro', 'autoref_sfera', e.target.value)}
                        className="w-full p-2 border rounded-lg"
                        placeholder="+1.50"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70">Cilindro</label>
                      <input
                        type="text"
                        value={esame.occhio_destro.autoref_cilindro}
                        onChange={(e) => updateEye('occhio_destro', 'autoref_cilindro', e.target.value)}
                        className="w-full p-2 border rounded-lg"
                        placeholder="-0.75"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70">Asse</label>
                      <input
                        type="text"
                        value={esame.occhio_destro.autoref_asse}
                        onChange={(e) => updateEye('occhio_destro', 'autoref_asse', e.target.value)}
                        className="w-full p-2 border rounded-lg"
                        placeholder="180°"
                      />
                    </div>
                  </div>
                </div>

                {/* Cicloplegia x2 con Timer - OD */}
                <div className="mb-4 p-3 border-2 border-dashed rounded-lg" style={{ borderColor: esame.cicloplegia_x2_od?.attiva ? '#F59E0B' : '#E5E7EB' }}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Cicloplegia x2 OD
                    </h3>
                    {!esame.cicloplegia_x2_od?.attiva ? (
                      <button
                        onClick={startCicloplegiaTimerOD}
                        className="px-3 py-1 bg-amber-500 text-white rounded-lg text-sm hover:bg-amber-600 flex items-center gap-1"
                        data-testid="start-cicloplegia-od-btn"
                      >
                        <Play className="w-3 h-3" /> Avvia Timer (20 min)
                      </button>
                    ) : (
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-600">
                          Somministrata alle: <strong>{esame.cicloplegia_x2_od?.orario_somministrazione}</strong>
                        </span>
                        <span className={`text-xl font-mono font-bold ${cicloplegiaTimeRemainingOD <= 60 ? 'text-red-600 animate-pulse' : 'text-amber-600'}`}>
                          {formatTimerDisplay(cicloplegiaTimeRemainingOD)}
                        </span>
                        {esame.cicloplegia_x2_od?.timer_scaduto && (
                          <span className="px-2 py-1 bg-green-500 text-white rounded-full text-xs flex items-center gap-1">
                            <Check className="w-3 h-3" /> Pronto!
                          </span>
                        )}
                        <button
                          onClick={stopCicloplegiaTimerOD}
                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                          title="Annulla timer"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                  {esame.cicloplegia_x2_od?.attiva && !esame.cicloplegia_x2_od?.timer_scaduto && (
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="h-2 rounded-full transition-all duration-1000"
                        style={{ 
                          width: `${(1 - cicloplegiaTimeRemainingOD / (20 * 60)) * 100}%`,
                          backgroundColor: cicloplegiaTimeRemainingOD <= 60 ? '#EF4444' : '#F59E0B'
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Autoref CON cicloplegia */}
                <div className="mb-4">
                  <h3 className="font-medium mb-2 flex items-center gap-2">
                    Autoref (con cicloplegia)
                    <DropsTimer label="Ciclolux OD" patientName={patient?.first_name} onComplete={() => toast.info('Pronto per seconda goccia OD!')} />
                  </h3>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70">Sfera</label>
                      <input
                        type="text"
                        value={esame.occhio_destro.autoref_ciclo_sfera}
                        onChange={(e) => updateEye('occhio_destro', 'autoref_ciclo_sfera', e.target.value)}
                        className="w-full p-2 border rounded-lg"
                        placeholder="+1.50"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70">Cilindro</label>
                      <input
                        type="text"
                        value={esame.occhio_destro.autoref_ciclo_cilindro}
                        onChange={(e) => updateEye('occhio_destro', 'autoref_ciclo_cilindro', e.target.value)}
                        className="w-full p-2 border rounded-lg"
                        placeholder="-0.75"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70">Asse</label>
                      <input
                        type="text"
                        value={esame.occhio_destro.autoref_ciclo_asse}
                        onChange={(e) => updateEye('occhio_destro', 'autoref_ciclo_asse', e.target.value)}
                        className="w-full p-2 border rounded-lg"
                        placeholder="180°"
                      />
                    </div>
                  </div>
                </div>

                {/* Prescrizione Lenti */}
                <div className="mb-4">
                  <h3 className="font-medium mb-2">Prescrizione Lenti (per 10/10)</h3>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70">Sfera</label>
                      <input
                        type="text"
                        value={esame.occhio_destro.prescrizione_sfera}
                        onChange={(e) => updateEye('occhio_destro', 'prescrizione_sfera', e.target.value)}
                        className="w-full p-2 border rounded-lg"
                        placeholder="+1.75"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70">Cilindro</label>
                      <input
                        type="text"
                        value={esame.occhio_destro.prescrizione_cilindro}
                        onChange={(e) => updateEye('occhio_destro', 'prescrizione_cilindro', e.target.value)}
                        className="w-full p-2 border rounded-lg"
                        placeholder="-0.50"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70">Asse</label>
                      <input
                        type="text"
                        value={esame.occhio_destro.prescrizione_asse}
                        onChange={(e) => updateEye('occhio_destro', 'prescrizione_asse', e.target.value)}
                        className="w-full p-2 border rounded-lg"
                        placeholder="180°"
                      />
                    </div>
                  </div>
                </div>

                {/* Tonometria */}
                <div className="mb-4">
                  <h3 className="font-medium mb-2">Tonometria OD (mmHg)</h3>
                  <input
                    type="text"
                    value={esame.occhio_destro.tonometria}
                    onChange={(e) => updateEye('occhio_destro', 'tonometria', e.target.value)}
                    className="w-full p-2 border rounded-lg"
                    placeholder="16"
                  />
                </div>

                {/* Annessi */}
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Annessi</label>
                  <select
                    value={esame.occhio_destro.annessi}
                    onChange={(e) => updateEye('occhio_destro', 'annessi', e.target.value)}
                    className="w-full p-2 border rounded-lg"
                    data-testid="annessi-od-select"
                  >
                    <option value="Sani">Sani</option>
                    <option value="Dermatocalasi">Dermatocalasi</option>
                  </select>
                </div>

                {/* Segmento Anteriore */}
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Segmento Anteriore</label>
                  <select
                    value={esame.occhio_destro.mezzi_diottrici}
                    onChange={(e) => updateEye('occhio_destro', 'mezzi_diottrici', e.target.value)}
                    className="w-full p-2 border rounded-lg mb-2"
                  >
                    <option value="trasparenti">Mezzi diottrici trasparenti</option>
                    <option value="opacita_corticonucleare">Opacità corticonucleare</option>
                    <option value="opacita_secondaria">Opacità secondaria</option>
                    <option value="pseudofachia">Pseudofachia (IOL)</option>
                    <option value="afachia">Afachia</option>
                  </select>
                  
                  {/* Cornea colorabile Fluo */}
                  <div className="flex items-center gap-4 mb-2 p-2 bg-emerald-50 rounded-lg">
                    <span className="text-sm font-medium text-emerald-700">Cornea colorabile:</span>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="radio"
                        name="cornea_fluo_od"
                        value="fluo_positivo"
                        checked={esame.occhio_destro.cornea_fluo === 'fluo_positivo'}
                        onChange={(e) => updateEye('occhio_destro', 'cornea_fluo', e.target.value)}
                        className="text-emerald-600"
                      />
                      <span className="text-sm text-emerald-800 font-medium">Fluo +</span>
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="radio"
                        name="cornea_fluo_od"
                        value="fluo_negativo"
                        checked={esame.occhio_destro.cornea_fluo === 'fluo_negativo'}
                        onChange={(e) => updateEye('occhio_destro', 'cornea_fluo', e.target.value)}
                        className="text-emerald-600"
                      />
                      <span className="text-sm text-emerald-800 font-medium">Fluo -</span>
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="radio"
                        name="cornea_fluo_od"
                        value=""
                        checked={!esame.occhio_destro.cornea_fluo}
                        onChange={(e) => updateEye('occhio_destro', 'cornea_fluo', '')}
                        className="text-gray-400"
                      />
                      <span className="text-sm text-gray-500">N/D</span>
                    </label>
                  </div>
                  
                  <textarea
                    value={esame.occhio_destro.segmento_anteriore}
                    onChange={(e) => updateEye('occhio_destro', 'segmento_anteriore', e.target.value)}
                    className="w-full p-2 border rounded-lg h-16"
                    placeholder="Note aggiuntive..."
                  />
                </div>

                {/* Topografia - Screening Cheratocono */}
                <div className="mb-4">
                  <h4 className="text-md font-semibold mb-3" style={{color: '#2A9FCB'}}>Topografia</h4>
                  <label className="block text-sm font-medium mb-1">Topografia OD (Screening Cheratocono)</label>
                  <div 
                    className="border-2 border-dashed rounded-lg text-center mb-2 transition-colors hover:border-emerald-400 hover:bg-emerald-50/30"
                    style={{ height: '200px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '16px' }}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, 'occhio_destro', 'topografia_url')}
                  >
                    {esame.occhio_destro.topografia_url ? (
                      <div className="w-full h-full flex flex-col justify-between">
                        <div className="flex-1 flex items-center justify-center overflow-hidden">
                          <img src={esame.occhio_destro.topografia_url} alt="Topografia OD" className="max-h-24 object-contain rounded-lg" />
                        </div>
                        <div className="flex items-center justify-center gap-2 mt-2 pt-2 border-t">
                          <button
                            onClick={() => analyzeImageWithAI(esame.occhio_destro.topografia_url, 'topografia', 'OD')}
                            disabled={aiImageAnalysisLoading['topografia_OD']}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 bg-purple-500 text-white hover:bg-purple-600 disabled:opacity-50"
                            data-testid="genera-referto-topografia-od-btn"
                          >
                            {aiImageAnalysisLoading['topografia_OD'] ? (
                              <><Loader2 className="w-3 h-3 animate-spin" /> Analisi AI...</>
                            ) : (
                              <><Sparkles className="w-3 h-3" /> Genera Referto</>
                            )}
                          </button>
                          <button onClick={() => updateEye('occhio_destro', 'topografia_url', '')} className="px-2 py-1.5 text-red-500 hover:bg-red-50 rounded-lg text-xs">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full">
                        <input type="file" accept="image/*" ref={topografiaODRef} className="hidden" onChange={(e) => { if (e.target.files[0]) { const reader = new FileReader(); reader.onload = (ev) => updateEye('occhio_destro', 'topografia_url', ev.target.result); reader.readAsDataURL(e.target.files[0]); }}} />
                        <button onClick={() => topografiaODRef.current?.click()} className="px-4 py-2 rounded-lg border-2 border-emerald-500 text-emerald-600 hover:bg-emerald-50 flex items-center gap-2">
                          <Upload className="w-4 h-4" /> Carica Topografia
                        </button>
                        <p className="text-xs text-emerald-500/60 mt-2">oppure trascina qui</p>
                        <button className="mt-3 px-3 py-1 rounded-lg text-xs bg-gray-200 text-gray-400 flex items-center gap-1" disabled>
                          <Sparkles className="w-3 h-3" /> Genera Referto
                        </button>
                      </div>
                    )}
                  </div>
                  {aiImageAnalysisResults['topografia_OD'] && (
                    <div className="mb-2 p-2 bg-purple-50 rounded-lg border border-purple-200">
                      <p className="font-medium text-purple-800 text-xs">✨ Referto AI:</p>
                      <p className="text-purple-700 text-xs">{aiImageAnalysisResults['topografia_OD'].referto}</p>
                      {aiImageAnalysisResults['topografia_OD'].evoluzione && (
                        <p className="mt-1 text-xs font-medium" style={{ color: aiImageAnalysisResults['topografia_OD'].evoluzione === 'MIGLIORAMENTO' ? 'green' : aiImageAnalysisResults['topografia_OD'].evoluzione === 'PEGGIORAMENTO' ? 'red' : 'gray' }}>
                          📊 Evoluzione: {aiImageAnalysisResults['topografia_OD'].evoluzione}
                        </p>
                      )}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70">Screening Cheratocono</label>
                      <select
                        value={esame.occhio_destro.topografia_cheratocono}
                        onChange={(e) => updateEye('occhio_destro', 'topografia_cheratocono', e.target.value)}
                        className="w-full p-2 border rounded-lg"
                      >
                        <option value="negativo">Negativo</option>
                        <option value="sospetto">Sospetto</option>
                        <option value="positivo">Positivo</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70">K1 / K2</label>
                      <input
                        type="text"
                        value={esame.occhio_destro.topografia_k1_k2 || ''}
                        onChange={(e) => updateEye('occhio_destro', 'topografia_k1_k2', e.target.value)}
                        className="w-full p-2 border rounded-lg"
                        placeholder="K1: 43.5D / K2: 44.2D"
                      />
                    </div>
                  </div>
                  <div className="mt-2">
                    <label className="text-xs text-[#2A9FCB]/70">Referto Topografia</label>
                    <textarea
                      value={esame.occhio_destro.topografia_referto}
                      onChange={(e) => updateEye('occhio_destro', 'topografia_referto', e.target.value)}
                      className="w-full p-2 border rounded-lg h-16"
                      placeholder="Indici topografici regolari. Astigmatismo corneale: ___D. Pattern: simmetrico/asimmetrico. Note: ..."
                    />
                  </div>
                </div>

                {/* Fondo Oculare */}
                <div className="mb-4">
                  <h4 className="text-md font-semibold mb-3" style={{color: '#2A9FCB'}}>Fondo Oculare</h4>
                  
                  <label className="block text-sm font-medium mb-1">Retinografia OD</label>
                  <div 
                    className="border-2 border-dashed rounded-lg text-center transition-colors hover:border-emerald-400 hover:bg-emerald-50/30"
                    style={{ height: '200px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '16px' }}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, 'occhio_destro', 'retinografia_url')}
                  >
                    {esame.occhio_destro.retinografia_url ? (
                      <div className="w-full h-full flex flex-col justify-between">
                        <div className="flex-1 flex items-center justify-center overflow-hidden">
                          <img src={esame.occhio_destro.retinografia_url} alt="Retinografia OD" className="max-h-24 object-contain rounded-lg" />
                        </div>
                        <div className="flex items-center justify-center gap-2 mt-2 pt-2 border-t">
                          <button
                            onClick={() => analyzeImageWithAI(esame.occhio_destro.retinografia_url, 'retinografia', 'OD')}
                            disabled={aiImageAnalysisLoading['retinografia_OD']}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 bg-purple-500 text-white hover:bg-purple-600 disabled:opacity-50"
                            data-testid="genera-referto-retinografia-od-btn"
                          >
                            {aiImageAnalysisLoading['retinografia_OD'] ? (
                              <><Loader2 className="w-3 h-3 animate-spin" /> Analisi AI...</>
                            ) : (
                              <><Sparkles className="w-3 h-3" /> Genera Referto</>
                            )}
                          </button>
                          <button onClick={() => updateEye('occhio_destro', 'retinografia_url', '')} className="px-2 py-1.5 text-red-500 hover:bg-red-50 rounded-lg text-xs">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full">
                        <input
                          type="file"
                          accept="image/*"
                          ref={retinografiaODRef}
                          className="hidden"
                          onChange={(e) => e.target.files[0] && handleRetinographyUpload('occhio_destro', e.target.files[0])}
                        />
                        <button
                          onClick={() => retinografiaODRef.current?.click()}
                          className="px-4 py-2 rounded-lg border-2 border-emerald-500 text-emerald-600 hover:bg-emerald-50 flex items-center gap-2"
                        >
                          <Upload className="w-4 h-4" /> Carica Retinografia
                        </button>
                        <p className="text-xs text-emerald-500/60 mt-2">oppure trascina qui</p>
                        <button className="mt-3 px-3 py-1 rounded-lg text-xs bg-gray-200 text-gray-400 flex items-center gap-1" disabled>
                          <Sparkles className="w-3 h-3" /> Genera Referto
                        </button>
                      </div>
                    )}
                  </div>
                  <AIResultsDisplay resultKey="retinografia_OD" />
                  <textarea
                    value={esame.occhio_destro.retinografia_referto}
                    onChange={(e) => updateEye('occhio_destro', 'retinografia_referto', e.target.value)}
                    className="w-full p-2 border rounded-lg h-20 mt-2"
                    placeholder={retinografiaDefaultText}
                  />
                  <button
                    onClick={() => updateEye('occhio_destro', 'retinografia_referto', retinografiaDefaultText)}
                    className="text-xs text-emerald-600 hover:underline mt-1"
                  >
                    Usa testo standard
                  </button>
                </div>

                {/* OCT Macula OD */}
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1 text-orange-700">🟠 OCT Macula OD</label>
                  <div 
                    className="border-2 border-dashed border-orange-300 rounded-lg text-center mb-2 transition-colors hover:border-orange-400 hover:bg-orange-50/30"
                    style={{ height: '200px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '16px' }}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, 'occhio_destro', 'oct_macula_url')}
                  >
                    {esame.occhio_destro.oct_macula_url ? (
                      <div className="w-full h-full flex flex-col justify-between">
                        <div className="flex-1 flex items-center justify-center overflow-hidden">
                          <img src={esame.occhio_destro.oct_macula_url} alt="OCT Macula OD" className="max-h-24 object-contain rounded-lg" />
                        </div>
                        <div className="flex items-center justify-center gap-2 mt-2 pt-2 border-t">
                          <button
                            onClick={() => analyzeImageWithAI(esame.occhio_destro.oct_macula_url, 'oct_macula', 'OD')}
                            disabled={aiImageAnalysisLoading['oct_macula_OD']}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 bg-purple-500 text-white hover:bg-purple-600 disabled:opacity-50"
                            data-testid="genera-referto-oct-macula-od-btn"
                          >
                            {aiImageAnalysisLoading['oct_macula_OD'] ? (
                              <><Loader2 className="w-3 h-3 animate-spin" /> Analisi AI...</>
                            ) : (
                              <><Sparkles className="w-3 h-3" /> Genera Referto</>
                            )}
                          </button>
                          <button onClick={() => updateEye('occhio_destro', 'oct_macula_url', '')} className="px-2 py-1.5 text-red-500 hover:bg-red-50 rounded-lg text-xs">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        {aiImageAnalysisResults['oct_macula_OD'] && (
                          <div className="mt-2 p-2 bg-purple-50 rounded-lg text-left text-xs">
                            <p className="font-medium text-purple-800">Referto AI generato:</p>
                            <p className="text-purple-700">{aiImageAnalysisResults['oct_macula_OD'].referto}</p>
                            {aiImageAnalysisResults['oct_macula_OD'].evoluzione && (
                              <p className="mt-1 font-medium" style={{ color: aiImageAnalysisResults['oct_macula_OD'].evoluzione === 'MIGLIORAMENTO' ? 'green' : aiImageAnalysisResults['oct_macula_OD'].evoluzione === 'PEGGIORAMENTO' ? 'red' : 'gray' }}>
                                📊 Evoluzione: {aiImageAnalysisResults['oct_macula_OD'].evoluzione}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full">
                        <input
                          type="file"
                          accept="image/*"
                          ref={octMaculaODRef}
                          className="hidden"
                          onChange={(e) => {
                            if (e.target.files[0]) {
                              const reader = new FileReader();
                              reader.onload = (ev) => updateEye('occhio_destro', 'oct_macula_url', ev.target.result);
                              reader.readAsDataURL(e.target.files[0]);
                            }
                          }}
                        />
                        <button
                          onClick={() => octMaculaODRef.current?.click()}
                          className="px-4 py-2 rounded-lg border-2 border-orange-400 text-orange-600 hover:bg-orange-50 flex items-center gap-2"
                        >
                          <Upload className="w-4 h-4" /> Carica OCT Macula
                        </button>
                        <p className="text-xs text-orange-500/60 mt-2">oppure trascina qui</p>
                        <button className="mt-3 px-3 py-1 rounded-lg text-xs bg-gray-200 text-gray-400 flex items-center gap-1" disabled>
                          <Sparkles className="w-3 h-3" /> Genera Referto
                        </button>
                      </div>
                    )}
                  </div>
                  <AIResultsDisplay resultKey="oct_macula_OD" />
                  <textarea
                    value={esame.occhio_destro.oct_macula_referto || ''}
                    onChange={(e) => updateEye('occhio_destro', 'oct_macula_referto', e.target.value)}
                    className="w-full p-2 border rounded-lg h-20"
                    placeholder="Spessore maculare centrale: ___µm. Profilo foveale: normale/alterato. Strati retinici: conservati. Note: ..."
                  />
                </div>

                {/* OCT Nervo Ottico OD */}
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1 text-blue-700">🔵 OCT Nervo Ottico OD</label>
                  <div 
                    className="border-2 border-dashed border-blue-300 rounded-lg text-center mb-2 transition-colors hover:border-blue-400 hover:bg-blue-50/30"
                    style={{ height: '200px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '16px' }}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, 'occhio_destro', 'oct_nervo_url')}
                  >
                    {esame.occhio_destro.oct_nervo_url ? (
                      <div className="w-full h-full flex flex-col justify-between">
                        <div className="flex-1 flex items-center justify-center overflow-hidden">
                          <img src={esame.occhio_destro.oct_nervo_url} alt="OCT Nervo OD" className="max-h-24 object-contain rounded-lg" />
                        </div>
                        <div className="flex items-center justify-center gap-2 mt-2 pt-2 border-t">
                          <button
                            onClick={() => analyzeImageWithAI(esame.occhio_destro.oct_nervo_url, 'oct_nervo', 'OD')}
                            disabled={aiImageAnalysisLoading['oct_nervo_OD']}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 bg-purple-500 text-white hover:bg-purple-600 disabled:opacity-50"
                            data-testid="genera-referto-oct-nervo-od-btn"
                          >
                            {aiImageAnalysisLoading['oct_nervo_OD'] ? (
                              <><Loader2 className="w-3 h-3 animate-spin" /> Analisi AI...</>
                            ) : (
                              <><Sparkles className="w-3 h-3" /> Genera Referto</>
                            )}
                          </button>
                          <button onClick={() => updateEye('occhio_destro', 'oct_nervo_url', '')} className="px-2 py-1.5 text-red-500 hover:bg-red-50 rounded-lg text-xs">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full">
                        <input
                          type="file"
                          accept="image/*"
                          ref={octNervoODRef}
                          className="hidden"
                          onChange={(e) => {
                            if (e.target.files[0]) {
                              const reader = new FileReader();
                              reader.onload = (ev) => updateEye('occhio_destro', 'oct_nervo_url', ev.target.result);
                              reader.readAsDataURL(e.target.files[0]);
                            }
                          }}
                        />
                        <button
                          onClick={() => octNervoODRef.current?.click()}
                          className="px-4 py-2 rounded-lg border-2 border-blue-400 text-blue-600 hover:bg-blue-50 flex items-center gap-2"
                        >
                          <Upload className="w-4 h-4" /> Carica OCT Nervo Ottico
                        </button>
                        <p className="text-xs text-blue-500/60 mt-2">oppure trascina qui</p>
                        <button className="mt-3 px-3 py-1 rounded-lg text-xs bg-gray-200 text-gray-400 flex items-center gap-1" disabled>
                          <Sparkles className="w-3 h-3" /> Genera Referto
                        </button>
                      </div>
                    )}
                  </div>
                  <AIResultsDisplay resultKey="oct_nervo_OD" />
                  <textarea
                    value={esame.occhio_destro.oct_nervo_referto || ''}
                    onChange={(e) => updateEye('occhio_destro', 'oct_nervo_referto', e.target.value)}
                    className="w-full p-2 border rounded-lg h-20"
                    placeholder="RNFL medio: ___µm. Sup: ___ Nas: ___ Inf: ___ Temp: ___. C/D ratio: ___. Classificazione: nella norma/borderline/fuori norma."
                  />
                </div>

                {/* OCT Legacy - nascosto ma manteniamo i dati */}
                {esame.occhio_destro.oct_url && (
                  <div className="mb-4 p-3 bg-gray-100 rounded-lg">
                    <label className="block text-sm font-medium mb-1 text-gray-500">OCT (vecchio formato)</label>
                    <img src={esame.occhio_destro.oct_url} alt="OCT OD" className="max-h-32 mx-auto rounded-lg opacity-70" />
                    <p className="text-xs text-gray-400 text-center mt-2">Immagine caricata nel vecchio formato</p>
                  </div>
                )}

                {/* Conta Endoteliale OD - CV, CD, CT */}
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Conta Endoteliale OD</label>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70">CV (%)</label>
                      <input
                        type="text"
                        value={esame.occhio_destro.conta_cv}
                        onChange={(e) => updateEye('occhio_destro', 'conta_cv', e.target.value)}
                        className="w-full p-2 border rounded-lg"
                        placeholder="35"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70">CD (cell/mm²)</label>
                      <input
                        type="text"
                        value={esame.occhio_destro.conta_cd}
                        onChange={(e) => updateEye('occhio_destro', 'conta_cd', e.target.value)}
                        className="w-full p-2 border rounded-lg"
                        placeholder="2500"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70">CT (μm)</label>
                      <input
                        type="text"
                        value={esame.occhio_destro.conta_ct}
                        onChange={(e) => updateEye('occhio_destro', 'conta_ct', e.target.value)}
                        className="w-full p-2 border rounded-lg"
                        placeholder="540"
                      />
                    </div>
                  </div>
                  {/* Presenza di Gutte */}
                  <div className="mt-2">
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={esame.occhio_destro.gutte_presenti || false}
                        onChange={(e) => updateEye('occhio_destro', 'gutte_presenti', e.target.checked)}
                        className="w-4 h-4 border-2 border-gray-300 rounded"
                      />
                      <span className="text-sm">Presenza di Gutte</span>
                    </label>
                  </div>
                </div>

                {/* Campo Visivo Computerizzato OD */}
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Campo Visivo Computerizzato OD</label>
                  <input
                    type="file"
                    ref={campoVisivoODRef}
                    className="hidden"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'occhio_destro', 'campo_visivo_url')}
                  />
                  <div 
                    className="border-2 border-dashed rounded-lg text-center transition-colors hover:border-sky-400 hover:bg-sky-50/30"
                    style={{ height: '200px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '16px', borderColor: '#2A9FCB' }}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, 'occhio_destro', 'campo_visivo_url')}
                  >
                    {esame.occhio_destro.campo_visivo_url ? (
                      <div className="w-full h-full flex flex-col justify-between">
                        <div className="flex-1 flex items-center justify-center overflow-hidden">
                          <img src={esame.occhio_destro.campo_visivo_url} alt="Campo Visivo OD" className="max-h-24 object-contain rounded-lg" />
                        </div>
                        <div className="flex items-center justify-center gap-2 mt-2 pt-2 border-t">
                          <button
                            onClick={() => analyzeImageWithAI(esame.occhio_destro.campo_visivo_url, 'campo_visivo', 'OD')}
                            disabled={aiImageAnalysisLoading['campo_visivo_OD']}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 bg-purple-500 text-white hover:bg-purple-600 disabled:opacity-50"
                            data-testid="genera-referto-campo-visivo-od-btn"
                          >
                            {aiImageAnalysisLoading['campo_visivo_OD'] ? (
                              <><Loader2 className="w-3 h-3 animate-spin" /> Analisi AI...</>
                            ) : (
                              <><Sparkles className="w-3 h-3" /> Genera Referto</>
                            )}
                          </button>
                          <button onClick={() => updateEye('occhio_destro', 'campo_visivo_url', '')} className="px-2 py-1.5 text-red-500 hover:bg-red-50 rounded-lg text-xs">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full">
                        <button
                          onClick={() => campoVisivoODRef.current?.click()}
                          className="px-4 py-2 rounded-lg border-2 border-sky-500 text-sky-600 hover:bg-sky-50 flex items-center gap-2"
                        >
                          <Upload className="w-4 h-4" /> Carica Campo Visivo
                        </button>
                        <p className="text-xs text-sky-500/60 mt-2">oppure trascina qui</p>
                        <button className="mt-3 px-3 py-1 rounded-lg text-xs bg-gray-200 text-gray-400 flex items-center gap-1" disabled>
                          <Sparkles className="w-3 h-3" /> Genera Referto
                        </button>
                      </div>
                    )}
                  </div>
                  {aiImageAnalysisResults['campo_visivo_OD'] && (
                    <div className="mb-2 p-2 bg-purple-50 rounded-lg border border-purple-200">
                      <p className="font-medium text-purple-800 text-xs">✨ Referto AI:</p>
                      <p className="text-purple-700 text-xs">{aiImageAnalysisResults['campo_visivo_OD'].referto}</p>
                      {aiImageAnalysisResults['campo_visivo_OD'].evoluzione && (
                        <p className="mt-1 text-xs font-medium" style={{ color: aiImageAnalysisResults['campo_visivo_OD'].evoluzione === 'MIGLIORAMENTO' ? 'green' : aiImageAnalysisResults['campo_visivo_OD'].evoluzione === 'PEGGIORAMENTO' ? 'red' : 'gray' }}>
                          📊 Evoluzione: {aiImageAnalysisResults['campo_visivo_OD'].evoluzione}
                        </p>
                      )}
                    </div>
                  )}
                  <textarea
                    value={esame.occhio_destro.campo_visivo_referto}
                    onChange={(e) => updateEye('occhio_destro', 'campo_visivo_referto', e.target.value)}
                    className="w-full p-2 border rounded-lg h-16 mt-2"
                    placeholder="MD: -2.5 dB, PSD: 2.1 dB. Scotoma arcuato superiore..."
                  />
                </div>
              </div>

              {/* Occhio Sinistro */}
              <div className="bg-white rounded-xl p-6 shadow-sm h-fit">
                <h2 className="text-lg font-semibold mb-4 text-center" style={{ background: '#FFF3E0', padding: '8px', borderRadius: '8px' }}>
                  👁️ OCCHIO SINISTRO (OS)
                </h2>

                {/* Visus */}
                <div className="mb-4">
                  <h3 className="font-medium mb-2">Visus</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70">Naturale</label>
                      <select
                        value={esame.occhio_sinistro.visus}
                        onChange={(e) => updateEye('occhio_sinistro', 'visus', e.target.value)}
                        className="w-full p-2 border rounded-lg"
                      >
                        <option value="">Seleziona</option>
                        {['10/10', '9/10', '8/10', '7/10', '6/10', '5/10', '4/10', '3/10', '2/10', '1/10', '1/20', '1/30', '1/60', '1/120', 'Moto Mano', 'Percezione Luce', 'Visus Spento'].map(v => (
                          <option key={v} value={v}>{v}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70">Con Correzione</label>
                      <select
                        value={esame.occhio_sinistro.visus_con_correzione}
                        onChange={(e) => updateEye('occhio_sinistro', 'visus_con_correzione', e.target.value)}
                        className="w-full p-2 border rounded-lg"
                      >
                        <option value="">Seleziona</option>
                        {['10/10', '9/10', '8/10', '7/10', '6/10', '5/10', '4/10', '3/10', '2/10', '1/10', '1/20', '1/30', '1/60', '1/120', 'Moto Mano', 'Percezione Luce', 'Visus Spento'].map(v => (
                          <option key={v} value={v}>{v}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Autoref SENZA cicloplegia */}
                <div className="mb-4">
                  <h3 className="font-medium mb-2">Autoref (senza cicloplegia)</h3>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70">Sfera</label>
                      <input
                        type="text"
                        value={esame.occhio_sinistro.autoref_sfera}
                        onChange={(e) => updateEye('occhio_sinistro', 'autoref_sfera', e.target.value)}
                        className="w-full p-2 border rounded-lg"
                        placeholder="+1.50"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70">Cilindro</label>
                      <input
                        type="text"
                        value={esame.occhio_sinistro.autoref_cilindro}
                        onChange={(e) => updateEye('occhio_sinistro', 'autoref_cilindro', e.target.value)}
                        className="w-full p-2 border rounded-lg"
                        placeholder="-0.75"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70">Asse</label>
                      <input
                        type="text"
                        value={esame.occhio_sinistro.autoref_asse}
                        onChange={(e) => updateEye('occhio_sinistro', 'autoref_asse', e.target.value)}
                        className="w-full p-2 border rounded-lg"
                        placeholder="180°"
                      />
                    </div>
                  </div>
                </div>

                {/* Cicloplegia x2 con Timer - OS */}
                <div className="mb-4 p-3 border-2 border-dashed rounded-lg" style={{ borderColor: esame.cicloplegia_x2_os?.attiva ? '#F59E0B' : '#E5E7EB' }}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Cicloplegia x2 OS
                    </h3>
                    {!esame.cicloplegia_x2_os?.attiva ? (
                      <button
                        onClick={startCicloplegiaTimerOS}
                        className="px-3 py-1 bg-amber-500 text-white rounded-lg text-sm hover:bg-amber-600 flex items-center gap-1"
                        data-testid="start-cicloplegia-os-btn"
                      >
                        <Play className="w-3 h-3" /> Avvia Timer (20 min)
                      </button>
                    ) : (
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-600">
                          Somministrata alle: <strong>{esame.cicloplegia_x2_os?.orario_somministrazione}</strong>
                        </span>
                        <span className={`text-xl font-mono font-bold ${cicloplegiaTimeRemainingOS <= 60 ? 'text-red-600 animate-pulse' : 'text-amber-600'}`}>
                          {formatTimerDisplay(cicloplegiaTimeRemainingOS)}
                        </span>
                        {esame.cicloplegia_x2_os?.timer_scaduto && (
                          <span className="px-2 py-1 bg-green-500 text-white rounded-full text-xs flex items-center gap-1">
                            <Check className="w-3 h-3" /> Pronto!
                          </span>
                        )}
                        <button
                          onClick={stopCicloplegiaTimerOS}
                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                          title="Annulla timer"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                  {esame.cicloplegia_x2_os?.attiva && !esame.cicloplegia_x2_os?.timer_scaduto && (
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="h-2 rounded-full transition-all duration-1000"
                        style={{ 
                          width: `${(1 - cicloplegiaTimeRemainingOS / (20 * 60)) * 100}%`,
                          backgroundColor: cicloplegiaTimeRemainingOS <= 60 ? '#EF4444' : '#F59E0B'
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Autoref CON cicloplegia */}
                <div className="mb-4">
                  <h3 className="font-medium mb-2 flex items-center gap-2">
                    Autoref (con cicloplegia)
                    <DropsTimer label="Ciclolux OS" patientName={patient?.first_name} onComplete={() => toast.info('Pronto per seconda goccia OS!')} />
                  </h3>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70">Sfera</label>
                      <input
                        type="text"
                        value={esame.occhio_sinistro.autoref_ciclo_sfera}
                        onChange={(e) => updateEye('occhio_sinistro', 'autoref_ciclo_sfera', e.target.value)}
                        className="w-full p-2 border rounded-lg"
                        placeholder="+1.50"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70">Cilindro</label>
                      <input
                        type="text"
                        value={esame.occhio_sinistro.autoref_ciclo_cilindro}
                        onChange={(e) => updateEye('occhio_sinistro', 'autoref_ciclo_cilindro', e.target.value)}
                        className="w-full p-2 border rounded-lg"
                        placeholder="-0.75"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70">Asse</label>
                      <input
                        type="text"
                        value={esame.occhio_sinistro.autoref_ciclo_asse}
                        onChange={(e) => updateEye('occhio_sinistro', 'autoref_ciclo_asse', e.target.value)}
                        className="w-full p-2 border rounded-lg"
                        placeholder="180°"
                      />
                    </div>
                  </div>
                </div>

                {/* Prescrizione Lenti */}
                <div className="mb-4">
                  <h3 className="font-medium mb-2">Prescrizione Lenti (per 10/10)</h3>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70">Sfera</label>
                      <input
                        type="text"
                        value={esame.occhio_sinistro.prescrizione_sfera}
                        onChange={(e) => updateEye('occhio_sinistro', 'prescrizione_sfera', e.target.value)}
                        className="w-full p-2 border rounded-lg"
                        placeholder="+1.75"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70">Cilindro</label>
                      <input
                        type="text"
                        value={esame.occhio_sinistro.prescrizione_cilindro}
                        onChange={(e) => updateEye('occhio_sinistro', 'prescrizione_cilindro', e.target.value)}
                        className="w-full p-2 border rounded-lg"
                        placeholder="-0.50"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70">Asse</label>
                      <input
                        type="text"
                        value={esame.occhio_sinistro.prescrizione_asse}
                        onChange={(e) => updateEye('occhio_sinistro', 'prescrizione_asse', e.target.value)}
                        className="w-full p-2 border rounded-lg"
                        placeholder="180°"
                      />
                    </div>
                  </div>
                </div>

                {/* Tonometria */}
                <div className="mb-4">
                  <h3 className="font-medium mb-2">Tonometria OS (mmHg)</h3>
                  <input
                    type="text"
                    value={esame.occhio_sinistro.tonometria}
                    onChange={(e) => updateEye('occhio_sinistro', 'tonometria', e.target.value)}
                    className="w-full p-2 border rounded-lg"
                    placeholder="16"
                  />
                </div>

                {/* Annessi */}
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Annessi</label>
                  <select
                    value={esame.occhio_sinistro.annessi}
                    onChange={(e) => updateEye('occhio_sinistro', 'annessi', e.target.value)}
                    className="w-full p-2 border rounded-lg"
                    data-testid="annessi-os-select"
                  >
                    <option value="Sani">Sani</option>
                    <option value="Dermatocalasi">Dermatocalasi</option>
                  </select>
                </div>

                {/* Segmento Anteriore */}
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Segmento Anteriore</label>
                  <select
                    value={esame.occhio_sinistro.mezzi_diottrici}
                    onChange={(e) => updateEye('occhio_sinistro', 'mezzi_diottrici', e.target.value)}
                    className="w-full p-2 border rounded-lg mb-2"
                  >
                    <option value="trasparenti">Mezzi diottrici trasparenti</option>
                    <option value="opacita_corticonucleare">Opacità corticonucleare</option>
                    <option value="opacita_secondaria">Opacità secondaria</option>
                    <option value="pseudofachia">Pseudofachia (IOL)</option>
                    <option value="afachia">Afachia</option>
                  </select>
                  
                  {/* Cornea colorabile Fluo */}
                  <div className="flex items-center gap-4 mb-2 p-2 bg-blue-50 rounded-lg">
                    <span className="text-sm font-medium text-blue-700">Cornea colorabile:</span>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="radio"
                        name="cornea_fluo_os"
                        value="fluo_positivo"
                        checked={esame.occhio_sinistro.cornea_fluo === 'fluo_positivo'}
                        onChange={(e) => updateEye('occhio_sinistro', 'cornea_fluo', e.target.value)}
                        className="text-blue-600"
                      />
                      <span className="text-sm text-blue-800 font-medium">Fluo +</span>
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="radio"
                        name="cornea_fluo_os"
                        value="fluo_negativo"
                        checked={esame.occhio_sinistro.cornea_fluo === 'fluo_negativo'}
                        onChange={(e) => updateEye('occhio_sinistro', 'cornea_fluo', e.target.value)}
                        className="text-blue-600"
                      />
                      <span className="text-sm text-blue-800 font-medium">Fluo -</span>
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="radio"
                        name="cornea_fluo_os"
                        value=""
                        checked={!esame.occhio_sinistro.cornea_fluo}
                        onChange={(e) => updateEye('occhio_sinistro', 'cornea_fluo', '')}
                        className="text-gray-400"
                      />
                      <span className="text-sm text-gray-500">N/D</span>
                    </label>
                  </div>
                  
                  <textarea
                    value={esame.occhio_sinistro.segmento_anteriore}
                    onChange={(e) => updateEye('occhio_sinistro', 'segmento_anteriore', e.target.value)}
                    className="w-full p-2 border rounded-lg h-16"
                    placeholder="Note aggiuntive..."
                  />
                </div>

                {/* Topografia - Screening Cheratocono */}
                <div className="mb-4">
                  <h4 className="text-md font-semibold mb-3" style={{color: '#2A9FCB'}}>Topografia</h4>
                  <label className="block text-sm font-medium mb-1">Topografia OS (Screening Cheratocono)</label>
                  <div 
                    className="border-2 border-dashed rounded-lg text-center mb-2 transition-colors hover:border-emerald-400 hover:bg-emerald-50/30"
                    style={{ height: '200px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '16px', borderColor: '#2A9FCB' }}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, 'occhio_sinistro', 'topografia_url')}
                  >
                    {esame.occhio_sinistro.topografia_url ? (
                      <div className="w-full h-full flex flex-col justify-between">
                        <div className="flex-1 flex items-center justify-center overflow-hidden">
                          <img src={esame.occhio_sinistro.topografia_url} alt="Topografia OS" className="max-h-24 object-contain rounded-lg" />
                        </div>
                        <div className="flex items-center justify-center gap-2 mt-2 pt-2 border-t">
                          <button
                            onClick={() => analyzeImageWithAI(esame.occhio_sinistro.topografia_url, 'topografia', 'OS')}
                            disabled={aiImageAnalysisLoading['topografia_OS']}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 bg-purple-500 text-white hover:bg-purple-600 disabled:opacity-50"
                            data-testid="genera-referto-topografia-os-btn"
                          >
                            {aiImageAnalysisLoading['topografia_OS'] ? (
                              <><Loader2 className="w-3 h-3 animate-spin" /> Analisi AI...</>
                            ) : (
                              <><Sparkles className="w-3 h-3" /> Genera Referto</>
                            )}
                          </button>
                          <button onClick={() => updateEye('occhio_sinistro', 'topografia_url', '')} className="px-2 py-1.5 text-red-500 hover:bg-red-50 rounded-lg text-xs">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full">
                        <input
                          type="file"
                          accept="image/*"
                          ref={topografiaOSRef}
                          className="hidden"
                          onChange={(e) => {
                            if (e.target.files[0]) {
                              const reader = new FileReader();
                              reader.onload = (ev) => updateEye('occhio_sinistro', 'topografia_url', ev.target.result);
                              reader.readAsDataURL(e.target.files[0]);
                            }
                          }}
                        />
                        <button
                          onClick={() => topografiaOSRef.current?.click()}
                          className="px-4 py-2 rounded-lg border-2 border-emerald-500 text-emerald-600 hover:bg-emerald-50 flex items-center gap-2"
                        >
                          <Upload className="w-4 h-4" /> Carica Topografia
                        </button>
                        <p className="text-xs text-emerald-500/60 mt-2">oppure trascina qui</p>
                        <button className="mt-3 px-3 py-1 rounded-lg text-xs bg-gray-200 text-gray-400 flex items-center gap-1" disabled>
                          <Sparkles className="w-3 h-3" /> Genera Referto
                        </button>
                      </div>
                    )}
                  </div>
                  {aiImageAnalysisResults['topografia_OS'] && (
                    <div className="mb-2 p-2 bg-purple-50 rounded-lg border border-purple-200">
                      <p className="font-medium text-purple-800 text-xs">✨ Referto AI:</p>
                      <p className="text-purple-700 text-xs">{aiImageAnalysisResults['topografia_OS'].referto}</p>
                      {aiImageAnalysisResults['topografia_OS'].evoluzione && (
                        <p className="mt-1 text-xs font-medium" style={{ color: aiImageAnalysisResults['topografia_OS'].evoluzione === 'MIGLIORAMENTO' ? 'green' : aiImageAnalysisResults['topografia_OS'].evoluzione === 'PEGGIORAMENTO' ? 'red' : 'gray' }}>
                          📊 Evoluzione: {aiImageAnalysisResults['topografia_OS'].evoluzione}
                        </p>
                      )}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70">Screening Cheratocono</label>
                      <select
                        value={esame.occhio_sinistro.topografia_cheratocono}
                        onChange={(e) => updateEye('occhio_sinistro', 'topografia_cheratocono', e.target.value)}
                        className="w-full p-2 border rounded-lg"
                      >
                        <option value="negativo">Negativo</option>
                        <option value="sospetto">Sospetto</option>
                        <option value="positivo">Positivo</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70">K1 / K2</label>
                      <input
                        type="text"
                        value={esame.occhio_sinistro.topografia_k1_k2 || ''}
                        onChange={(e) => updateEye('occhio_sinistro', 'topografia_k1_k2', e.target.value)}
                        className="w-full p-2 border rounded-lg"
                        placeholder="K1: 43.5D / K2: 44.2D"
                      />
                    </div>
                  </div>
                  <div className="mt-2">
                    <label className="text-xs text-[#2A9FCB]/70">Referto Topografia</label>
                    <textarea
                      value={esame.occhio_sinistro.topografia_referto}
                      onChange={(e) => updateEye('occhio_sinistro', 'topografia_referto', e.target.value)}
                      className="w-full p-2 border rounded-lg h-16"
                      placeholder="Indici topografici regolari. Astigmatismo corneale: ___D. Pattern: simmetrico/asimmetrico. Note: ..."
                    />
                  </div>
                </div>

                {/* Fondo Oculare */}
                <div className="mb-4">
                  <h4 className="text-md font-semibold mb-3" style={{color: '#2A9FCB'}}>Fondo Oculare</h4>
                  <label className="block text-sm font-medium mb-1">Retinografia OS</label>
                  <div 
                    className="border-2 border-dashed rounded-lg text-center transition-colors hover:border-emerald-400 hover:bg-emerald-50/30"
                    style={{ height: '200px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '16px', borderColor: '#2A9FCB' }}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, 'occhio_sinistro', 'retinografia_url')}
                  >
                    {esame.occhio_sinistro.retinografia_url ? (
                      <div className="w-full h-full flex flex-col justify-between">
                        <div className="flex-1 flex items-center justify-center overflow-hidden">
                          <img src={esame.occhio_sinistro.retinografia_url} alt="Retinografia OS" className="max-h-24 object-contain rounded-lg" />
                        </div>
                        <div className="flex items-center justify-center gap-2 mt-2 pt-2 border-t">
                          <button
                            onClick={() => analyzeImageWithAI(esame.occhio_sinistro.retinografia_url, 'retinografia', 'OS')}
                            disabled={aiImageAnalysisLoading['retinografia_OS']}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 bg-purple-500 text-white hover:bg-purple-600 disabled:opacity-50"
                            data-testid="genera-referto-retinografia-os-btn"
                          >
                            {aiImageAnalysisLoading['retinografia_OS'] ? (
                              <><Loader2 className="w-3 h-3 animate-spin" /> Analisi AI...</>
                            ) : (
                              <><Sparkles className="w-3 h-3" /> Genera Referto</>
                            )}
                          </button>
                          <button onClick={() => updateEye('occhio_sinistro', 'retinografia_url', '')} className="px-2 py-1.5 text-red-500 hover:bg-red-50 rounded-lg text-xs">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full">
                        <input
                          type="file"
                          accept="image/*"
                          ref={retinografiaOSRef}
                          className="hidden"
                          onChange={(e) => e.target.files[0] && handleRetinographyUpload('occhio_sinistro', e.target.files[0])}
                        />
                        <button
                          onClick={() => retinografiaOSRef.current?.click()}
                          className="px-4 py-2 rounded-lg border-2 border-emerald-500 text-emerald-600 hover:bg-emerald-50 flex items-center gap-2"
                        >
                          <Upload className="w-4 h-4" /> Carica Retinografia
                        </button>
                        <p className="text-xs text-emerald-500/60 mt-2">oppure trascina qui</p>
                        <button className="mt-3 px-3 py-1 rounded-lg text-xs bg-gray-200 text-gray-400 flex items-center gap-1" disabled>
                          <Sparkles className="w-3 h-3" /> Genera Referto
                        </button>
                      </div>
                    )}
                  </div>
                  {aiImageAnalysisResults['retinografia_OS'] && (
                    <div className="mb-2 p-2 bg-purple-50 rounded-lg border border-purple-200">
                      <p className="font-medium text-purple-800 text-xs">✨ Referto AI:</p>
                      <p className="text-purple-700 text-xs">{aiImageAnalysisResults['retinografia_OS'].referto}</p>
                      {aiImageAnalysisResults['retinografia_OS'].evoluzione && (
                        <p className="mt-1 text-xs font-medium" style={{ color: aiImageAnalysisResults['retinografia_OS'].evoluzione === 'MIGLIORAMENTO' ? 'green' : aiImageAnalysisResults['retinografia_OS'].evoluzione === 'PEGGIORAMENTO' ? 'red' : 'gray' }}>
                          📊 Evoluzione: {aiImageAnalysisResults['retinografia_OS'].evoluzione}
                        </p>
                      )}
                    </div>
                  )}
                  <textarea
                    value={esame.occhio_sinistro.retinografia_referto}
                    onChange={(e) => updateEye('occhio_sinistro', 'retinografia_referto', e.target.value)}
                    className="w-full p-2 border rounded-lg h-20 mt-2"
                    placeholder={retinografiaDefaultText}
                  />
                  <button
                    onClick={() => updateEye('occhio_sinistro', 'retinografia_referto', retinografiaDefaultText)}
                    className="text-xs text-emerald-600 hover:underline mt-1"
                  >
                    Usa testo standard
                  </button>
                </div>

                {/* OCT Macula OS */}
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1 text-orange-700">🟠 OCT Macula OS</label>
                  <div 
                    className="border-2 border-dashed border-orange-300 rounded-lg text-center mb-2 transition-colors hover:border-orange-400 hover:bg-orange-50/30"
                    style={{ height: '200px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '16px' }}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, 'occhio_sinistro', 'oct_macula_url')}
                  >
                    {esame.occhio_sinistro.oct_macula_url ? (
                      <div className="w-full h-full flex flex-col justify-between">
                        <div className="flex-1 flex items-center justify-center overflow-hidden">
                          <img src={esame.occhio_sinistro.oct_macula_url} alt="OCT Macula OS" className="max-h-24 object-contain rounded-lg" />
                        </div>
                        <div className="flex items-center justify-center gap-2 mt-2 pt-2 border-t">
                          <button
                            onClick={() => analyzeImageWithAI(esame.occhio_sinistro.oct_macula_url, 'oct_macula', 'OS')}
                            disabled={aiImageAnalysisLoading['oct_macula_OS']}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 bg-purple-500 text-white hover:bg-purple-600 disabled:opacity-50"
                            data-testid="genera-referto-oct-macula-os-btn"
                          >
                            {aiImageAnalysisLoading['oct_macula_OS'] ? (
                              <><Loader2 className="w-3 h-3 animate-spin" /> Analisi AI...</>
                            ) : (
                              <><Sparkles className="w-3 h-3" /> Genera Referto</>
                            )}
                          </button>
                          <button onClick={() => updateEye('occhio_sinistro', 'oct_macula_url', '')} className="px-2 py-1.5 text-red-500 hover:bg-red-50 rounded-lg text-xs">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full">
                        <input
                          type="file"
                          accept="image/*"
                          ref={octMaculaOSRef}
                          className="hidden"
                          onChange={(e) => {
                            if (e.target.files[0]) {
                              const reader = new FileReader();
                              reader.onload = (ev) => updateEye('occhio_sinistro', 'oct_macula_url', ev.target.result);
                              reader.readAsDataURL(e.target.files[0]);
                            }
                          }}
                        />
                        <button
                          onClick={() => octMaculaOSRef.current?.click()}
                          className="px-4 py-2 rounded-lg border-2 border-orange-400 text-orange-600 hover:bg-orange-50 flex items-center gap-2"
                        >
                          <Upload className="w-4 h-4" /> Carica OCT Macula
                        </button>
                        <p className="text-xs text-orange-500/60 mt-2">oppure trascina qui</p>
                        <button className="mt-3 px-3 py-1 rounded-lg text-xs bg-gray-200 text-gray-400 flex items-center gap-1" disabled>
                          <Sparkles className="w-3 h-3" /> Genera Referto
                        </button>
                      </div>
                    )}
                  </div>
                  {aiImageAnalysisResults['oct_macula_OS'] && (
                    <div className="mb-2 p-2 bg-purple-50 rounded-lg border border-purple-200">
                      <p className="font-medium text-purple-800 text-xs">✨ Referto AI:</p>
                      <p className="text-purple-700 text-xs">{aiImageAnalysisResults['oct_macula_OS'].referto}</p>
                      {aiImageAnalysisResults['oct_macula_OS'].evoluzione && (
                        <p className="mt-1 text-xs font-medium" style={{ color: aiImageAnalysisResults['oct_macula_OS'].evoluzione === 'MIGLIORAMENTO' ? 'green' : aiImageAnalysisResults['oct_macula_OS'].evoluzione === 'PEGGIORAMENTO' ? 'red' : 'gray' }}>
                          📊 Evoluzione: {aiImageAnalysisResults['oct_macula_OS'].evoluzione}
                        </p>
                      )}
                    </div>
                  )}
                  <textarea
                    value={esame.occhio_sinistro.oct_macula_referto || ''}
                    onChange={(e) => updateEye('occhio_sinistro', 'oct_macula_referto', e.target.value)}
                    className="w-full p-2 border rounded-lg h-20"
                    placeholder="Spessore maculare centrale: ___µm. Profilo foveale: normale/alterato. Strati retinici: conservati. Note: ..."
                  />
                </div>

                {/* OCT Nervo Ottico OS */}
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1 text-blue-700">🔵 OCT Nervo Ottico OS</label>
                  <div 
                    className="border-2 border-dashed border-blue-300 rounded-lg text-center mb-2 transition-colors hover:border-blue-400 hover:bg-blue-50/30"
                    style={{ height: '200px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '16px' }}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, 'occhio_sinistro', 'oct_nervo_url')}
                  >
                    {esame.occhio_sinistro.oct_nervo_url ? (
                      <div className="w-full h-full flex flex-col justify-between">
                        <div className="flex-1 flex items-center justify-center overflow-hidden">
                          <img src={esame.occhio_sinistro.oct_nervo_url} alt="OCT Nervo OS" className="max-h-24 object-contain rounded-lg" />
                        </div>
                        <div className="flex items-center justify-center gap-2 mt-2 pt-2 border-t">
                          <button
                            onClick={() => analyzeImageWithAI(esame.occhio_sinistro.oct_nervo_url, 'oct_nervo', 'OS')}
                            disabled={aiImageAnalysisLoading['oct_nervo_OS']}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 bg-purple-500 text-white hover:bg-purple-600 disabled:opacity-50"
                            data-testid="genera-referto-oct-nervo-os-btn"
                          >
                            {aiImageAnalysisLoading['oct_nervo_OS'] ? (
                              <><Loader2 className="w-3 h-3 animate-spin" /> Analisi AI...</>
                            ) : (
                              <><Sparkles className="w-3 h-3" /> Genera Referto</>
                            )}
                          </button>
                          <button onClick={() => updateEye('occhio_sinistro', 'oct_nervo_url', '')} className="px-2 py-1.5 text-red-500 hover:bg-red-50 rounded-lg text-xs">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full">
                        <input
                          type="file"
                          accept="image/*"
                          ref={octNervoOSRef}
                          className="hidden"
                          onChange={(e) => {
                            if (e.target.files[0]) {
                              const reader = new FileReader();
                              reader.onload = (ev) => updateEye('occhio_sinistro', 'oct_nervo_url', ev.target.result);
                              reader.readAsDataURL(e.target.files[0]);
                            }
                          }}
                        />
                        <button
                          onClick={() => octNervoOSRef.current?.click()}
                          className="px-4 py-2 rounded-lg border-2 border-blue-400 text-blue-600 hover:bg-blue-50 flex items-center gap-2"
                        >
                          <Upload className="w-4 h-4" /> Carica OCT Nervo Ottico
                        </button>
                        <p className="text-xs text-blue-500/60 mt-2">oppure trascina qui</p>
                        <button className="mt-3 px-3 py-1 rounded-lg text-xs bg-gray-200 text-gray-400 flex items-center gap-1" disabled>
                          <Sparkles className="w-3 h-3" /> Genera Referto
                        </button>
                      </div>
                    )}
                  </div>
                  {aiImageAnalysisResults['oct_nervo_OS'] && (
                    <div className="mb-2 p-2 bg-purple-50 rounded-lg border border-purple-200">
                      <p className="font-medium text-purple-800 text-xs">✨ Referto AI:</p>
                      <p className="text-purple-700 text-xs">{aiImageAnalysisResults['oct_nervo_OS'].referto}</p>
                      {aiImageAnalysisResults['oct_nervo_OS'].evoluzione && (
                        <p className="mt-1 text-xs font-medium" style={{ color: aiImageAnalysisResults['oct_nervo_OS'].evoluzione === 'MIGLIORAMENTO' ? 'green' : aiImageAnalysisResults['oct_nervo_OS'].evoluzione === 'PEGGIORAMENTO' ? 'red' : 'gray' }}>
                          📊 Evoluzione: {aiImageAnalysisResults['oct_nervo_OS'].evoluzione}
                        </p>
                      )}
                    </div>
                  )}
                  <textarea
                    value={esame.occhio_sinistro.oct_nervo_referto || ''}
                    onChange={(e) => updateEye('occhio_sinistro', 'oct_nervo_referto', e.target.value)}
                    className="w-full p-2 border rounded-lg h-20"
                    placeholder="RNFL medio: ___µm. Sup: ___ Nas: ___ Inf: ___ Temp: ___. C/D ratio: ___. Classificazione: nella norma/borderline/fuori norma."
                  />
                </div>

                {/* OCT Legacy OS - nascosto ma manteniamo i dati */}
                {esame.occhio_sinistro.oct_url && (
                  <div className="mb-4 p-3 bg-gray-100 rounded-lg">
                    <label className="block text-sm font-medium mb-1 text-gray-500">OCT (vecchio formato)</label>
                    <img src={esame.occhio_sinistro.oct_url} alt="OCT OS" className="max-h-32 mx-auto rounded-lg opacity-70" />
                    <p className="text-xs text-gray-400 text-center mt-2">Immagine caricata nel vecchio formato</p>
                  </div>
                )}

                {/* Conta Endoteliale OS - CV, CD, CT */}
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Conta Endoteliale OS</label>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70">CV (%)</label>
                      <input
                        type="text"
                        value={esame.occhio_sinistro.conta_cv}
                        onChange={(e) => updateEye('occhio_sinistro', 'conta_cv', e.target.value)}
                        className="w-full p-2 border rounded-lg"
                        placeholder="35"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70">CD (cell/mm²)</label>
                      <input
                        type="text"
                        value={esame.occhio_sinistro.conta_cd}
                        onChange={(e) => updateEye('occhio_sinistro', 'conta_cd', e.target.value)}
                        className="w-full p-2 border rounded-lg"
                        placeholder="2500"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#2A9FCB]/70">CT (μm)</label>
                      <input
                        type="text"
                        value={esame.occhio_sinistro.conta_ct}
                        onChange={(e) => updateEye('occhio_sinistro', 'conta_ct', e.target.value)}
                        className="w-full p-2 border rounded-lg"
                        placeholder="540"
                      />
                    </div>
                  </div>
                  {/* Presenza di Gutte */}
                  <div className="mt-2">
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={esame.occhio_sinistro.gutte_presenti || false}
                        onChange={(e) => updateEye('occhio_sinistro', 'gutte_presenti', e.target.checked)}
                        className="w-4 h-4 border-2 border-gray-300 rounded"
                      />
                      <span className="text-sm">Presenza di Gutte</span>
                    </label>
                  </div>
                </div>

                {/* Campo Visivo Computerizzato OS */}
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Campo Visivo Computerizzato OS</label>
                  <input
                    type="file"
                    ref={campoVisivoOSRef}
                    className="hidden"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'occhio_sinistro', 'campo_visivo_url')}
                  />
                  <div 
                    className="border-2 border-dashed rounded-lg text-center transition-colors hover:border-sky-400 hover:bg-sky-50/30"
                    style={{ height: '200px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '16px', borderColor: '#2A9FCB' }}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, 'occhio_sinistro', 'campo_visivo_url')}
                  >
                    {esame.occhio_sinistro.campo_visivo_url ? (
                      <div className="w-full h-full flex flex-col justify-between">
                        <div className="flex-1 flex items-center justify-center overflow-hidden">
                          <img src={esame.occhio_sinistro.campo_visivo_url} alt="Campo Visivo OS" className="max-h-24 object-contain rounded-lg" />
                        </div>
                        <div className="flex items-center justify-center gap-2 mt-2 pt-2 border-t">
                          <button
                            onClick={() => analyzeImageWithAI(esame.occhio_sinistro.campo_visivo_url, 'campo_visivo', 'OS')}
                            disabled={aiImageAnalysisLoading['campo_visivo_OS']}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 bg-purple-500 text-white hover:bg-purple-600 disabled:opacity-50"
                            data-testid="genera-referto-campo-visivo-os-btn"
                          >
                            {aiImageAnalysisLoading['campo_visivo_OS'] ? (
                              <><Loader2 className="w-3 h-3 animate-spin" /> Analisi AI...</>
                            ) : (
                              <><Sparkles className="w-3 h-3" /> Genera Referto</>
                            )}
                          </button>
                          <button onClick={() => updateEye('occhio_sinistro', 'campo_visivo_url', '')} className="px-2 py-1.5 text-red-500 hover:bg-red-50 rounded-lg text-xs">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full">
                        <button
                          onClick={() => campoVisivoOSRef.current?.click()}
                          className="px-4 py-2 rounded-lg border-2 border-sky-500 text-sky-600 hover:bg-sky-50 flex items-center gap-2"
                        >
                          <Upload className="w-4 h-4" /> Carica Campo Visivo
                        </button>
                        <p className="text-xs text-sky-500/60 mt-2">oppure trascina qui</p>
                        <button className="mt-3 px-3 py-1 rounded-lg text-xs bg-gray-200 text-gray-400 flex items-center gap-1" disabled>
                          <Sparkles className="w-3 h-3" /> Genera Referto
                        </button>
                      </div>
                    )}
                  </div>
                  {aiImageAnalysisResults['campo_visivo_OS'] && (
                    <div className="mb-2 p-2 bg-purple-50 rounded-lg border border-purple-200">
                      <p className="font-medium text-purple-800 text-xs">✨ Referto AI:</p>
                      <p className="text-purple-700 text-xs">{aiImageAnalysisResults['campo_visivo_OS'].referto}</p>
                      {aiImageAnalysisResults['campo_visivo_OS'].evoluzione && (
                        <p className="mt-1 text-xs font-medium" style={{ color: aiImageAnalysisResults['campo_visivo_OS'].evoluzione === 'MIGLIORAMENTO' ? 'green' : aiImageAnalysisResults['campo_visivo_OS'].evoluzione === 'PEGGIORAMENTO' ? 'red' : 'gray' }}>
                          📊 Evoluzione: {aiImageAnalysisResults['campo_visivo_OS'].evoluzione}
                        </p>
                      )}
                    </div>
                  )}
                  <textarea
                    value={esame.occhio_sinistro.campo_visivo_referto}
                    onChange={(e) => updateEye('occhio_sinistro', 'campo_visivo_referto', e.target.value)}
                    className="w-full p-2 border rounded-lg h-16 mt-2"
                    placeholder="MD: -2.5 dB, PSD: 2.1 dB. Scotoma arcuato superiore..."
                  />
                </div>
              </div>
            </div>

            {/* Motilità Oculare Section */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: '#2A9FCB' }}>
                <Eye className="w-5 h-5" /> Motilità Oculare
              </h2>
              
              {/* Gaze9 - Immagine 9 posizioni di sguardo */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Foto Gaze9 (9 posizioni di sguardo)</label>
                <div 
                  className="border-2 border-dashed rounded-lg p-6 text-center transition-colors hover:border-emerald-400 hover:bg-emerald-50/30"
                  style={{ minHeight: '200px' }}
                  onDragOver={handleDragOver}
                  onDrop={(e) => {
                    e.preventDefault();
                    const file = e.dataTransfer.files[0];
                    if (file && file.type.startsWith('image/')) {
                      const reader = new FileReader();
                      reader.onload = (ev) => setEsame(p => ({ ...p, gaze9_image_url: ev.target.result }));
                      reader.readAsDataURL(file);
                    }
                  }}
                >
                  {esame.gaze9_image_url ? (
                    <div>
                      <img 
                        src={esame.gaze9_image_url} 
                        alt="Gaze9 - 9 posizioni di sguardo" 
                        className="max-h-64 mx-auto rounded-lg shadow-md"
                      />
                      <div className="flex items-center justify-center gap-3 mt-3">
                        <button
                          onClick={() => analyzeImageWithAI(esame.gaze9_image_url, 'gaze9', 'OU')}
                          disabled={aiImageAnalysisLoading['gaze9_OU']}
                          className="px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 bg-purple-500 text-white hover:bg-purple-600 disabled:opacity-50"
                          data-testid="genera-referto-gaze9-btn"
                        >
                          {aiImageAnalysisLoading['gaze9_OU'] ? (
                            <><Loader2 className="w-4 h-4 animate-spin" /> Analisi AI in corso...</>
                          ) : (
                            <><Sparkles className="w-4 h-4" /> Analizza Motilità con AI</>
                          )}
                        </button>
                        <button
                          onClick={() => setEsame(p => ({ ...p, gaze9_image_url: '' }))}
                          className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        ref={gaze9Ref}
                        className="hidden"
                        onChange={(e) => {
                          if (e.target.files[0]) {
                            const reader = new FileReader();
                            reader.onload = (ev) => setEsame(p => ({ ...p, gaze9_image_url: ev.target.result }));
                            reader.readAsDataURL(e.target.files[0]);
                          }
                        }}
                      />
                      <button
                        onClick={() => gaze9Ref.current?.click()}
                        className="px-6 py-3 rounded-lg border-2 hover:bg-gray-50 flex items-center gap-2 mx-auto"
                        style={{ borderColor: '#2A9FCB', color: '#2A9FCB' }}
                      >
                        <Camera className="w-5 h-5" /> Carica Foto Gaze9
                      </button>
                      <p className="text-sm text-gray-500 mt-2">Trascina qui la foto delle 9 posizioni di sguardo</p>
                      <button className="mt-3 px-3 py-1 rounded-lg text-xs bg-gray-200 text-gray-400 flex items-center gap-1 mx-auto" disabled>
                        <Sparkles className="w-3 h-3" /> Analizza Motilità con AI
                      </button>
                    </div>
                  )}
                </div>
                
                {/* Risultato AI Gaze9 */}
                {aiImageAnalysisResults['gaze9_OU'] && (
                  <div className="mt-3 p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <h4 className="font-semibold text-purple-800 flex items-center gap-2 mb-3">
                      <Sparkles className="w-4 h-4" /> Analisi AI Motilità Oculare
                    </h4>
                    
                    {/* Allineamento */}
                    {aiImageAnalysisResults['gaze9_OU'].allineamento_primario && (
                      <div className="mb-2">
                        <span className="text-xs font-medium text-purple-600">Allineamento: </span>
                        <span className="text-sm text-purple-800 font-semibold">
                          {aiImageAnalysisResults['gaze9_OU'].allineamento_primario}
                          {aiImageAnalysisResults['gaze9_OU'].angolo_deviazione && ` (${aiImageAnalysisResults['gaze9_OU'].angolo_deviazione})`}
                        </span>
                      </div>
                    )}
                    
                    {/* Pattern identificato */}
                    {aiImageAnalysisResults['gaze9_OU'].pattern_identificato && aiImageAnalysisResults['gaze9_OU'].pattern_identificato !== 'null' && (
                      <div className="mb-2 p-2 bg-amber-100 rounded">
                        <span className="text-xs font-medium text-amber-700">Pattern: </span>
                        <span className="text-sm text-amber-800 font-semibold">{aiImageAnalysisResults['gaze9_OU'].pattern_identificato}</span>
                      </div>
                    )}
                    
                    {/* Diagnosi */}
                    {aiImageAnalysisResults['gaze9_OU'].diagnosi_probabile && (
                      <div className="mb-2 p-2 bg-blue-100 rounded">
                        <span className="text-xs font-medium text-blue-700">Diagnosi probabile: </span>
                        <span className="text-sm text-blue-800">{aiImageAnalysisResults['gaze9_OU'].diagnosi_probabile}</span>
                      </div>
                    )}
                    
                    {/* Referto descrittivo */}
                    <div className="mt-2">
                      <p className="text-sm text-purple-700">{aiImageAnalysisResults['gaze9_OU'].referto}</p>
                    </div>
                    
                    {/* Raccomandazioni */}
                    {aiImageAnalysisResults['gaze9_OU'].raccomandazioni && (
                      <div className="mt-2 p-2 bg-green-50 rounded border border-green-200">
                        <span className="text-xs font-medium text-green-700">Raccomandazioni: </span>
                        <span className="text-sm text-green-800">{aiImageAnalysisResults['gaze9_OU'].raccomandazioni}</span>
                      </div>
                    )}
                    
                    {/* Pulsante per copiare nel referto */}
                    <button
                      onClick={() => {
                        const result = aiImageAnalysisResults['gaze9_OU'];
                        const noteText = `${result.referto || ''}\nDiagnosi: ${result.diagnosi_probabile || 'N/D'}`;
                        setEsame(p => ({ ...p, motilita_note: noteText }));
                        toast.success('Referto AI copiato nelle note motilità');
                      }}
                      className="mt-3 px-3 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700"
                    >
                      Copia nelle Note Motilità
                    </button>
                  </div>
                )}
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Allineamento */}
                <div>
                  <label className="block text-sm font-medium mb-1">Allineamento</label>
                  <select
                    value={esame.motilita_oculare.allineamento}
                    onChange={(e) => setEsame(p => ({ 
                      ...p, 
                      motilita_oculare: { ...p.motilita_oculare, allineamento: e.target.value }
                    }))}
                    className="w-full p-2 border rounded-lg"
                    data-testid="motilita-allineamento"
                  >
                    <option value="ortotropico">Ortotropico</option>
                    <option value="ET">Esotropia (ET)</option>
                    <option value="XT">Exotropia (XT)</option>
                    <option value="ipertropia">Ipertropia</option>
                    <option value="ipotropia">Ipotropia</option>
                  </select>
                </div>

                {/* Angolo per Lontano (PL) - con/senza correzione */}
                <div>
                  <label className="block text-sm font-medium mb-1">Angolo per Lontano (PL)</label>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-gray-500">Senza correzione</label>
                      <input
                        type="text"
                        value={esame.motilita_oculare.angolo_lontano_sc || esame.motilita_oculare.angolo_lontano || ''}
                        onChange={(e) => setEsame(p => ({ 
                          ...p, 
                          motilita_oculare: { ...p.motilita_oculare, angolo_lontano_sc: e.target.value }
                        }))}
                        className="w-full p-2 border rounded-lg text-sm"
                        placeholder="SC"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Con correzione</label>
                      <input
                        type="text"
                        value={esame.motilita_oculare.angolo_lontano_cc || ''}
                        onChange={(e) => setEsame(p => ({ 
                          ...p, 
                          motilita_oculare: { ...p.motilita_oculare, angolo_lontano_cc: e.target.value }
                        }))}
                        className="w-full p-2 border rounded-lg text-sm"
                        placeholder="CC"
                      />
                    </div>
                  </div>
                </div>

                {/* Angolo per Vicino (PV) - con/senza correzione */}
                <div>
                  <label className="block text-sm font-medium mb-1">Angolo per Vicino (PV)</label>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-gray-500">Senza correzione</label>
                      <input
                        type="text"
                        value={esame.motilita_oculare.angolo_vicino_sc || esame.motilita_oculare.angolo_vicino || ''}
                        onChange={(e) => setEsame(p => ({ 
                          ...p, 
                          motilita_oculare: { ...p.motilita_oculare, angolo_vicino_sc: e.target.value }
                        }))}
                        className="w-full p-2 border rounded-lg text-sm"
                        placeholder="SC"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Con correzione</label>
                      <input
                        type="text"
                        value={esame.motilita_oculare.angolo_vicino_cc || ''}
                        onChange={(e) => setEsame(p => ({ 
                          ...p, 
                          motilita_oculare: { ...p.motilita_oculare, angolo_vicino_cc: e.target.value }
                        }))}
                        className="w-full p-2 border rounded-lg text-sm"
                        placeholder="CC"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Test di Irvine */}
                <div>
                  <label className="block text-sm font-medium mb-1">Test di Irvine</label>
                  <input
                    type="text"
                    value={esame.motilita_oculare.test_irvine || ''}
                    onChange={(e) => setEsame(p => ({ 
                      ...p, 
                      motilita_oculare: { ...p.motilita_oculare, test_irvine: e.target.value }
                    }))}
                    className="w-full p-2 border rounded-lg"
                    placeholder="Risultato Test di Irvine"
                    data-testid="motilita-test-irvine"
                  />
                </div>
              </div>

              {/* Angolo nelle diverse posizioni di sguardo */}
              <div className="mt-4 p-4 rounded-lg" style={{ background: '#F0FAF5' }}>
                <h3 className="font-medium mb-3">Angolo di Deviazione nelle Posizioni di Sguardo</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <label className="text-xs text-[#2A9FCB]/70">Sguardo in Alto</label>
                    <input
                      type="text"
                      value={esame.motilita_oculare.angolo_sguardo_alto}
                      onChange={(e) => setEsame(p => ({ 
                        ...p, 
                        motilita_oculare: { ...p.motilita_oculare, angolo_sguardo_alto: e.target.value }
                      }))}
                      className="w-full p-2 border rounded-lg"
                      placeholder="DP"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-[#2A9FCB]/70">Sguardo in Basso</label>
                    <input
                      type="text"
                      value={esame.motilita_oculare.angolo_sguardo_basso}
                      onChange={(e) => setEsame(p => ({ 
                        ...p, 
                        motilita_oculare: { ...p.motilita_oculare, angolo_sguardo_basso: e.target.value }
                      }))}
                      className="w-full p-2 border rounded-lg"
                      placeholder="DP"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-[#2A9FCB]/70">Sguardo a Destra</label>
                    <input
                      type="text"
                      value={esame.motilita_oculare.angolo_sguardo_destra}
                      onChange={(e) => setEsame(p => ({ 
                        ...p, 
                        motilita_oculare: { ...p.motilita_oculare, angolo_sguardo_destra: e.target.value }
                      }))}
                      className="w-full p-2 border rounded-lg"
                      placeholder="DP"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-[#2A9FCB]/70">Sguardo a Sinistra</label>
                    <input
                      type="text"
                      value={esame.motilita_oculare.angolo_sguardo_sinistra}
                      onChange={(e) => setEsame(p => ({ 
                        ...p, 
                        motilita_oculare: { ...p.motilita_oculare, angolo_sguardo_sinistra: e.target.value }
                      }))}
                      className="w-full p-2 border rounded-lg"
                      placeholder="DP"
                    />
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                {/* Test Krimsky */}
                <div>
                  <label className="block text-sm font-medium mb-1">Test di Krimsky</label>
                  <input
                    type="text"
                    value={esame.motilita_oculare.krimsky}
                    onChange={(e) => setEsame(p => ({ 
                      ...p, 
                      motilita_oculare: { ...p.motilita_oculare, krimsky: e.target.value }
                    }))}
                    className="w-full p-2 border rounded-lg"
                    placeholder="Risultato Krimsky..."
                    data-testid="motilita-krimsky"
                  />
                </div>

                {/* Lang II Stereo Test */}
                <div>
                  <label className="block text-sm font-medium mb-1">Lang II Stereo Test</label>
                  <select
                    value={esame.motilita_oculare.stereo_lang_ii}
                    onChange={(e) => setEsame(p => ({ 
                      ...p, 
                      motilita_oculare: { ...p.motilita_oculare, stereo_lang_ii: e.target.value }
                    }))}
                    className="w-full p-2 border rounded-lg"
                    data-testid="motilita-lang"
                  >
                    <option value="">Seleziona...</option>
                    <option value="positivo">Positivo (stereopsi presente)</option>
                    <option value="negativo">Negativo (stereopsi assente)</option>
                    <option value="non_eseguibile">Non eseguibile</option>
                  </select>
                </div>

                {/* Stereopsi ad Angolo Corretto */}
                <div>
                  <label className="block text-sm font-medium mb-1">Stereopsi ad Angolo Corretto</label>
                  <select
                    value={esame.motilita_oculare.stereopsi_angolo_corretto}
                    onChange={(e) => setEsame(p => ({ 
                      ...p, 
                      motilita_oculare: { ...p.motilita_oculare, stereopsi_angolo_corretto: e.target.value }
                    }))}
                    className="w-full p-2 border rounded-lg"
                    data-testid="motilita-stereopsi-angolo"
                  >
                    <option value="">Seleziona...</option>
                    <option value="presente">Presente</option>
                    <option value="assente">Assente</option>
                    <option value="non_valutabile">Non valutabile</option>
                  </select>
                </div>

                {/* Luci di Worth */}
                <div>
                  <label className="block text-sm font-medium mb-1">Luci di Worth</label>
                  <select
                    value={esame.motilita_oculare.luci_worth}
                    onChange={(e) => setEsame(p => ({ 
                      ...p, 
                      motilita_oculare: { ...p.motilita_oculare, luci_worth: e.target.value }
                    }))}
                    className="w-full p-2 border rounded-lg"
                    data-testid="motilita-worth"
                  >
                    <option value="">Seleziona...</option>
                    <option value="fusione">Fusione (4 luci)</option>
                    <option value="diplopia">Diplopia (5 luci)</option>
                    <option value="soppressione_od">Soppressione OD (3 luci verdi)</option>
                    <option value="soppressione_os">Soppressione OS (2 luci rosse)</option>
                  </select>
                </div>

                {/* Head Tilt Test (Bielschowsky) */}
                <div>
                  <label className="block text-sm font-medium mb-1">Head Tilt Test (Bielschowsky)</label>
                  <input
                    type="text"
                    value={esame.motilita_oculare.head_tilt_test}
                    onChange={(e) => setEsame(p => ({ 
                      ...p, 
                      motilita_oculare: { ...p.motilita_oculare, head_tilt_test: e.target.value }
                    }))}
                    className="w-full p-2 border rounded-lg"
                    placeholder="es. Positivo inclinando a dx"
                    data-testid="motilita-headtilt"
                  />
                </div>

                {/* Test della Bambola (Doll Head) */}
                <div>
                  <label className="block text-sm font-medium mb-1">Test della Bambola (Doll Head)</label>
                  <input
                    type="text"
                    value={esame.motilita_oculare.test_bambola}
                    onChange={(e) => setEsame(p => ({ 
                      ...p, 
                      motilita_oculare: { ...p.motilita_oculare, test_bambola: e.target.value }
                    }))}
                    className="w-full p-2 border rounded-lg"
                    placeholder="Normale / Alterato..."
                    data-testid="motilita-bambola"
                  />
                </div>
              </div>

              {/* Posizioni di Sguardo - Tabella */}
              <div className="mt-6">
                <h3 className="font-medium mb-3">Versioni</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  {/* OD */}
                  <div className="p-4 rounded-lg" style={{ background: '#E8F5E9' }}>
                    <h4 className="font-semibold text-center mb-3">OD - Occhio Destro</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { key: 'adduzione_od', label: 'Adduzione' },
                        { key: 'abduzione_od', label: 'Abduzione' },
                        { key: 'elevazione_od', label: 'Elevazione' },
                        { key: 'depressione_od', label: 'Depressione' }
                      ].map(item => (
                        <div key={item.key}>
                          <label className="text-xs text-[#2A9FCB]/80">{item.label}</label>
                          <select
                            value={esame.motilita_oculare.posizioni_sguardo[item.key]}
                            onChange={(e) => setEsame(p => ({
                              ...p,
                              motilita_oculare: {
                                ...p.motilita_oculare,
                                posizioni_sguardo: {
                                  ...p.motilita_oculare.posizioni_sguardo,
                                  [item.key]: e.target.value
                                }
                              }
                            }))}
                            className="w-full p-2 border rounded-lg text-sm"
                          >
                            <option value="normale">Normale</option>
                            <option value="limitazione_lieve">Limitazione lieve (-1)</option>
                            <option value="limitazione_moderata">Limitazione moderata (-2)</option>
                            <option value="limitazione_severa">Limitazione severa (-3)</option>
                            <option value="assente">Assente (-4)</option>
                            <option value="iperazione">Iperazione (+)</option>
                          </select>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* OS */}
                  <div className="p-4 rounded-lg" style={{ background: '#FFF3E0' }}>
                    <h4 className="font-semibold text-center mb-3">OS - Occhio Sinistro</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { key: 'adduzione_os', label: 'Adduzione' },
                        { key: 'abduzione_os', label: 'Abduzione' },
                        { key: 'elevazione_os', label: 'Elevazione' },
                        { key: 'depressione_os', label: 'Depressione' }
                      ].map(item => (
                        <div key={item.key}>
                          <label className="text-xs text-[#2A9FCB]/80">{item.label}</label>
                          <select
                            value={esame.motilita_oculare.posizioni_sguardo[item.key]}
                            onChange={(e) => setEsame(p => ({
                              ...p,
                              motilita_oculare: {
                                ...p.motilita_oculare,
                                posizioni_sguardo: {
                                  ...p.motilita_oculare.posizioni_sguardo,
                                  [item.key]: e.target.value
                                }
                              }
                            }))}
                            className="w-full p-2 border rounded-lg text-sm"
                          >
                            <option value="normale">Normale</option>
                            <option value="limitazione_lieve">Limitazione lieve (-1)</option>
                            <option value="limitazione_moderata">Limitazione moderata (-2)</option>
                            <option value="limitazione_severa">Limitazione severa (-3)</option>
                            <option value="assente">Assente (-4)</option>
                            <option value="iperazione">Iperazione (+)</option>
                          </select>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Note Motilità Oculare Estrinseca */}
              <div className="mt-4">
                <label className="block text-sm font-medium mb-1">Note Motilità Oculare Estrinseca</label>
                <textarea
                  value={esame.motilita_oculare.note_motilita}
                  onChange={(e) => setEsame(p => ({ 
                    ...p, 
                    motilita_oculare: { ...p.motilita_oculare, note_motilita: e.target.value }
                  }))}
                  className="w-full p-2 border rounded-lg h-20"
                  placeholder="buone le lateroversioni"
                  data-testid="motilita-note"
                />
              </div>

              {/* Tabella 3x3 Strabismo - 9 Posizioni di Sguardo */}
              <div className="mt-6 p-4 rounded-xl" style={{ background: 'linear-gradient(160deg, #eaf5fc 0%, #f7fbfe 50%, #e1eef8 100%)' }}>
                <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ color: '#2A9FCB' }}>
                  <Eye className="w-4 h-4" /> Motilità Oculare Estrinseca - 9 Posizioni di Sguardo (DP)
                </h3>
                <p className="text-xs text-gray-500 mb-3">
                  Inserisci la deviazione in diottrie prismatiche (es: "S/D 35 DP con XT -10DP", "ET 15 DP")
                </p>
                <div className="grid grid-cols-3 gap-2 max-w-md mx-auto">
                  {/* Nota: Le posizioni sono dal punto di vista del paziente */}
                  {/* Riga superiore */}
                  <div className="text-center">
                    <label className="text-xs text-gray-500">↖ Alto DX</label>
                    <input
                      type="text"
                      value={esame.motilita_oculare.strabismo_9_posizioni?.alto_dx || ''}
                      onChange={(e) => setEsame(p => ({
                        ...p,
                        motilita_oculare: {
                          ...p.motilita_oculare,
                          strabismo_9_posizioni: {
                            ...p.motilita_oculare.strabismo_9_posizioni,
                            alto_dx: e.target.value
                          }
                        }
                      }))}
                      className="w-full p-2 border rounded-lg text-sm text-center"
                      placeholder="DP"
                      data-testid="strabismo-alto-dx"
                    />
                  </div>
                  <div className="text-center">
                    <label className="text-xs text-gray-500">↑ Alto Centro</label>
                    <input
                      type="text"
                      value={esame.motilita_oculare.strabismo_9_posizioni?.alto_centro || ''}
                      onChange={(e) => setEsame(p => ({
                        ...p,
                        motilita_oculare: {
                          ...p.motilita_oculare,
                          strabismo_9_posizioni: {
                            ...p.motilita_oculare.strabismo_9_posizioni,
                            alto_centro: e.target.value
                          }
                        }
                      }))}
                      className="w-full p-2 border rounded-lg text-sm text-center"
                      placeholder="DP"
                      data-testid="strabismo-alto-centro"
                    />
                  </div>
                  <div className="text-center">
                    <label className="text-xs text-gray-500">↗ Alto SX</label>
                    <input
                      type="text"
                      value={esame.motilita_oculare.strabismo_9_posizioni?.alto_sx || ''}
                      onChange={(e) => setEsame(p => ({
                        ...p,
                        motilita_oculare: {
                          ...p.motilita_oculare,
                          strabismo_9_posizioni: {
                            ...p.motilita_oculare.strabismo_9_posizioni,
                            alto_sx: e.target.value
                          }
                        }
                      }))}
                      className="w-full p-2 border rounded-lg text-sm text-center"
                      placeholder="DP"
                      data-testid="strabismo-alto-sx"
                    />
                  </div>
                  {/* Riga centrale */}
                  <div className="text-center">
                    <label className="text-xs text-gray-500">← Centro DX</label>
                    <input
                      type="text"
                      value={esame.motilita_oculare.strabismo_9_posizioni?.centro_dx || ''}
                      onChange={(e) => setEsame(p => ({
                        ...p,
                        motilita_oculare: {
                          ...p.motilita_oculare,
                          strabismo_9_posizioni: {
                            ...p.motilita_oculare.strabismo_9_posizioni,
                            centro_dx: e.target.value
                          }
                        }
                      }))}
                      className="w-full p-2 border rounded-lg text-sm text-center"
                      placeholder="DP"
                      data-testid="strabismo-centro-dx"
                    />
                  </div>
                  <div className="text-center">
                    <label className="text-xs text-gray-500 font-bold">● Primaria</label>
                    <input
                      type="text"
                      value={esame.motilita_oculare.strabismo_9_posizioni?.centro || ''}
                      onChange={(e) => setEsame(p => ({
                        ...p,
                        motilita_oculare: {
                          ...p.motilita_oculare,
                          strabismo_9_posizioni: {
                            ...p.motilita_oculare.strabismo_9_posizioni,
                            centro: e.target.value
                          }
                        }
                      }))}
                      className="w-full p-2 border-2 border-sky-500 rounded-lg text-sm text-center font-medium"
                      placeholder="DP Primaria"
                      data-testid="strabismo-centro"
                    />
                  </div>
                  <div className="text-center">
                    <label className="text-xs text-gray-500">→ Centro SX</label>
                    <input
                      type="text"
                      value={esame.motilita_oculare.strabismo_9_posizioni?.centro_sx || ''}
                      onChange={(e) => setEsame(p => ({
                        ...p,
                        motilita_oculare: {
                          ...p.motilita_oculare,
                          strabismo_9_posizioni: {
                            ...p.motilita_oculare.strabismo_9_posizioni,
                            centro_sx: e.target.value
                          }
                        }
                      }))}
                      className="w-full p-2 border rounded-lg text-sm text-center"
                      placeholder="DP"
                      data-testid="strabismo-centro-sx"
                    />
                  </div>
                  {/* Riga inferiore */}
                  <div className="text-center">
                    <label className="text-xs text-gray-500">↙ Basso DX</label>
                    <input
                      type="text"
                      value={esame.motilita_oculare.strabismo_9_posizioni?.basso_dx || ''}
                      onChange={(e) => setEsame(p => ({
                        ...p,
                        motilita_oculare: {
                          ...p.motilita_oculare,
                          strabismo_9_posizioni: {
                            ...p.motilita_oculare.strabismo_9_posizioni,
                            basso_dx: e.target.value
                          }
                        }
                      }))}
                      className="w-full p-2 border rounded-lg text-sm text-center"
                      placeholder="DP"
                      data-testid="strabismo-basso-dx"
                    />
                  </div>
                  <div className="text-center">
                    <label className="text-xs text-gray-500">↓ Basso Centro</label>
                    <input
                      type="text"
                      value={esame.motilita_oculare.strabismo_9_posizioni?.basso_centro || ''}
                      onChange={(e) => setEsame(p => ({
                        ...p,
                        motilita_oculare: {
                          ...p.motilita_oculare,
                          strabismo_9_posizioni: {
                            ...p.motilita_oculare.strabismo_9_posizioni,
                            basso_centro: e.target.value
                          }
                        }
                      }))}
                      className="w-full p-2 border rounded-lg text-sm text-center"
                      placeholder="DP"
                      data-testid="strabismo-basso-centro"
                    />
                  </div>
                  <div className="text-center">
                    <label className="text-xs text-gray-500">↘ Basso SX</label>
                    <input
                      type="text"
                      value={esame.motilita_oculare.strabismo_9_posizioni?.basso_sx || ''}
                      onChange={(e) => setEsame(p => ({
                        ...p,
                        motilita_oculare: {
                          ...p.motilita_oculare,
                          strabismo_9_posizioni: {
                            ...p.motilita_oculare.strabismo_9_posizioni,
                            basso_sx: e.target.value
                          }
                        }
                      }))}
                      className="w-full p-2 border rounded-lg text-sm text-center"
                      placeholder="DP"
                      data-testid="strabismo-basso-sx"
                    />
                  </div>
                </div>
                {/* Campi aggiuntivi */}
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Asimmetria Facciale</label>
                    <input
                      type="text"
                      value={esame.motilita_oculare.asimmetria_facciale || ''}
                      onChange={(e) => setEsame(p => ({
                        ...p,
                        motilita_oculare: { ...p.motilita_oculare, asimmetria_facciale: e.target.value }
                      }))}
                      className="w-full p-2 border rounded-lg text-sm"
                      placeholder="es: inclinazione capo su spalla dx di 7°"
                      data-testid="strabismo-asimmetria"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Inclinazione Capo</label>
                    <input
                      type="text"
                      value={esame.motilita_oculare.inclinazione_capo || ''}
                      onChange={(e) => setEsame(p => ({
                        ...p,
                        motilita_oculare: { ...p.motilita_oculare, inclinazione_capo: e.target.value }
                      }))}
                      className="w-full p-2 border rounded-lg text-sm"
                      placeholder="es: torcicollo compensatorio"
                      data-testid="strabismo-inclinazione"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Ecografia Oculare Section */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: '#2A9FCB' }}>
                <Eye className="w-5 h-5" /> Ecografia Oculare
              </h2>
              
              <div className="flex items-center gap-3 mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={esame.ecografia?.eseguita || false}
                    onChange={(e) => setEsame(p => ({
                      ...p,
                      ecografia: { ...p.ecografia, eseguita: e.target.checked }
                    }))}
                    className="w-4 h-4 rounded text-sky-600"
                    data-testid="ecografia-eseguita"
                  />
                  <span className="text-sm">Ecografia eseguita</span>
                </label>
              </div>

              {esame.ecografia?.eseguita && (
                <div className="grid grid-cols-2 gap-6">
                  {/* Occhio Destro */}
                  <div className="p-4 rounded-lg" style={{ background: 'linear-gradient(160deg, #eaf5fc 0%, #f7fbfe 50%, #e1eef8 100%)' }}>
                    <h3 className="font-semibold mb-3" style={{ color: '#2A9FCB' }}>OD - Occhio Destro</h3>
                    
                    <div className="mb-3">
                      <label className="block text-sm font-medium mb-1">Immagine Ecografia OD</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (file) {
                              const formData = new FormData();
                              formData.append('file', file);
                              try {
                                const token = localStorage.getItem('token');
                                const res = await axios.post(`${API}/api/upload-image`, formData, {
                                  headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                                });
                                setEsame(p => ({
                                  ...p,
                                  ecografia: { ...p.ecografia, od_image_url: res.data.url }
                                }));
                                toast.success('Immagine ecografia OD caricata');
                              } catch (err) {
                                toast.error('Errore upload immagine');
                              }
                            }
                          }}
                          className="w-full text-sm"
                          data-testid="ecografia-od-image-input"
                        />
                      </div>
                      {esame.ecografia?.od_image_url && (
                        <img src={esame.ecografia.od_image_url} alt="Ecografia OD" className="mt-2 rounded-lg max-h-40 object-contain" />
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-1">Referto Ecografia OD</label>
                      <textarea
                        value={esame.ecografia?.od_referto || ''}
                        onChange={(e) => setEsame(p => ({
                          ...p,
                          ecografia: { ...p.ecografia, od_referto: e.target.value }
                        }))}
                        className="w-full p-2 border rounded-lg h-24 text-sm"
                        placeholder="Descrizione ecografia occhio destro..."
                        data-testid="ecografia-od-referto"
                      />
                    </div>
                  </div>

                  {/* Occhio Sinistro */}
                  <div className="p-4 rounded-lg" style={{ background: '#FEF3C7' }}>
                    <h3 className="font-semibold mb-3 text-amber-700">OS - Occhio Sinistro</h3>
                    
                    <div className="mb-3">
                      <label className="block text-sm font-medium mb-1">Immagine Ecografia OS</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (file) {
                              const formData = new FormData();
                              formData.append('file', file);
                              try {
                                const token = localStorage.getItem('token');
                                const res = await axios.post(`${API}/api/upload-image`, formData, {
                                  headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                                });
                                setEsame(p => ({
                                  ...p,
                                  ecografia: { ...p.ecografia, os_image_url: res.data.url }
                                }));
                                toast.success('Immagine ecografia OS caricata');
                              } catch (err) {
                                toast.error('Errore upload immagine');
                              }
                            }
                          }}
                          className="w-full text-sm"
                          data-testid="ecografia-os-image-input"
                        />
                      </div>
                      {esame.ecografia?.os_image_url && (
                        <img src={esame.ecografia.os_image_url} alt="Ecografia OS" className="mt-2 rounded-lg max-h-40 object-contain" />
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-1">Referto Ecografia OS</label>
                      <textarea
                        value={esame.ecografia?.os_referto || ''}
                        onChange={(e) => setEsame(p => ({
                          ...p,
                          ecografia: { ...p.ecografia, os_referto: e.target.value }
                        }))}
                        className="w-full p-2 border rounded-lg h-24 text-sm"
                        placeholder="Descrizione ecografia occhio sinistro..."
                        data-testid="ecografia-os-referto"
                      />
                    </div>
                  </div>
                </div>
              )}

              {esame.ecografia?.eseguita && (
                <div className="mt-4">
                  <label className="block text-sm font-medium mb-1">Note Ecografia</label>
                  <textarea
                    value={esame.ecografia?.note || ''}
                    onChange={(e) => setEsame(p => ({
                      ...p,
                      ecografia: { ...p.ecografia, note: e.target.value }
                    }))}
                    className="w-full p-2 border rounded-lg h-16 text-sm"
                    placeholder="Note aggiuntive..."
                    data-testid="ecografia-note"
                  />
                </div>
              )}
            </div>

            {/* AI Diagnosis Suggestion Section */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 shadow-sm border border-blue-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold flex items-center gap-2" style={{ color: '#2A9FCB' }}>
                  <Sparkles className="w-5 h-5" /> Suggerimento AI Diagnosi (Linee Guida Italiane)
                </h2>
                <button
                  onClick={getAiDiagnosis}
                  disabled={aiDiagnosisLoading}
                  className="px-4 py-2 rounded-lg text-white flex items-center gap-2 disabled:opacity-50"
                  style={{ background: '#2A9FCB' }}
                  data-testid="ai-diagnosis-btn"
                >
                  {aiDiagnosisLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Analisi in corso...
                    </>
                  ) : (
                    <>
                      <Brain className="w-4 h-4" /> Genera Suggerimento
                    </>
                  )}
                </button>
              </div>
              
              <p className="text-sm text-[#2A9FCB]/80 mb-4">
                L&apos;AI analizzerà i dati clinici e le immagini, fornendo diagnosi, terapia e percorso secondo le <strong>linee guida delle Società Scientifiche Italiane</strong>:
                <span className="text-xs block mt-1 text-[#2A9FCB]/70">
                  SIOPS (Strabismo/Pediatria) • SICCSO + SIDESO (Occhio Secco) • AICCER (Cataratta) • SIG (Glaucoma) • FLORETINA (Retina)
                </span>
              </p>
              
              {aiDiagnosis && (
                <div className="mt-4 space-y-4">
                  {aiDiagnosis.diagnosis?.parse_error ? (
                    <div className="p-4 bg-white rounded-lg border">
                      <p className="text-[#2A9FCB] whitespace-pre-wrap">{aiDiagnosis.diagnosis.raw_response}</p>
                    </div>
                  ) : (
                    <>
                      {/* Diagnosi Probabili */}
                      {aiDiagnosis.diagnosis?.diagnosi_probabili && (
                        <div className="p-4 bg-white rounded-lg border border-blue-200">
                          <h3 className="font-semibold text-blue-700 mb-2">🔍 Diagnosi Probabili</h3>
                          <ul className="space-y-2">
                            {aiDiagnosis.diagnosis.diagnosi_probabili.map((d, i) => (
                              <li key={i} className="flex items-center gap-2 flex-wrap">
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  i === 0 ? 'bg-blue-100 text-blue-800' : 
                                  i === 1 ? 'bg-indigo-100 text-indigo-800' : 
                                  'bg-gray-100 text-[#2A9FCB]'
                                }`}>
                                  {d.confidenza || d.percentuale || `${100 - i*20}%`}
                                </span>
                                <span className="font-medium">{d.nome || d.diagnosi || d}</span>
                                {d.codice_icd10 && <span className="text-xs text-[#2A9FCB]/70">({d.codice_icd10})</span>}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {/* Fattori di Rischio */}
                      {aiDiagnosis.diagnosis?.fattori_rischio && (
                        <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                          <h3 className="font-semibold text-amber-700 mb-2">⚠️ Fattori di Rischio</h3>
                          <ul className="list-disc list-inside text-sm text-amber-800">
                            {(Array.isArray(aiDiagnosis.diagnosis.fattori_rischio) 
                              ? aiDiagnosis.diagnosis.fattori_rischio 
                              : [aiDiagnosis.diagnosis.fattori_rischio]
                            ).map((f, i) => (
                              <li key={i}>{f}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {/* Segni Chiave */}
                      {aiDiagnosis.diagnosis?.segni_chiave && (
                        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                          <h3 className="font-semibold text-green-700 mb-2">🔬 Segni Clinici Significativi</h3>
                          <ul className="list-disc list-inside text-sm text-green-800">
                            {(Array.isArray(aiDiagnosis.diagnosis.segni_chiave)
                              ? aiDiagnosis.diagnosis.segni_chiave
                              : [aiDiagnosis.diagnosis.segni_chiave]
                            ).map((s, i) => (
                              <li key={i}>{s}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Terapia Suggerita - NUOVO */}
                      {aiDiagnosis.diagnosis?.terapia_suggerita && (
                        <div className="p-4 bg-sky-50 rounded-lg border border-sky-200">
                          <h3 className="font-semibold text-sky-700 mb-2">💊 Terapia Suggerita</h3>
                          {aiDiagnosis.diagnosis.terapia_suggerita.farmaci && (
                            <div className="mb-3">
                              <p className="text-sm font-medium text-sky-800 mb-1">Farmaci:</p>
                              <ul className="list-disc list-inside text-sm text-sky-700">
                                {(Array.isArray(aiDiagnosis.diagnosis.terapia_suggerita.farmaci)
                                  ? aiDiagnosis.diagnosis.terapia_suggerita.farmaci
                                  : [aiDiagnosis.diagnosis.terapia_suggerita.farmaci]
                                ).map((f, i) => (
                                  <li key={i}>{typeof f === 'object' ? `${f.nome}: ${f.posologia}` : f}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {aiDiagnosis.diagnosis.terapia_suggerita.durata_trattamento && (
                            <p className="text-sm text-sky-700"><strong>Durata:</strong> {aiDiagnosis.diagnosis.terapia_suggerita.durata_trattamento}</p>
                          )}
                          {aiDiagnosis.diagnosis.terapia_suggerita.linee_guida_riferimento && (
                            <p className="text-xs text-sky-600 mt-2 italic">📚 Riferimento: {aiDiagnosis.diagnosis.terapia_suggerita.linee_guida_riferimento}</p>
                          )}
                          {aiDiagnosis.diagnosis.terapia_suggerita.note_pediatriche && (
                            <div className="mt-2 p-2 bg-yellow-100 rounded text-xs text-yellow-800">
                              👶 Note pediatriche: {aiDiagnosis.diagnosis.terapia_suggerita.note_pediatriche}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Percorso Diagnostico - NUOVO */}
                      {aiDiagnosis.diagnosis?.percorso_diagnostico && (
                        <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                          <h3 className="font-semibold text-purple-700 mb-2">🔄 Percorso Diagnostico</h3>
                          {aiDiagnosis.diagnosis.percorso_diagnostico.esami_consigliati && (
                            <div className="mb-2">
                              <p className="text-sm font-medium text-purple-800">Esami consigliati:</p>
                              <ul className="list-disc list-inside text-sm text-purple-700">
                                {(Array.isArray(aiDiagnosis.diagnosis.percorso_diagnostico.esami_consigliati)
                                  ? aiDiagnosis.diagnosis.percorso_diagnostico.esami_consigliati
                                  : [aiDiagnosis.diagnosis.percorso_diagnostico.esami_consigliati]
                                ).map((e, i) => (
                                  <li key={i}>{e}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {aiDiagnosis.diagnosis.percorso_diagnostico.consulenze_specialistiche && (
                            <div className="mb-2">
                              <p className="text-sm font-medium text-purple-800">Consulenze specialistiche:</p>
                              <ul className="list-disc list-inside text-sm text-purple-700">
                                {(Array.isArray(aiDiagnosis.diagnosis.percorso_diagnostico.consulenze_specialistiche)
                                  ? aiDiagnosis.diagnosis.percorso_diagnostico.consulenze_specialistiche
                                  : [aiDiagnosis.diagnosis.percorso_diagnostico.consulenze_specialistiche]
                                ).map((c, i) => (
                                  <li key={i}>{c}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Piano Follow-up - NUOVO */}
                      {aiDiagnosis.diagnosis?.piano_follow_up && (
                        <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                          <h3 className="font-semibold text-indigo-700 mb-2">📅 Piano Follow-up</h3>
                          {aiDiagnosis.diagnosis.piano_follow_up.prossima_visita && (
                            <p className="text-sm text-indigo-700"><strong>Prossima visita:</strong> {aiDiagnosis.diagnosis.piano_follow_up.prossima_visita}</p>
                          )}
                          {aiDiagnosis.diagnosis.piano_follow_up.esami_controllo && (
                            <p className="text-sm text-indigo-700"><strong>Esami al controllo:</strong> {
                              Array.isArray(aiDiagnosis.diagnosis.piano_follow_up.esami_controllo)
                                ? aiDiagnosis.diagnosis.piano_follow_up.esami_controllo.join(', ')
                                : aiDiagnosis.diagnosis.piano_follow_up.esami_controllo
                            }</p>
                          )}
                          {aiDiagnosis.diagnosis.piano_follow_up.obiettivi_terapeutici && (
                            <p className="text-sm text-indigo-700"><strong>Obiettivi:</strong> {aiDiagnosis.diagnosis.piano_follow_up.obiettivi_terapeutici}</p>
                          )}
                        </div>
                      )}

                      {/* Piano Chirurgico - NUOVO */}
                      {aiDiagnosis.diagnosis?.piano_chirurgico && (
                        <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                          <h3 className="font-semibold text-red-700 mb-2">🏥 Piano Chirurgico</h3>
                          {aiDiagnosis.diagnosis.piano_chirurgico.intervento_proposto && (
                            <p className="text-sm text-red-700"><strong>Intervento:</strong> {aiDiagnosis.diagnosis.piano_chirurgico.intervento_proposto}</p>
                          )}
                          {aiDiagnosis.diagnosis.piano_chirurgico.indicazioni && (
                            <p className="text-sm text-red-700"><strong>Indicazioni:</strong> {aiDiagnosis.diagnosis.piano_chirurgico.indicazioni}</p>
                          )}
                          {aiDiagnosis.diagnosis.piano_chirurgico.timing_ottimale && (
                            <p className="text-sm text-red-700"><strong>Timing:</strong> {aiDiagnosis.diagnosis.piano_chirurgico.timing_ottimale}</p>
                          )}
                          {aiDiagnosis.diagnosis.piano_chirurgico.preparazione_preoperatoria && (
                            <p className="text-sm text-red-700"><strong>Preparazione:</strong> {aiDiagnosis.diagnosis.piano_chirurgico.preparazione_preoperatoria}</p>
                          )}
                        </div>
                      )}

                      {/* Note Linee Guida - NUOVO */}
                      {aiDiagnosis.diagnosis?.note_linee_guida && (
                        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <h3 className="font-semibold text-[#2A9FCB] mb-2">📚 Riferimenti Linee Guida</h3>
                          <p className="text-sm text-[#2A9FCB]/80">{aiDiagnosis.diagnosis.note_linee_guida}</p>
                        </div>
                      )}
                      
                      {/* Urgenza */}
                      {aiDiagnosis.diagnosis?.urgenza && (
                        <div className={`p-3 rounded-lg text-center font-medium ${
                          aiDiagnosis.diagnosis.urgenza === 'urgente' ? 'bg-red-100 text-red-800' :
                          aiDiagnosis.diagnosis.urgenza === 'alta' ? 'bg-orange-100 text-orange-800' :
                          aiDiagnosis.diagnosis.urgenza === 'media' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          🚨 Livello di Urgenza: {aiDiagnosis.diagnosis.urgenza.toUpperCase()}
                        </div>
                      )}

                      {/* Pulsante Conferma e Invia */}
                      <div className="mt-6 p-4 bg-white rounded-lg border-2 border-emerald-200">
                        <h3 className="font-semibold text-emerald-700 mb-3">✅ Conferma e Invia al Paziente</h3>
                        <p className="text-sm text-[#2A9FCB]/80 mb-4">
                          Confermando, il riepilogo verrà inviato al paziente via email e/o SMS.
                        </p>
                        <div className="flex flex-wrap gap-3">
                          <button
                            onClick={() => confirmAndSendDiagnosis(true, false)}
                            className="px-4 py-2 rounded-lg text-white flex items-center gap-2"
                            style={{ background: '#2A9FCB' }}
                            data-testid="confirm-send-email-btn"
                          >
                            <Mail className="w-4 h-4" /> Conferma e Invia Email
                          </button>
                          <button
                            onClick={() => confirmAndSendDiagnosis(true, true)}
                            className="px-4 py-2 rounded-lg text-white flex items-center gap-2"
                            style={{ background: '#2A9FCB' }}
                            data-testid="confirm-send-both-btn"
                          >
                            <Phone className="w-4 h-4" /> Conferma e Invia Email + SMS
                          </button>
                          <button
                            onClick={() => confirmAndSendDiagnosis(false, false)}
                            className="px-4 py-2 rounded-lg border flex items-center gap-2"
                            style={{ color: '#2A9FCB', borderColor: '#2A9FCB' }}
                          >
                            <Check className="w-4 h-4" /> Solo Conferma (no invio)
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                  
                  {/* Disclaimer */}
                  <div className="mt-4 p-3 bg-gray-100 rounded-lg text-xs text-[#2A9FCB]/80 italic">
                    ⚠️ {aiDiagnosis.disclaimer || "Questo è un suggerimento AI basato sulle linee guida italiane. La diagnosi finale è responsabilità del medico curante."}
                  </div>
                </div>
              )}
            </div>

            {/* Consigli e Follow-up */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h2 className="text-lg font-semibold mb-4" style={{ color: '#2A9FCB' }}>Consigli e Follow-up</h2>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium">Consigli</label>
                    <button
                      onClick={isRecording ? stopRecording : startRecording}
                      disabled={transcribing}
                      className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-2 transition-all ${
                        isRecording 
                          ? 'bg-red-500 text-white animate-pulse' 
                          : transcribing
                            ? 'bg-gray-200 text-gray-500'
                            : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                      }`}
                      title={isRecording ? 'Ferma registrazione' : 'Avvia dettatura vocale'}
                    >
                      {transcribing ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Trascrivo...
                        </>
                      ) : isRecording ? (
                        <>
                          <Volume2 className="w-4 h-4" />
                          Stop
                        </>
                      ) : (
                        <>
                          <Volume2 className="w-4 h-4" />
                          🎤 Dettatura
                        </>
                      )}
                    </button>
                  </div>
                  <textarea
                    value={esame.consigli}
                    onChange={(e) => setEsame(p => ({ ...p, consigli: e.target.value }))}
                    className="w-full p-2 border rounded-lg h-24"
                    placeholder="Consigli per il paziente... (usa la dettatura vocale o scrivi)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Prossima Visita Consigliata</label>
                  <div className="flex gap-2">
                    <select
                      value={esame.prossima_visita_mesi || ''}
                      onChange={(e) => setEsame(p => ({ ...p, prossima_visita_mesi: parseInt(e.target.value) || null }))}
                      className="flex-1 p-2 border rounded-lg"
                    >
                      <option value="">Seleziona...</option>
                      <option value="1">1 mese</option>
                      <option value="3">3 mesi</option>
                      <option value="6">6 mesi</option>
                      <option value="12">12 mesi</option>
                      <option value="24">24 mesi</option>
                    </select>
                    <button
                      onClick={() => navigate(`/booking?patient=${patientId || currentUser?.id}`)}
                      className="px-4 py-2 rounded-lg text-white flex items-center gap-2"
                      style={{ background: '#2A9FCB' }}
                    >
                      <Calendar className="w-4 h-4" /> Prenota
                    </button>
                  </div>
                  <p className="text-xs text-[#2A9FCB]/70 mt-2">
                    Se il paziente non prenota entro la data consigliata, riceverà un promemoria via SMS/Email
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Prescrizioni Tab */}
        {activeTab === 'prescrizioni' && (
          <div className="space-y-6">
            {/* AI Terapia Suggestion Banner */}
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-purple-800">🤖 Suggerimento AI Terapia</h3>
                    <p className="text-sm text-purple-600">
                      Basato su diagnosi: <strong>{aiDiagnosis?.diagnosis?.diagnosi_principale || concludiData.diagnosi || 'Non specificata'}</strong>
                    </p>
                  </div>
                </div>
                <button
                  onClick={getAiTerapia}
                  disabled={aiTerapiaLoading}
                  className="px-5 py-3 rounded-xl text-white font-medium flex items-center gap-2 disabled:opacity-50 shadow-lg hover:shadow-xl transition-all"
                  style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)' }}
                >
                  {aiTerapiaLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Elaborazione...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Ottieni Suggerimento
                    </>
                  )}
                </button>
              </div>
              <p className="text-xs text-purple-500 mt-3">
                L'AI analizzerà: età paziente ({patient?.date_of_birth ? Math.floor((new Date() - new Date(patient.date_of_birth)) / (365.25 * 24 * 60 * 60 * 1000)) : '?'} anni), esame clinico (IOP, visus), patologie concomitanti e farmaci in uso
              </p>
            </div>

            {/* Drug Selector */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold" style={{ color: '#2A9FCB' }}>
                  <Pill className="w-5 h-5 inline mr-2" /> Prescrizione Farmaci / Terapia
                </h2>
                <div className="flex gap-2">
                  <button
                    onClick={addManualPrescription}
                    className="px-3 py-2 rounded-lg border border-[#2A9FCB] text-[#2A9FCB] hover:bg-sky-50 flex items-center gap-2 text-sm"
                    data-testid="add-manual-prescription-btn"
                    title="Inserisci manualmente un farmaco non presente nel catalogo"
                  >
                    <Plus className="w-4 h-4" /> Inserisci manualmente
                  </button>
                  <button
                    onClick={() => setShowDrugSelector(!showDrugSelector)}
                    className="px-4 py-2 rounded-lg text-white flex items-center gap-2"
                    style={{ background: '#2A9FCB' }}
                    data-testid="open-drug-selector-btn"
                  >
                    <Plus className="w-4 h-4" /> Aggiungi da Catalogo
                  </button>
                </div>
              </div>

              {/* Allergie warning banner */}
              {esame.allergie && esame.allergie.trim() && esame.prescrizioni_colliri.length > 0 && (
                <div className="mb-4 p-3 border border-amber-300 bg-amber-50 rounded-lg flex items-start gap-2" data-testid="allergie-warning-banner">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <strong>Allergie/Intolleranze segnalate:</strong> {esame.allergie}
                    <div className="text-xs text-amber-700 mt-1">Verifica compatibilità dei farmaci prescritti.</div>
                  </div>
                </div>
              )}

              {showDrugSelector && (
                <div className="mb-6 p-4 border rounded-lg bg-gray-50">
                  <DrugSelector 
                    onSelect={addPrescription}
                    selectedDrugs={esame.prescrizioni_colliri}
                  />
                </div>
              )}

              {/* Prescribed Drugs */}
              {esame.prescrizioni_colliri.length > 0 ? (
                <div className="space-y-4">
                  {esame.prescrizioni_colliri.map((p, idx) => {
                    // Check allergia incrociata: se il principio attivo o nome compare nelle allergie del paziente
                    const allergiaText = (esame.allergie || '').toLowerCase();
                    const principio = (p.principio_attivo || '').toLowerCase();
                    const nomeCom = (p.nome_commerciale || '').toLowerCase();
                    const principleTokens = principio.split(/[\s+,\/]+/).filter(t => t.length >= 4);
                    const isAllergyConflict = !!allergiaText && (
                      (principio && allergiaText.includes(principio)) ||
                      (nomeCom && allergiaText.includes(nomeCom)) ||
                      principleTokens.some(t => allergiaText.includes(t))
                    );
                    const isManual = !!p._manual || (p.farmaco_id || '').startsWith('manual_');
                    const missingRequired = !p.nome_commerciale || !p.principio_attivo || !p.concentrazione || !p.posologia || !p.durata;
                    return (
                    <div key={idx} className={`border rounded-lg p-4 ${isAllergyConflict ? 'bg-red-50 border-red-300' : 'bg-gray-50'}`} data-testid={`prescription-row-${idx}`}>
                      {isAllergyConflict && (
                        <div className="mb-3 p-2 bg-red-100 border border-red-300 rounded text-sm text-red-800 flex items-center gap-2" data-testid={`allergy-conflict-${idx}`}>
                          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                          <strong>ATTENZIONE:</strong> il principio attivo o il farmaco potrebbe essere in conflitto con le allergie dichiarate del paziente.
                        </div>
                      )}
                      {missingRequired && (
                        <div className="mb-3 p-2 bg-amber-100 border border-amber-300 rounded text-xs text-amber-800" data-testid={`missing-required-${idx}`}>
                          Campi obbligatori mancanti: compila <strong>nome commerciale, principio attivo, concentrazione, posologia, durata</strong>.
                        </div>
                      )}
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          {isManual ? (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
                              <div>
                                <label className="text-xs text-[#2A9FCB]/70">Nome commerciale *</label>
                                <input
                                  type="text"
                                  value={p.nome_commerciale}
                                  onChange={(e) => updatePrescription(idx, 'nome_commerciale', e.target.value)}
                                  className={`w-full p-2 border rounded-lg text-sm font-semibold ${!p.nome_commerciale ? 'border-amber-400' : ''}`}
                                  placeholder="es. TOBRADEX"
                                  data-testid={`prescription-name-${idx}`}
                                />
                              </div>
                              <div>
                                <label className="text-xs text-[#2A9FCB]/70">Principio attivo *</label>
                                <input
                                  type="text"
                                  value={p.principio_attivo}
                                  onChange={(e) => updatePrescription(idx, 'principio_attivo', e.target.value)}
                                  className={`w-full p-2 border rounded-lg text-sm ${!p.principio_attivo ? 'border-amber-400' : ''}`}
                                  placeholder="es. Tobramicina + Desametasone"
                                  data-testid={`prescription-principio-${idx}`}
                                />
                              </div>
                              <div>
                                <label className="text-xs text-[#2A9FCB]/70">Concentrazione / % *</label>
                                <input
                                  type="text"
                                  value={p.concentrazione}
                                  onChange={(e) => updatePrescription(idx, 'concentrazione', e.target.value)}
                                  className={`w-full p-2 border rounded-lg text-sm ${!p.concentrazione ? 'border-amber-400' : ''}`}
                                  placeholder="es. 0.3% + 0.1%"
                                  data-testid={`prescription-concentrazione-${idx}`}
                                />
                              </div>
                            </div>
                          ) : (
                            <>
                              <h3 className="font-semibold">{p.nome_commerciale}</h3>
                              <p className="text-sm text-[#2A9FCB]/80">
                                {p.principio_attivo}{p.concentrazione ? ` • ${p.concentrazione}` : ''}
                              </p>
                            </>
                          )}
                        </div>
                        <div className="flex gap-2">
                          {!isManual && (
                            <button
                              onClick={() => {
                                const farmaco = { ...p, id: p.farmaco_id };
                                setSelectedDrugInfo(farmaco);
                              }}
                              className="p-1 hover:bg-blue-100 rounded text-blue-600"
                              title="Info"
                            >
                              <Info className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => removePrescription(idx)}
                            className="p-1 hover:bg-red-100 rounded text-red-600"
                            title="Rimuovi"
                            data-testid={`remove-prescription-${idx}`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div>
                          <label className="text-xs text-[#2A9FCB]/70">Forma farmaceutica</label>
                          <select
                            value={p.forma_farmaceutica || 'Collirio'}
                            onChange={(e) => updatePrescription(idx, 'forma_farmaceutica', e.target.value)}
                            className="w-full p-2 border rounded-lg text-sm"
                            data-testid={`prescription-forma-${idx}`}
                          >
                            <option value="Collirio">Collirio</option>
                            <option value="Pomata oftalmica">Pomata oftalmica</option>
                            <option value="Gel oftalmico">Gel oftalmico</option>
                            <option value="Capsule">Capsule</option>
                            <option value="Compresse">Compresse</option>
                            <option value="Bustine/Flaconcini orali">Bustine/Flaconcini orali</option>
                            <option value="Iniezione intravitreale">Iniezione intravitreale</option>
                            <option value="Iniezione">Iniezione</option>
                            <option value="Strisce diagnostiche">Strisce diagnostiche</option>
                            <option value="Altro">Altro</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-xs text-[#2A9FCB]/70">Posologia *</label>
                          <input
                            type="text"
                            value={p.posologia}
                            onChange={(e) => updatePrescription(idx, 'posologia', e.target.value)}
                            className={`w-full p-2 border rounded-lg text-sm ${!p.posologia ? 'border-amber-400' : ''}`}
                            placeholder="1 goccia x 4 volte/die"
                            data-testid={`prescription-posologia-${idx}`}
                          />
                        </div>
                        <div>
                          <label className="text-xs text-[#2A9FCB]/70">Durata *</label>
                          <input
                            type="text"
                            value={p.durata}
                            onChange={(e) => updatePrescription(idx, 'durata', e.target.value)}
                            className={`w-full p-2 border rounded-lg text-sm ${!p.durata ? 'border-amber-400' : ''}`}
                            placeholder="7 giorni"
                            data-testid={`prescription-durata-${idx}`}
                          />
                        </div>
                        <div>
                          <label className="text-xs text-[#2A9FCB]/70">Occhio</label>
                          <select
                            value={p.occhio}
                            onChange={(e) => updatePrescription(idx, 'occhio', e.target.value)}
                            className="w-full p-2 border rounded-lg text-sm"
                            data-testid={`prescription-occhio-${idx}`}
                          >
                            <option value="OU">Entrambi (OU)</option>
                            <option value="OD">Destro (OD)</option>
                            <option value="OS">Sinistro (OS)</option>
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                        <div>
                          <label className="text-xs text-[#2A9FCB]/70">Istruzioni per il paziente</label>
                          <input
                            type="text"
                            value={p.istruzioni || ''}
                            onChange={(e) => updatePrescription(idx, 'istruzioni', e.target.value)}
                            className="w-full p-2 border rounded-lg text-sm"
                            placeholder="Agitare prima dell'uso, lontano dai pasti, ecc."
                            data-testid={`prescription-istruzioni-${idx}`}
                          />
                        </div>
                        <div>
                          <label className="text-xs text-[#2A9FCB]/70">Controindicazioni / Avvertenze</label>
                          <input
                            type="text"
                            value={p.controindicazioni || ''}
                            onChange={(e) => updatePrescription(idx, 'controindicazioni', e.target.value)}
                            className="w-full p-2 border rounded-lg text-sm"
                            placeholder="Es. ipersensibilità ai componenti..."
                            data-testid={`prescription-controindicazioni-${idx}`}
                          />
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-[#2A9FCB]/70 text-center py-8">
                  Nessun farmaco prescritto. Clicca &quot;Aggiungi da Catalogo&quot; o &quot;Inserisci manualmente&quot; per iniziare.
                </p>
              )}
            </div>

            {/* Bendaggio Section */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: '#2A9FCB' }}>
                <Eye className="w-5 h-5" /> Bendaggio (Terapia Occlusiva)
              </h2>
              
              <div className="flex items-center gap-4 mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={esame.bendaggio.prescritto}
                    onChange={(e) => setEsame(p => ({
                      ...p,
                      bendaggio: { ...p.bendaggio, prescritto: e.target.checked }
                    }))}
                    className="w-5 h-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    data-testid="bendaggio-checkbox"
                  />
                  <span className="font-medium">Prescrivere Bendaggio</span>
                </label>
              </div>

              {esame.bendaggio.prescritto && (
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 p-4 rounded-lg" style={{ background: '#F0FAF5' }}>
                  <div>
                    <label className="block text-sm font-medium mb-1">Occhio da Bendare</label>
                    <select
                      value={esame.bendaggio.occhio}
                      onChange={(e) => setEsame(p => ({
                        ...p,
                        bendaggio: { ...p.bendaggio, occhio: e.target.value }
                      }))}
                      className="w-full p-2 border rounded-lg"
                      data-testid="bendaggio-occhio"
                    >
                      <option value="">Seleziona...</option>
                      <option value="OD">Occhio Destro (OD)</option>
                      <option value="OS">Occhio Sinistro (OS)</option>
                      <option value="alternato">Alternato</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Ore al Giorno</label>
                    <select
                      value={esame.bendaggio.ore_giorno}
                      onChange={(e) => setEsame(p => ({
                        ...p,
                        bendaggio: { ...p.bendaggio, ore_giorno: e.target.value }
                      }))}
                      className="w-full p-2 border rounded-lg"
                      data-testid="bendaggio-ore"
                    >
                      <option value="">Seleziona...</option>
                      <option value="1">1 ora</option>
                      <option value="2">2 ore</option>
                      <option value="3">3 ore</option>
                      <option value="4">4 ore</option>
                      <option value="5">5 ore</option>
                      <option value="6">6 ore</option>
                      <option value="8">8 ore</option>
                      <option value="tutto_giorno">Tutto il giorno</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Durata (settimane)</label>
                    <select
                      value={esame.bendaggio.durata_settimane}
                      onChange={(e) => setEsame(p => ({
                        ...p,
                        bendaggio: { ...p.bendaggio, durata_settimane: e.target.value }
                      }))}
                      className="w-full p-2 border rounded-lg"
                      data-testid="bendaggio-durata"
                    >
                      <option value="">Seleziona...</option>
                      <option value="1">1 settimana</option>
                      <option value="2">2 settimane</option>
                      <option value="4">4 settimane (1 mese)</option>
                      <option value="8">8 settimane (2 mesi)</option>
                      <option value="12">12 settimane (3 mesi)</option>
                      <option value="24">24 settimane (6 mesi)</option>
                      <option value="continuo">Continuo fino a controllo</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Note Bendaggio</label>
                    <input
                      type="text"
                      value={esame.bendaggio.note}
                      onChange={(e) => setEsame(p => ({
                        ...p,
                        bendaggio: { ...p.bendaggio, note: e.target.value }
                      }))}
                      className="w-full p-2 border rounded-lg"
                      placeholder="Note aggiuntive..."
                      data-testid="bendaggio-note"
                    />
                  </div>
                </div>
              )}

              {esame.bendaggio.prescritto && esame.bendaggio.occhio && (
                <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <p className="text-sm text-amber-800">
                    <strong>Riepilogo:</strong> Bendaggio occhio <strong>{esame.bendaggio.occhio}</strong> per{' '}
                    <strong>{esame.bendaggio.ore_giorno === 'tutto_giorno' ? 'tutto il giorno' : `${esame.bendaggio.ore_giorno} ore/giorno`}</strong>,{' '}
                    durata: <strong>{esame.bendaggio.durata_settimane === 'continuo' ? 'continuo' : `${esame.bendaggio.durata_settimane} settimane`}</strong>
                  </p>
                </div>
              )}
            </div>

            {/* Print Preview */}
            {(esame.prescrizioni_colliri.length > 0 || esame.bendaggio.prescritto) && (
              <div className="bg-white rounded-xl p-6 shadow-sm print:shadow-none" id="print-prescription">
                <div className="border-b pb-4 mb-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h1 className="text-2xl font-bold" style={{ color: '#2A9FCB' }}>PRIME VISION</h1>
                      <p className="text-sm text-[#2A9FCB]/80">Centro di Eccellenza Oftalmologica</p>
                      <p className="text-sm text-[#2A9FCB]/80">Via Santa Sofia 10, Roma</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm">Data: {new Date().toLocaleDateString('it-IT')}</p>
                      <p className="text-sm">Dr. {currentUser?.first_name} {currentUser?.last_name}</p>
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <h2 className="font-semibold mb-2">Paziente</h2>
                  <p>{patient?.first_name} {patient?.last_name}</p>
                </div>

                {esame.prescrizioni_colliri.length > 0 && (
                  <>
                    <h2 className="font-semibold mb-4">Prescrizione Farmaci / Terapia</h2>
                    {esame.allergie && (
                      <div className="mb-3 p-2 bg-amber-50 border border-amber-300 rounded text-sm text-amber-800">
                        <strong>⚠️ Allergie segnalate:</strong> {esame.allergie}
                      </div>
                    )}
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="border p-2 text-left">Farmaco</th>
                          <th className="border p-2 text-left">Conc.</th>
                          <th className="border p-2 text-left">Forma</th>
                          <th className="border p-2 text-left">Posologia</th>
                          <th className="border p-2 text-left">Durata</th>
                          <th className="border p-2 text-left">Occhio</th>
                          <th className="border p-2 text-left">Istruzioni</th>
                        </tr>
                      </thead>
                      <tbody>
                        {esame.prescrizioni_colliri.map((p, idx) => (
                          <tr key={idx}>
                            <td className="border p-2">
                              <strong>{p.nome_commerciale}</strong><br />
                              <span className="text-sm text-[#2A9FCB]/80">{p.principio_attivo}</span>
                            </td>
                            <td className="border p-2 text-sm">{p.concentrazione || '-'}</td>
                            <td className="border p-2 text-sm">{p.forma_farmaceutica || 'Collirio'}</td>
                            <td className="border p-2">{p.posologia}</td>
                            <td className="border p-2">{p.durata}</td>
                            <td className="border p-2">{p.occhio}</td>
                            <td className="border p-2 text-xs">{p.istruzioni || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}

                {esame.bendaggio.prescritto && (
                  <div className="mt-6">
                    <h2 className="font-semibold mb-4">Terapia Occlusiva (Bendaggio)</h2>
                    <div className="p-4 border rounded-lg bg-gray-50">
                      <p><strong>Occhio:</strong> {esame.bendaggio.occhio === 'OD' ? 'Occhio Destro (OD)' : esame.bendaggio.occhio === 'OS' ? 'Occhio Sinistro (OS)' : 'Alternato'}</p>
                      <p><strong>Durata giornaliera:</strong> {esame.bendaggio.ore_giorno === 'tutto_giorno' ? 'Tutto il giorno' : `${esame.bendaggio.ore_giorno} ore al giorno`}</p>
                      <p><strong>Durata terapia:</strong> {esame.bendaggio.durata_settimane === 'continuo' ? 'Continuo fino a controllo' : `${esame.bendaggio.durata_settimane} settimane`}</p>
                      {esame.bendaggio.note && <p><strong>Note:</strong> {esame.bendaggio.note}</p>}
                    </div>
                  </div>
                )}

                <div className="mt-6 flex justify-end">
                  <button
                    onClick={printPrescription}
                    className="px-6 py-2 rounded-lg text-white flex items-center gap-2 print:hidden"
                    style={{ background: '#2A9FCB' }}
                  >
                    <Printer className="w-4 h-4" /> Stampa Ricetta
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Storico Tab */}
        {activeTab === 'storico' && (
          <div className="space-y-6">
            {/* Header Storico */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold" style={{ color: '#2A9FCB' }}>
                  <FileText className="w-5 h-5 inline mr-2" />
                  Storico Visite ({storicoVisite.length})
                </h2>
                {storicoVisite.length >= 2 && (
                  <button
                    onClick={() => setCompareMode(!compareMode)}
                    className={`px-4 py-2 rounded-lg font-medium ${compareMode ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-[#2A9FCB]'}`}
                  >
                    {compareMode ? 'Esci da Confronto' : 'Confronta Visite'}
                  </button>
                )}
              </div>

              {storicoVisite.length === 0 ? (
                <p className="text-[#2A9FCB]/70 text-center py-8">
                  Nessuna visita registrata. Compila l&apos;esame clinico nella tab &quot;Esame Clinico&quot; per creare la prima visita.
                </p>
              ) : (
                <div className="space-y-3">
                  {storicoVisite.map((visita, idx) => (
                    <div 
                      key={visita.id}
                      className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${
                        selectedVisita?.id === visita.id 
                          ? 'border-emerald-500 bg-emerald-50' 
                          : compareVisita?.id === visita.id
                            ? 'border-amber-500 bg-amber-50'
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                      onClick={() => {
                        if (compareMode) {
                          if (selectedVisita?.id === visita.id) return;
                          setCompareVisita(visita);
                        } else {
                          setSelectedVisita(selectedVisita?.id === visita.id ? null : visita);
                        }
                      }}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-1 rounded-full  text-xs font-medium bg-emerald-100 text-emerald-800">
                              Visita #{storicoVisite.length - idx}
                            </span>
                            <span className="text-[#2A9FCB]/70 text-sm">
                              {new Date(visita.created_at).toLocaleDateString('it-IT', {
                                day: '2-digit',
                                month: 'long',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                            {visita.concluded && (
                              <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">
                                ✓ Conclusa
                              </span>
                            )}
                          </div>
                          <p className="font-medium mt-2">{visita.motivo_visita || 'Controllo'}</p>
                          <div className="flex gap-4 mt-2 text-sm text-[#2A9FCB]/80">
                            <span>OD: {visita.occhio_destro?.visus || '-'}</span>
                            <span>OS: {visita.occhio_sinistro?.visus || '-'}</span>
                            <span>Tono OD: {visita.occhio_destro?.tonometria_senza_gocce || '-'} mmHg</span>
                            <span>Tono OS: {visita.occhio_sinistro?.tonometria_senza_gocce || '-'} mmHg</span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm('Vuoi importare tutti i dati di questa visita nella bozza attuale? I campi gia compilati verranno sovrascritti.')) {
                                importFromPreviousVisit(visita);
                              }
                            }}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 bg-amber-500 text-white hover:bg-amber-600"
                            data-testid={`import-visit-btn-${visita.id}`}
                          >
                            <Copy className="w-3 h-3" /> Importa nella Bozza
                          </button>
                          {visita.concluded && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedVisitForPDF(visita);
                                setShowPDFViewer(true);
                              }}
                              className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 bg-blue-500 text-white hover:bg-blue-600"
                              data-testid={`download-pdf-btn-${visita.id}`}
                            >
                              <Eye className="w-3 h-3" /> Anteprima PDF
                            </button>
                          )}
                          {!visita.concluded && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedVisitForPDF(visita);
                                setShowPDFViewer(true);
                              }}
                              className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 bg-gray-500 text-white hover:bg-gray-600"
                              data-testid={`view-visit-btn-${visita.id}`}
                            >
                              <FileText className="w-3 h-3" /> Vedi PDF
                            </button>
                          )}
                          {compareMode && selectedVisita?.id !== visita.id && (
                            <span className="text-xs text-amber-600">
                              {compareVisita?.id === visita.id ? '✓ Confronto' : 'Clicca per confrontare'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Dettaglio Visita Selezionata */}
            {selectedVisita && !compareMode && (
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-semibold mb-4" style={{ color: '#2A9FCB' }}>
                  Dettaglio Visita del {new Date(selectedVisita.created_at).toLocaleDateString('it-IT')}
                </h3>
                
                <div className="grid lg:grid-cols-2 gap-6">
                  {/* OD */}
                  <div className="p-4 rounded-xl" style={{ background: '#E8F5E9' }}>
                    <h4 className="font-semibold mb-3 text-center">👁️ OCCHIO DESTRO (OD)</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span>Visus:</span><strong>{selectedVisita.occhio_destro?.visus || '-'}</strong></div>
                      <div className="flex justify-between"><span>Visus c/c:</span><strong>{selectedVisita.occhio_destro?.visus_con_correzione || '-'}</strong></div>
                      <div className="flex justify-between"><span>Autoref:</span><strong>{selectedVisita.occhio_destro?.autoref_sfera || '-'} {selectedVisita.occhio_destro?.autoref_cilindro || ''} {selectedVisita.occhio_destro?.autoref_asse || ''}</strong></div>
                      <div className="flex justify-between"><span>Prescrizione:</span><strong>{selectedVisita.occhio_destro?.prescrizione_sfera || '-'} {selectedVisita.occhio_destro?.prescrizione_cilindro || ''} {selectedVisita.occhio_destro?.prescrizione_asse || ''}</strong></div>
                      <div className="flex justify-between"><span>Tono s/g:</span><strong>{selectedVisita.occhio_destro?.tonometria_senza_gocce || '-'} mmHg</strong></div>
                      <div className="flex justify-between"><span>Tono c/g:</span><strong>{selectedVisita.occhio_destro?.tonometria_con_gocce || '-'} mmHg</strong></div>
                      <div className="flex justify-between"><span>Mezzi:</span><strong>{selectedVisita.occhio_destro?.mezzi_diottrici || '-'}</strong></div>
                      <div className="flex justify-between"><span>Conta End.:</span><strong>{selectedVisita.occhio_destro?.conta_endoteliale || '-'} cell/mm²</strong></div>
                      <div className="flex justify-between"><span>Topografia:</span><strong>{selectedVisita.occhio_destro?.topografia_cheratocono || '-'}</strong></div>
                      {selectedVisita.occhio_destro?.retinografia_referto && (
                        <div className="mt-2 p-2 bg-white rounded">
                          <span className="font-medium">Retinografia:</span>
                          <p className="text-xs mt-1">{selectedVisita.occhio_destro.retinografia_referto}</p>
                        </div>
                      )}
                      {selectedVisita.occhio_destro?.oct_referto && (
                        <div className="mt-2 p-2 bg-white rounded">
                          <span className="font-medium">OCT:</span>
                          <p className="text-xs mt-1">{selectedVisita.occhio_destro.oct_referto}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* OS */}
                  <div className="p-4 rounded-xl" style={{ background: '#FFF3E0' }}>
                    <h4 className="font-semibold mb-3 text-center">👁️ OCCHIO SINISTRO (OS)</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span>Visus:</span><strong>{selectedVisita.occhio_sinistro?.visus || '-'}</strong></div>
                      <div className="flex justify-between"><span>Visus c/c:</span><strong>{selectedVisita.occhio_sinistro?.visus_con_correzione || '-'}</strong></div>
                      <div className="flex justify-between"><span>Autoref:</span><strong>{selectedVisita.occhio_sinistro?.autoref_sfera || '-'} {selectedVisita.occhio_sinistro?.autoref_cilindro || ''} {selectedVisita.occhio_sinistro?.autoref_asse || ''}</strong></div>
                      <div className="flex justify-between"><span>Prescrizione:</span><strong>{selectedVisita.occhio_sinistro?.prescrizione_sfera || '-'} {selectedVisita.occhio_sinistro?.prescrizione_cilindro || ''} {selectedVisita.occhio_sinistro?.prescrizione_asse || ''}</strong></div>
                      <div className="flex justify-between"><span>Tono s/g:</span><strong>{selectedVisita.occhio_sinistro?.tonometria_senza_gocce || '-'} mmHg</strong></div>
                      <div className="flex justify-between"><span>Tono c/g:</span><strong>{selectedVisita.occhio_sinistro?.tonometria_con_gocce || '-'} mmHg</strong></div>
                      <div className="flex justify-between"><span>Mezzi:</span><strong>{selectedVisita.occhio_sinistro?.mezzi_diottrici || '-'}</strong></div>
                      <div className="flex justify-between"><span>Conta End.:</span><strong>{selectedVisita.occhio_sinistro?.conta_endoteliale || '-'} cell/mm²</strong></div>
                      <div className="flex justify-between"><span>Topografia:</span><strong>{selectedVisita.occhio_sinistro?.topografia_cheratocono || '-'}</strong></div>
                      {selectedVisita.occhio_sinistro?.retinografia_referto && (
                        <div className="mt-2 p-2 bg-white rounded">
                          <span className="font-medium">Retinografia:</span>
                          <p className="text-xs mt-1">{selectedVisita.occhio_sinistro.retinografia_referto}</p>
                        </div>
                      )}
                      {selectedVisita.occhio_sinistro?.oct_referto && (
                        <div className="mt-2 p-2 bg-white rounded">
                          <span className="font-medium">OCT:</span>
                          <p className="text-xs mt-1">{selectedVisita.occhio_sinistro.oct_referto}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Consigli e Prescrizioni */}
                {(selectedVisita.consigli || selectedVisita.prescrizioni_colliri?.length > 0) && (
                  <div className="mt-6 grid md:grid-cols-2 gap-4">
                    {selectedVisita.consigli && (
                      <div className="p-4 bg-blue-50 rounded-xl">
                        <h4 className="font-semibold mb-2">Consigli</h4>
                        <p className="text-sm">{selectedVisita.consigli}</p>
                      </div>
                    )}
                    {selectedVisita.prescrizioni_colliri?.length > 0 && (
                      <div className="p-4 bg-purple-50 rounded-xl">
                        <h4 className="font-semibold mb-2">Prescrizioni</h4>
                        <ul className="text-sm space-y-1">
                          {selectedVisita.prescrizioni_colliri.map((p, i) => (
                            <li key={i}>• {p.nome_commerciale} - {p.posologia} - {p.occhio}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Confronto Visite */}
            {compareMode && selectedVisita && compareVisita && (
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-semibold mb-4" style={{ color: '#2A9FCB' }}>
                  Confronto Visite
                </h3>
                
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="py-2 px-3 text-left">Parametro</th>
                        <th className="py-2 px-3 text-center bg-emerald-50">
                          {new Date(selectedVisita.created_at).toLocaleDateString('it-IT')}
                        </th>
                        <th className="py-2 px-3 text-center bg-amber-50">
                          {new Date(compareVisita.created_at).toLocaleDateString('it-IT')}
                        </th>
                        <th className="py-2 px-3 text-center">Variazione</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="py-2 px-3 font-medium">Visus OD</td>
                        <td className="py-2 px-3 text-center bg-emerald-50">{selectedVisita.occhio_destro?.visus || '-'}</td>
                        <td className="py-2 px-3 text-center bg-amber-50">{compareVisita.occhio_destro?.visus || '-'}</td>
                        <td className="py-2 px-3 text-center">
                          {selectedVisita.occhio_destro?.visus === compareVisita.occhio_destro?.visus ? '=' : '≠'}
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 px-3 font-medium">Visus OS</td>
                        <td className="py-2 px-3 text-center bg-emerald-50">{selectedVisita.occhio_sinistro?.visus || '-'}</td>
                        <td className="py-2 px-3 text-center bg-amber-50">{compareVisita.occhio_sinistro?.visus || '-'}</td>
                        <td className="py-2 px-3 text-center">
                          {selectedVisita.occhio_sinistro?.visus === compareVisita.occhio_sinistro?.visus ? '=' : '≠'}
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 px-3 font-medium">Tono OD (mmHg)</td>
                        <td className="py-2 px-3 text-center bg-emerald-50">{selectedVisita.occhio_destro?.tonometria_senza_gocce || '-'}</td>
                        <td className="py-2 px-3 text-center bg-amber-50">{compareVisita.occhio_destro?.tonometria_senza_gocce || '-'}</td>
                        <td className="py-2 px-3 text-center">
                          {(() => {
                            const v1 = parseFloat(selectedVisita.occhio_destro?.tonometria_senza_gocce);
                            const v2 = parseFloat(compareVisita.occhio_destro?.tonometria_senza_gocce);
                            if (isNaN(v1) || isNaN(v2)) return '-';
                            const diff = v1 - v2;
                            return diff > 0 ? `+${diff}` : diff < 0 ? diff : '=';
                          })()}
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 px-3 font-medium">Tono OS (mmHg)</td>
                        <td className="py-2 px-3 text-center bg-emerald-50">{selectedVisita.occhio_sinistro?.tonometria_senza_gocce || '-'}</td>
                        <td className="py-2 px-3 text-center bg-amber-50">{compareVisita.occhio_sinistro?.tonometria_senza_gocce || '-'}</td>
                        <td className="py-2 px-3 text-center">
                          {(() => {
                            const v1 = parseFloat(selectedVisita.occhio_sinistro?.tonometria_senza_gocce);
                            const v2 = parseFloat(compareVisita.occhio_sinistro?.tonometria_senza_gocce);
                            if (isNaN(v1) || isNaN(v2)) return '-';
                            const diff = v1 - v2;
                            return diff > 0 ? `+${diff}` : diff < 0 ? diff : '=';
                          })()}
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 px-3 font-medium">Conta End. OD</td>
                        <td className="py-2 px-3 text-center bg-emerald-50">{selectedVisita.occhio_destro?.conta_endoteliale || '-'}</td>
                        <td className="py-2 px-3 text-center bg-amber-50">{compareVisita.occhio_destro?.conta_endoteliale || '-'}</td>
                        <td className="py-2 px-3 text-center">
                          {(() => {
                            const v1 = parseFloat(selectedVisita.occhio_destro?.conta_endoteliale);
                            const v2 = parseFloat(compareVisita.occhio_destro?.conta_endoteliale);
                            if (isNaN(v1) || isNaN(v2)) return '-';
                            const diff = v1 - v2;
                            return diff > 0 ? `+${diff}` : diff < 0 ? diff : '=';
                          })()}
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 px-3 font-medium">Conta End. OS</td>
                        <td className="py-2 px-3 text-center bg-emerald-50">{selectedVisita.occhio_sinistro?.conta_endoteliale || '-'}</td>
                        <td className="py-2 px-3 text-center bg-amber-50">{compareVisita.occhio_sinistro?.conta_endoteliale || '-'}</td>
                        <td className="py-2 px-3 text-center">
                          {(() => {
                            const v1 = parseFloat(selectedVisita.occhio_sinistro?.conta_endoteliale);
                            const v2 = parseFloat(compareVisita.occhio_sinistro?.conta_endoteliale);
                            if (isNaN(v1) || isNaN(v2)) return '-';
                            const diff = v1 - v2;
                            return diff > 0 ? `+${diff}` : diff < 0 ? diff : '=';
                          })()}
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 px-3 font-medium">Topografia OD</td>
                        <td className="py-2 px-3 text-center bg-emerald-50">{selectedVisita.occhio_destro?.topografia_cheratocono || '-'}</td>
                        <td className="py-2 px-3 text-center bg-amber-50">{compareVisita.occhio_destro?.topografia_cheratocono || '-'}</td>
                        <td className="py-2 px-3 text-center">
                          {selectedVisita.occhio_destro?.topografia_cheratocono === compareVisita.occhio_destro?.topografia_cheratocono ? '=' : '≠'}
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 px-3 font-medium">Topografia OS</td>
                        <td className="py-2 px-3 text-center bg-emerald-50">{selectedVisita.occhio_sinistro?.topografia_cheratocono || '-'}</td>
                        <td className="py-2 px-3 text-center bg-amber-50">{compareVisita.occhio_sinistro?.topografia_cheratocono || '-'}</td>
                        <td className="py-2 px-3 text-center">
                          {selectedVisita.occhio_sinistro?.topografia_cheratocono === compareVisita.occhio_sinistro?.topografia_cheratocono ? '=' : '≠'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Drug Info Modal */}
      {selectedDrugInfo && (
        <DrugInfoModal 
          farmaco={selectedDrugInfo} 
          onClose={() => setSelectedDrugInfo(null)} 
        />
      )}

      {/* AI Terapia Modal */}
      {showAiTerapiaModal && aiTerapia && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="p-6 border-b sticky top-0 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Suggerimento AI Terapia</h2>
                    <p className="text-sm text-white/80">Basato sull'esame clinico e linee guida</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowAiTerapiaModal(false)}
                  className="p-2 hover:bg-white/20 rounded-lg text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Terapia Overview */}
              <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                <h3 className="font-semibold text-purple-800 mb-2">📋 Terapia Suggerita</h3>
                <p className="text-purple-700 whitespace-pre-wrap">{aiTerapia.terapia_suggerita}</p>
              </div>

              {/* Farmaci Consigliati */}
              {aiTerapia.farmaci_consigliati && aiTerapia.farmaci_consigliati.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-800 mb-3">💊 Farmaci Consigliati</h3>
                  <div className="space-y-3">
                    {aiTerapia.farmaci_consigliati.map((farmaco, idx) => (
                      <div key={idx} className="border rounded-lg p-4 bg-gray-50 hover:bg-green-50 transition-colors">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-semibold text-green-700">{farmaco.nome}</h4>
                            <p className="text-sm text-gray-600">{farmaco.concentrazione}</p>
                          </div>
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                            {farmaco.occhio || 'OU'}
                          </span>
                        </div>
                        <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="text-gray-500">Posologia:</span>
                            <span className="ml-1 text-gray-800">{farmaco.posologia}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Durata:</span>
                            <span className="ml-1 text-gray-800">{farmaco.durata}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Note Cliniche */}
              {aiTerapia.note_cliniche && (
                <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
                  <h3 className="font-semibold text-yellow-800 mb-2">📝 Note Cliniche</h3>
                  <p className="text-yellow-700">{aiTerapia.note_cliniche}</p>
                </div>
              )}

              {/* Controindicazioni */}
              {aiTerapia.controindicazioni && aiTerapia.controindicazioni.length > 0 && (
                <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                  <h3 className="font-semibold text-red-800 mb-2">⚠️ Controindicazioni</h3>
                  <ul className="list-disc list-inside text-red-700 text-sm">
                    {aiTerapia.controindicazioni.map((c, i) => (
                      <li key={i}>{c}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Follow-up */}
              {aiTerapia.follow_up_consigliato && (
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                  <h3 className="font-semibold text-blue-800 mb-2">📅 Follow-up Consigliato</h3>
                  <p className="text-blue-700">{aiTerapia.follow_up_consigliato}</p>
                </div>
              )}

              {/* Fonti */}
              <div className="text-xs text-gray-500">
                📚 Fonti: {aiTerapia.fonti?.join(', ') || 'SOI, AIMO, linee guida nazionali'}
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t sticky bottom-0 bg-white rounded-b-2xl">
              <div className="flex gap-3">
                <button
                  onClick={() => setShowAiTerapiaModal(false)}
                  className="flex-1 px-4 py-3 rounded-lg border border-gray-200 text-gray-700 font-medium hover:bg-gray-50"
                >
                  Chiudi
                </button>
                <button
                  onClick={applyAiTerapia}
                  className="flex-1 px-4 py-3 rounded-lg text-white font-medium flex items-center justify-center gap-2"
                  style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)' }}
                >
                  <Check className="w-5 h-5" />
                  Applica alla Prescrizione
                </button>
              </div>
              <p className="text-xs text-gray-500 text-center mt-3">
                ⚠️ Verifica sempre i suggerimenti prima di applicarli. La responsabilità è del medico.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* PDF Viewer Modal */}
      {showPDFViewer && selectedVisitForPDF && (
        <VisitaPDFViewer
          visitaId={selectedVisitForPDF.concluded_visit_id || selectedVisitForPDF.id}
          patientName={`${patient?.first_name || ''} ${patient?.last_name || ''}`}
          visitaDate={new Date(selectedVisitForPDF.created_at).toLocaleDateString('it-IT')}
          onClose={() => {
            setShowPDFViewer(false);
            setSelectedVisitForPDF(null);
          }}
          canDownload={true}
        />
      )}

      {/* Import Template Modal */}
      <ImportTemplateModal
        isOpen={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onImport={handleTemplateImport}
        targetField={importTargetField}
        initialSearch={importInitialSearch}
      />
    </div>
  );
};

export default CartellaClinicaNew;
